import { z } from "zod";
//#region src/primitives.ts
/**
* Wire-format version for every top-level Displaro contract.
* Bump only on a breaking change; additive fields do NOT bump it.
* Players reject documents whose `schemaVersion` they don't understand
* and keep playing their last verified manifest (or the embedded fallback reel).
*/
const CONTRACT_SCHEMA_VERSION = 1;
const SchemaVersionSchema = z.literal(1);
/** Server- or device-generated identifier (RFC 9562 UUID). */
const IdSchema = z.uuid();
/** Lowercase hex SHA-256 digest. The player's sole source of truth for "this file is intact". */
const Sha256HexSchema = z.string().regex(/^[a-f0-9]{64}$/, "Expected a 64-char lowercase hex SHA-256 digest");
/** Absolute instant, ISO-8601 with `Z` or an explicit offset. Never a naive local time. */
const IsoDateTimeSchema = z.iso.datetime({ offset: true });
/** Wall-clock time of day `HH:mm` (24h), interpreted in the manifest's `timezone`. */
const LocalTimeSchema = z.iso.time({ precision: -1 });
/** Absolute http(s) URL. */
const HttpUrlSchema = z.url({ protocol: /^https?$/ });
const NonNegativeIntSchema = z.int().nonnegative();
const PositiveIntSchema = z.int().positive();
function isValidIanaTimeZone(tz) {
	try {
		new Intl.DateTimeFormat("en-US", { timeZone: tz });
		return true;
	} catch {
		return false;
	}
}
/** IANA zone name, e.g. `Asia/Karachi`. Schedules are evaluated on-device in this zone. */
const IanaTimeZoneSchema = z.string().min(1).refine(isValidIanaTimeZone, { message: "Expected a valid IANA time zone (e.g. \"Asia/Karachi\")" });
/** Video codecs the manifest compiler may target and the player may report as hardware-decodable. */
const VideoCodecSchema = z.enum([
	"h264",
	"hevc",
	"vp9",
	"av1"
]);
//#endregion
//#region src/decoder-budget.ts
const MAX_CONCURRENT_VIDEOS = 2;
/** Phase 16 Step 15d: sections of one layout that may show custom apps (each is a WebView). */
const MAX_APP_ZONES_PER_LAYOUT = 2;
const label$1 = (z) => z.name ? `“${z.name}”` : z.zoneId;
/** Primary zone = the video zone with the largest area; if no zone has video, the largest zone. */
function choosePrimaryZone(zones) {
	const byArea = (a, b) => b.width * b.height - a.width * a.height || a.zoneId.localeCompare(b.zoneId);
	return (zones.filter((z) => z.items.some((i) => i.kind === "video")).sort(byArea)[0] ?? [...zones].sort(byArea)[0])?.zoneId ?? null;
}
function analyzeDecoderBudget(zones) {
	const primaryZoneId = choosePrimaryZone(zones);
	const peakByZone = {};
	const violations = [];
	for (const zone of zones) peakByZone[zone.zoneId] = zone.items.some((i) => i.kind === "video") ? 1 : 0;
	const peakConcurrentVideos = Object.values(peakByZone).reduce((a, b) => a + b, 0);
	if (peakConcurrentVideos > 2) {
		const contributors = zones.filter((z) => (peakByZone[z.zoneId] ?? 0) > 0);
		violations.push({
			zoneId: contributors[contributors.length - 1].zoneId,
			code: "MAX_CONCURRENT_VIDEOS",
			message: `${peakConcurrentVideos} zones play video (${contributors.map(label$1).join(", ")}); the limit is 2. Use video in at most two zones of a layout.`
		});
	}
	const appZones = zones.filter((z) => z.items.some((i) => i.kind === "app" || i.kind === "composition"));
	if (appZones.length > 2) violations.push({
		zoneId: appZones[appZones.length - 1].zoneId,
		code: "MAX_APP_ZONES",
		message: `${appZones.length} sections show custom apps or compositions (${appZones.map(label$1).join(", ")}); the limit is 2.`
	});
	return {
		primaryZoneId,
		peakByZone,
		peakConcurrentVideos,
		violations
	};
}
//#endregion
//#region src/player-settings.ts
const WeekdaySchema = z.int().min(1).max(7);
/** "08:00–22:00"; `to` ≤ `from` runs past midnight (22:00–06:00). Equal times are refused. */
const TimeWindowSchema = z.object({
	from: LocalTimeSchema,
	to: LocalTimeSchema
}).refine((w) => w.from !== w.to, {
	message: "Start and end must differ",
	path: ["to"]
});
/** When the player is "open". No days = every day; no windows = all day; both empty = always on. */
const WorkingScheduleSchema = z.object({
	days: z.array(WeekdaySchema).max(7).refine((d) => new Set(d).size === d.length, "Duplicate day"),
	windows: z.array(TimeWindowSchema).max(3)
});
/** Outside working hours: a black screen (audio muted) or the Displaro standby screen. */
const OutsideHoursSchema = z.enum(["black", "standby"]);
const VolumeSchema = z.discriminatedUnion("mode", [
	z.object({ mode: z.literal("default") }),
	z.object({
		mode: z.literal("custom"),
		level: z.int().min(0).max(100)
	}),
	z.object({ mode: z.literal("mute") })
]);
/** null = leave the device's own brightness alone. */
const BrightnessSchema = z.int().min(0).max(100).nullable();
const OrientationSchema = z.enum([
	"landscape",
	"portrait",
	"landscape_flipped",
	"portrait_flipped"
]);
/**
* Phase 16 Step 15e: monitoring screenshots. `intervalMinutes` null = off. `wifiOnly`: skip uploads on metered
* networks (they wait in the player's queue).
*/
const ScreenshotScheduleSchema = z.object({
	intervalMinutes: z.union([
		z.literal(5),
		z.literal(10),
		z.literal(15),
		z.literal(30),
		z.literal(60),
		z.literal(120),
		z.literal(360)
	]).nullable(),
	wifiOnly: z.boolean()
});
const DEFAULT_SCREENSHOT_SCHEDULE = {
	intervalMinutes: null,
	wifiOnly: false
};
/**
* Device logs: how often the player sends its log to the server, and what it keeps (it only ever records warnings and
* errors). Fewer, less frequent uploads for large fleets on metered links; errors only for quiet players.
*/
const DEVICE_LOG_UPLOAD_MINUTES = [
	1,
	5,
	15,
	30,
	60
];
const DeviceLogSettingsSchema = z.object({
	uploadMinutes: z.union([
		z.literal(1),
		z.literal(5),
		z.literal(15),
		z.literal(30),
		z.literal(60)
	]),
	level: z.enum([
		"warn",
		"error",
		"off"
	])
});
const DEFAULT_DEVICE_LOGS = {
	uploadMinutes: 5,
	level: "warn"
};
/**
* What the screen shows when nothing is scheduled: the standby mark, black, your logo (an image from the library,
* downloaded with the content so it shows offline) or a message on a colour.
*/
const HexColorSchema$1 = z.string().regex(/^#[0-9a-fA-F]{6}$/, "A colour like #112233");
const IdleScreenSchema = z.object({
	kind: z.enum([
		"standby",
		"black",
		"logo",
		"message"
	]),
	/** `logo`: an image from the library. */
	assetId: IdSchema.nullable(),
	/** `message`: up to 200 characters. */
	text: z.string().trim().max(200),
	background: HexColorSchema$1,
	textColor: HexColorSchema$1
});
const DEFAULT_IDLE_SCREEN = {
	kind: "standby",
	assetId: null,
	text: "",
	background: "#000000",
	textColor: "#ffffff"
};
/** Restart the player app every day at a time on the screen's clock (no days = every day). */
const RestartScheduleSchema = z.object({
	enabled: z.boolean(),
	time: LocalTimeSchema,
	days: z.array(WeekdaySchema).max(7)
});
const DEFAULT_RESTART_SCHEDULE = {
	enabled: false,
	time: "04:00",
	days: []
};
/**
* Data saving: download new content only inside a time window on the screen's clock (e.g. at night), and on
* Android optionally only on Wi-Fi / unmetered networks. What is already on the player keeps playing meanwhile.
*/
const DataSavingSchema = z.object({
	enabled: z.boolean(),
	window: TimeWindowSchema,
	wifiOnly: z.boolean()
});
const DEFAULT_DATA_SAVING = {
	enabled: false,
	window: {
		from: "01:00",
		to: "05:00"
	},
	wifiOnly: false
};
const LocationMinutesSchema = z.int().min(1, "At least every minute").max(1440, "At most once a day");
const DEFAULT_LOCATION_MINUTES = 30;
/** Effective settings (manifest) and organization defaults: every field present. */
const PlayerSettingsSchema = z.object({
	workingSchedule: WorkingScheduleSchema,
	outsideHours: OutsideHoursSchema,
	volume: VolumeSchema,
	/**
	* The mute switch (organization, group or player; the most specific one set wins). On = no sound whatever
	* the volume says; the volume is kept for when it's switched off. Stored settings from before it parse to off.
	*/
	muted: z.boolean().default(false),
	brightness: BrightnessSchema,
	orientation: OrientationSchema,
	/** Phase 16 Step 15e (stored defaults from before it parse to "off"). */
	screenshots: ScreenshotScheduleSchema.default(DEFAULT_SCREENSHOT_SCHEDULE),
	/** Stored settings from before it parse to the default. */
	deviceLogs: DeviceLogSettingsSchema.default(DEFAULT_DEVICE_LOGS),
	/** Stored settings from before these parse to the defaults. */
	idleScreen: IdleScreenSchema.default(DEFAULT_IDLE_SCREEN),
	restart: RestartScheduleSchema.default(DEFAULT_RESTART_SCHEDULE),
	dataSaving: DataSavingSchema.default(DEFAULT_DATA_SAVING),
	/**
	* How often the player looks for a fresh position (Android: Wi-Fi/GPS), in minutes. Regions and location-aware
	* apps follow it: 30 suits fixed screens; a few minutes suits screens in vehicles.
	*/
	locationMinutes: LocationMinutesSchema.default(30)
});
/** Built-in defaults (a new organization starts here): always on, standby, full volume, system brightness, landscape. */
const DEFAULT_PLAYER_SETTINGS = {
	workingSchedule: {
		days: [],
		windows: []
	},
	outsideHours: "standby",
	volume: { mode: "default" },
	muted: false,
	brightness: null,
	orientation: "landscape",
	screenshots: DEFAULT_SCREENSHOT_SCHEDULE,
	deviceLogs: DEFAULT_DEVICE_LOGS,
	idleScreen: DEFAULT_IDLE_SCREEN,
	restart: DEFAULT_RESTART_SCHEDULE,
	dataSaving: DEFAULT_DATA_SAVING,
	locationMinutes: 30
};
/**
* A player's own overrides: a missing key inherits the organization default. (Brightness `null` is a real
* value — "system" — so inheriting is expressed by leaving the key out, never by null.)
*/
const PlayerSettingsOverridesSchema = PlayerSettingsSchema.extend({
	muted: z.boolean(),
	screenshots: ScreenshotScheduleSchema,
	deviceLogs: DeviceLogSettingsSchema,
	idleScreen: IdleScreenSchema,
	restart: RestartScheduleSchema,
	dataSaving: DataSavingSchema,
	locationMinutes: LocationMinutesSchema
}).partial();
/** Effective = defaults ⊕ overrides (the compiler and the CMS use the same rule). */
const effectiveSettings = (defaults, overrides) => ({
	...defaults,
	...Object.fromEntries(Object.entries(overrides).filter(([, v]) => v !== void 0))
});
/**
* What a player inherits before its own overrides: the organization defaults, then its player groups' settings.
* Groups are applied in name order and the first group that sets a key wins (the same rule as buildings), so one
* group never silently overrides another the player is also in.
*/
const inheritedSettings = (defaults, groups) => {
	return [...groups].sort((a, b) => a.name.localeCompare(b.name)).reverse().reduce((acc, g) => effectiveSettings(acc, g.settings), defaults);
};
/** The volume a player actually uses: the mute switch wins over the volume setting. */
const soundOf = (s) => s.muted ? { mode: "mute" } : s.volume;
//#endregion
//#region src/interactions.ts
/** A hotspot as fractions of the canvas (0–1), so it survives resolution changes. */
const HotspotSchema = z.object({
	x: z.number().min(0).max(1),
	y: z.number().min(0).max(1),
	w: z.number().gt(0).max(1),
	h: z.number().gt(0).max(1)
}).refine((r) => r.x + r.w <= 1.0001 && r.y + r.h <= 1.0001, "The hotspot must stay inside the screen");
/** Remote-control keys a TV box sends (Android key codes are mapped on the player). */
const REMOTE_KEYS = [
	"OK",
	"0",
	"1",
	"2",
	"3",
	"4",
	"5",
	"6",
	"7",
	"8",
	"9",
	"UP",
	"DOWN",
	"LEFT",
	"RIGHT",
	"RED",
	"GREEN",
	"YELLOW",
	"BLUE"
];
const RemoteKeySchema = z.enum(REMOTE_KEYS);
const InteractionTriggerSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("touch"),
	area: z.discriminatedUnion("kind", [
		z.object({ kind: z.literal("full") }),
		z.object({
			kind: z.literal("zone"),
			zoneId: IdSchema
		}),
		z.object({
			kind: z.literal("rects"),
			rects: z.array(HotspotSchema).min(1, "Draw at least one hotspot").max(10)
		})
	])
}), z.object({
	type: z.literal("key"),
	key: RemoteKeySchema
})]);
/** Media (image, video, app) or a campaign. */
const InteractionTargetSchema = z.object({
	kind: z.enum(["media", "campaign"]),
	id: IdSchema
});
const InteractionActionSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("play"),
	target: InteractionTargetSchema,
	/** Back to the normal list after N seconds, or when the content ends. */
	returnAfter: z.discriminatedUnion("mode", [z.object({
		mode: z.literal("seconds"),
		seconds: z.int().min(3).max(3600)
	}), z.object({ mode: z.literal("end") })]),
	closeButton: z.boolean()
}), z.object({
	type: z.literal("web"),
	url: HttpUrlSchema.refine((u) => u.startsWith("https://"), "Web pages must use https"),
	/** null = stays until closed (then a close button is required). */
	returnAfterSeconds: z.int().min(5).max(3600).nullable(),
	closeButton: z.boolean()
})]);
/** Player level: any content, or only while a given item plays. Campaign level: implicitly that campaign. */
const InteractionAppliesToSchema = z.discriminatedUnion("kind", [z.object({ kind: z.literal("any") }), z.object({
	kind: z.literal("content"),
	target: InteractionTargetSchema
})]);
const UpsertInteractionRequestSchema = z.object({
	label: z.string().trim().min(1).max(120),
	enabled: z.boolean().default(true),
	trigger: InteractionTriggerSchema,
	appliesTo: InteractionAppliesToSchema.default({ kind: "any" }),
	action: InteractionActionSchema
}).refine((b) => b.action.type !== "web" || b.action.returnAfterSeconds !== null || b.action.closeButton, {
	message: "A web page that never times out needs a close button",
	path: ["action"]
});
const InteractionSchema = z.object({
	id: IdSchema,
	owner: z.discriminatedUnion("kind", [z.object({
		kind: z.literal("player"),
		screenId: IdSchema
	}), z.object({
		kind: z.literal("campaign"),
		layoutId: IdSchema,
		campaignName: z.string()
	})]),
	label: z.string(),
	enabled: z.boolean(),
	position: z.int().nonnegative(),
	trigger: InteractionTriggerSchema,
	appliesTo: InteractionAppliesToSchema,
	action: InteractionActionSchema,
	/** Display names resolved by the API (target content, "applies to" content). */
	targetName: z.string().nullable(),
	appliesToName: z.string().nullable(),
	updatedAt: IsoDateTimeSchema
});
/** A player's effective list (I10): its own, and those of the campaigns it plays. */
const PlayerInteractionsSchema = z.object({
	own: z.array(InteractionSchema),
	inherited: z.array(InteractionSchema)
});
const ManifestTriggerSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("touch"),
	rects: z.array(HotspotSchema).min(1).max(10)
}), z.object({
	type: z.literal("key"),
	key: RemoteKeySchema
})]);
const ManifestActionSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("play"),
	layoutId: IdSchema,
	returnAfterMs: z.int().min(1e3),
	closeButton: z.boolean()
}), z.object({
	type: z.literal("web"),
	url: HttpUrlSchema,
	returnAfterMs: z.int().min(1e3).nullable(),
	closeButton: z.boolean()
})]);
const ManifestInteractionSchema = z.object({
	interactionId: IdSchema,
	/** Scene layoutIds during which it is active; null = any scene. Campaign-level ones are checked first. */
	appliesTo: z.array(IdSchema).nullable(),
	/** 0 = campaign level, 1 = player level (campaign ones win when both match). */
	priority: z.int().min(0).max(1),
	trigger: ManifestTriggerSchema,
	action: ManifestActionSchema
});
//#endregion
//#region src/widgets.ts
const WIDGET_TYPES = [
	"prayer_time",
	"rss",
	"weather",
	"time",
	"countdown",
	"qr_code",
	"text",
	"ticker",
	"menu_board",
	"world_clock",
	"web_page",
	"youtube",
	"google_slides",
	"calendar",
	"chart",
	"dashboard",
	"photos",
	"people",
	"meeting_room",
	"stream",
	"social_wall"
];
/** What players built before Phase 17.11 can show (they don't report `capabilities.widgets`). */
const LEGACY_WIDGET_TYPES = [
	"prayer_time",
	"rss",
	"weather",
	"time"
];
const WidgetTypeSchema = z.enum(WIDGET_TYPES);
const LatitudeSchema = z.number().min(-90).max(90);
const LongitudeSchema = z.number().min(-180).max(180);
/**
* Authoring: `screen` = use the playing screen's latitude/longitude (resolved by the compiler, so a
* group of screens in different cities each get their own coordinates). Compiled manifests only ever
* contain `fixed` locations.
*/
const WidgetLocationSchema = z.discriminatedUnion("source", [z.object({ source: z.literal("screen") }), z.object({
	source: z.literal("fixed"),
	latitude: LatitudeSchema,
	longitude: LongitudeSchema
})]);
/** `#RRGGBB`. */
const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Expected a #RRGGBB colour");
/**
* Look shared by every app (legacy "Customize App": Background / Font colour, brand navy + white).
* Defaults keep configs saved before Phase 16 Step 5 valid; the player falls back to the same values.
*/
const appearance = {
	background: HexColorSchema.default("#0F387A"),
	foreground: HexColorSchema.default("#FFFFFF"),
	/** A custom font of the organization (Fonts); absent/null = the player's own font. */
	fontId: IdSchema.nullable().optional()
};
/** Legacy apps default to Arabic (Gulf/MENA market). */
const AppLanguageSchema = z.enum(["ar", "en"]);
const TimeWidgetSchema = z.object({
	type: z.literal("time"),
	format: z.enum(["12h", "24h"]),
	showSeconds: z.boolean(),
	showDate: z.boolean(),
	/** null = the manifest's (screen's) time zone. */
	timezone: IanaTimeZoneSchema.nullable(),
	/** Legacy "Clock Round / Clock Digital". */
	style: z.enum(["round", "digital"]).default("round"),
	/** Legacy "Hide city/country" (inverted). */
	showLocation: z.boolean().default(true),
	/** Round clock: the time written under the face ("Hide time" turns it off; the digital clock always shows it). */
	showTime: z.boolean().default(true),
	language: AppLanguageSchema.default("ar"),
	...appearance
});
/** Computed fully OFFLINE on the device from coordinates + method (no network needed). */
const PrayerTimeWidgetSchema = z.object({
	type: z.literal("prayer_time"),
	location: WidgetLocationSchema,
	calculationMethod: z.enum([
		"MWL",
		"ISNA",
		"EGYPT",
		"MAKKAH",
		"KARACHI",
		"TEHRAN",
		"JAFARI",
		"GULF",
		"SINGAPORE"
	]),
	/** `hanafi` = shadow factor 2 for Asr. */
	asrMethod: z.enum(["standard", "hanafi"]),
	highlightNext: z.boolean(),
	language: z.enum([
		"en",
		"ar",
		"ur"
	]),
	...appearance
});
const RssWidgetSchema = z.object({
	type: z.literal("rss"),
	feedUrl: HttpUrlSchema.refine((u) => u.startsWith("https://"), "Feeds must use https"),
	maxItems: z.int().min(1).max(20),
	/** How long each headline stays on screen. */
	itemDurationMs: z.int().min(3e3).max(6e4),
	showImages: z.boolean(),
	/** ≥ 5 min: protects feeds and cellular data plans. */
	refreshIntervalSec: z.int().min(300).max(86400),
	/** Legacy "App Font Size". */
	fontSize: z.enum([
		"small",
		"medium",
		"large"
	]).default("large"),
	/** Legacy "Speed": how fast the ticker scrolls. */
	speed: z.enum([
		"slow",
		"medium",
		"fast"
	]).default("slow"),
	/** Legacy "Text Direction"; `rtl` for Arabic/Urdu feeds. */
	direction: z.enum(["ltr", "rtl"]).default("ltr"),
	...appearance
});
const WeatherWidgetSchema = z.object({
	type: z.literal("weather"),
	location: WidgetLocationSchema,
	units: z.enum(["metric", "imperial"]),
	forecastDays: z.int().min(0).max(5),
	refreshIntervalSec: z.int().min(600).max(86400),
	language: AppLanguageSchema.default("ar"),
	...appearance
});
const TextSizeSchema = z.enum([
	"small",
	"medium",
	"large",
	"xlarge"
]);
const AlignSchema = z.enum([
	"start",
	"center",
	"end"
]);
const DirectionSchema = z.enum(["ltr", "rtl"]);
const HttpsUrlSchema = HttpUrlSchema.refine((u) => u.startsWith("https://"), "Use an https:// address");
/** Counts down to a moment (or up from it). `target` is local wall-clock time in `timezone` (null = the screen's). */
const CountdownWidgetSchema = z.object({
	type: z.literal("countdown"),
	title: z.string().trim().max(120).default(""),
	/** "2026-12-31T23:59" (local, no zone). */
	target: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "Choose the date and time"),
	timezone: IanaTimeZoneSchema.nullable().default(null),
	mode: z.enum(["down", "up"]).default("down"),
	showSeconds: z.boolean().default(true),
	/** Shown once the moment has passed (countdown only). */
	doneText: z.string().trim().max(120).default(""),
	language: AppLanguageSchema.default("en"),
	...appearance
});
/** A QR code (made on the device) with an optional caption. */
const QrCodeWidgetSchema = z.object({
	type: z.literal("qr_code"),
	data: z.string().trim().min(1, "Enter a link or text").max(1e3),
	caption: z.string().trim().max(120).default(""),
	/** Caption above or below the code. */
	captionPosition: z.enum(["above", "below"]).default("below"),
	...appearance
});
/** An announcement: a headline and text. */
const TextWidgetSchema = z.object({
	type: z.literal("text"),
	headline: z.string().trim().max(200).default(""),
	body: z.string().trim().max(2e3).default(""),
	size: TextSizeSchema.default("large"),
	align: AlignSchema.default("center"),
	direction: DirectionSchema.default("ltr"),
	...appearance
}).refine((w) => w.headline || w.body, {
	message: "Enter a headline or text",
	path: ["headline"]
});
/** Messages scrolling across the zone (no feed needed). */
const TickerWidgetSchema = z.object({
	type: z.literal("ticker"),
	messages: z.array(z.string().trim().min(1).max(300)).min(1, "Add at least one message").max(30),
	speed: z.enum([
		"slow",
		"medium",
		"fast"
	]).default("medium"),
	size: TextSizeSchema.default("large"),
	direction: DirectionSchema.default("ltr"),
	/** Shown between messages. */
	separator: z.string().max(5).default("•"),
	...appearance
});
const MenuItemSchema = z.object({
	name: z.string().trim().min(1, "Enter a name").max(80),
	description: z.string().trim().max(160).default(""),
	/** Free text so "3.500 BD", "from $5" or "S/M/L" all work. */
	price: z.string().trim().max(30).default("")
});
const MenuSectionSchema = z.object({
	name: z.string().trim().max(60).default(""),
	items: z.array(MenuItemSchema).min(1, "Add at least one item").max(30)
});
/** A price list / menu in sections and columns. */
const MenuBoardWidgetSchema = z.object({
	type: z.literal("menu_board"),
	title: z.string().trim().max(80).default(""),
	sections: z.array(MenuSectionSchema).min(1, "Add a section").max(12),
	columns: z.int().min(1).max(4).default(2),
	direction: DirectionSchema.default("ltr"),
	/** Price colour. */
	accent: HexColorSchema.default("#F5B301"),
	...appearance
});
/** Several cities' local times side by side. */
const WorldClockWidgetSchema = z.object({
	type: z.literal("world_clock"),
	cities: z.array(z.object({
		label: z.string().trim().min(1, "Enter a name").max(40),
		timezone: IanaTimeZoneSchema
	})).min(1, "Add a city").max(8),
	format: z.enum(["12h", "24h"]).default("24h"),
	showDate: z.boolean().default(false),
	language: AppLanguageSchema.default("en"),
	...appearance
});
/** A web page in a sandboxed browser, reloaded on a schedule. */
const WebPageWidgetSchema = z.object({
	type: z.literal("web_page"),
	url: HttpsUrlSchema,
	/** 0 = never reload. */
	refreshIntervalSec: z.int().min(0).max(86400).default(0),
	/** Page zoom in percent. */
	zoom: z.int().min(25).max(400).default(100),
	/** Allow taps / scrolling (interactive kiosks). */
	interactive: z.boolean().default(false)
});
/** A YouTube video or playlist. `videoId` / `playlistId` are taken from the pasted link by the CMS. */
const YouTubeWidgetSchema = z.object({
	type: z.literal("youtube"),
	videoId: z.string().regex(/^[A-Za-z0-9_-]{11}$/).nullable().default(null),
	playlistId: z.string().regex(/^[A-Za-z0-9_-]{10,64}$/).nullable().default(null),
	muted: z.boolean().default(true),
	loop: z.boolean().default(true),
	captions: z.boolean().default(false)
}).refine((w) => w.videoId || w.playlistId, {
	message: "Paste a YouTube video or playlist link",
	path: ["videoId"]
});
/** A published Google Slides deck, advancing by itself. `presentationId` comes from the pasted link. */
const GoogleSlidesWidgetSchema = z.object({
	type: z.literal("google_slides"),
	/** The id from the link; "Publish to web" links keep their "e/" prefix. */
	presentationId: z.string().regex(/^(e\/)?[A-Za-z0-9_-]{20,100}$/, "Paste the link of a Google Slides presentation"),
	/** Seconds per slide. */
	slideSeconds: z.int().min(2).max(300).default(8),
	loop: z.boolean().default(true)
});
/**
* A calendar's next events, from its iCal link (Google "Secret address in iCal format", Outlook "Publish calendar",
* Apple public calendar). The API reads and expands it (repeating events, time zones); players get plain events.
*/
const CalendarUrlSchema = z.string().trim().max(2048).regex(/^(https|webcal):\/\/[^\s]+$/i, "Paste the calendar’s iCal link (https:// or webcal://)");
/** webcal:// is https:// with a hint to open a calendar app. */
const calendarFetchUrl = (u) => u.trim().replace(/^webcal:\/\//i, "https://");
const CalendarWidgetSchema = z.object({
	type: z.literal("calendar"),
	icalUrl: CalendarUrlSchema,
	/** Heading above the list; blank = the calendar's own name. */
	title: z.string().trim().max(60).default(""),
	/** How far ahead to list. */
	daysAhead: z.int().min(1).max(31).default(7),
	maxEvents: z.int().min(1).max(20).default(8),
	format: z.enum(["12h", "24h"]).default("24h"),
	showLocation: z.boolean().default(true),
	/** ≥ 5 min: protects calendar servers. */
	refreshIntervalSec: z.int().min(300).max(86400).default(900),
	language: AppLanguageSchema.default("en"),
	/** Today's events and the date headings. */
	accent: HexColorSchema.default("#F5B301"),
	...appearance
});
/**
* A chart of a Data Feed's table: one column gives the labels, up to four give the values. The feed's approved table
* reaches players as a verified file with the app (`WidgetAsset.feed`), so charts work offline and update when the
* feed changes. `number` shows the first value large (a KPI tile).
*/
const CHART_KINDS = [
	"bar",
	"column",
	"line",
	"pie",
	"number"
];
const ChartWidgetSchema = z.object({
	type: z.literal("chart"),
	feedId: IdSchema,
	kind: z.enum(CHART_KINDS).default("column"),
	title: z.string().trim().max(80).default(""),
	/** Column key for the labels (x axis, slices, rows). */
	labelColumn: z.string().trim().min(1, "Choose the labels column").max(40),
	/** Column keys for the values (one series each; pie and number use the first). */
	valueColumns: z.array(z.string().trim().min(1).max(40)).min(1, "Choose a values column").max(4),
	/** First rows only (after sorting). */
	maxRows: z.int().min(1).max(50).default(12),
	sort: z.enum([
		"none",
		"desc",
		"asc"
	]).default("none"),
	showValues: z.boolean().default(true),
	/** Shown around every value ("AED ", " %"). */
	prefix: z.string().max(8).default(""),
	suffix: z.string().max(8).default(""),
	decimals: z.int().min(0).max(4).default(0),
	/** Series colours in order. */
	colors: z.array(HexColorSchema).min(1).max(4).default([
		"#F5B301",
		"#38BDF8",
		"#F472B6",
		"#34D399"
	]),
	direction: DirectionSchema.default("ltr"),
	...appearance
});
/**
* A BI dashboard shared by link (Power BI "Publish to web", Looker Studio, Tableau Public, Grafana, Metabase public
* links, or any page): shown full-screen without the tool's menus, refreshed on a schedule. `url` is what was pasted;
* `embedUrl` is the address players open (dashboardEmbedUrl, recomputed by the compiler on every publish).
*/
const DASHBOARD_PROVIDERS = [
	"powerbi",
	"looker",
	"tableau",
	"grafana",
	"metabase",
	"other"
];
const DashboardWidgetSchema = z.object({
	type: z.literal("dashboard"),
	url: HttpsUrlSchema,
	embedUrl: HttpsUrlSchema,
	provider: z.enum(DASHBOARD_PROVIDERS).default("other"),
	/** Reload this often (seconds; 0 = never: the dashboard refreshes itself). */
	refreshIntervalSec: z.int().min(0).max(86400).default(900),
	/** Page zoom in percent (fit a dense dashboard on a small screen). */
	zoom: z.int().min(25).max(400).default(100),
	/** Grafana: dark or light theme. */
	theme: z.enum(["dark", "light"]).default("dark")
});
/**
* Photos from a Data Feed's image column (a Google Sheet of links, an RSS feed's pictures, a typed table): a slideshow,
* a wall of tiles that change one at a time, or instant-photo frames with a caption. The feed's approved table reaches
* players as a verified file with the app (`WidgetAsset.feed`); the photos load from their links and players keep them.
*/
const PHOTO_STYLES = [
	"slideshow",
	"wall",
	"polaroid"
];
const PhotosWidgetSchema = z.object({
	type: z.literal("photos"),
	feedId: IdSchema,
	style: z.enum(PHOTO_STYLES).default("slideshow"),
	title: z.string().trim().max(80).default(""),
	/** Column key with the picture links (https). */
	imageColumn: z.string().trim().min(1, "Choose the pictures column").max(40),
	/** Column key shown under each photo; '' = none. */
	captionColumn: z.string().trim().max(40).default(""),
	/** Seconds each photo stays (the wall changes one tile this often). */
	slideSeconds: z.int().min(2).max(120).default(6),
	fit: z.enum(["cover", "contain"]).default("cover"),
	wallColumns: z.int().min(2).max(6).default(3),
	wallRows: z.int().min(1).max(4).default(2),
	shuffle: z.boolean().default(false),
	/** First photos only (after shuffling). */
	maxPhotos: z.int().min(1).max(100).default(30),
	direction: DirectionSchema.default("ltr"),
	...appearance
});
/**
* People from a Data Feed (a Google Sheet of staff): an employee-of-the-month spotlight, a team grid, today's / this
* week's / this month's birthdays, or an alphabetical directory. The feed's table reaches players as a verified file
* (`WidgetAsset.feed`); photos load from their links and players keep them.
*/
const PEOPLE_STYLES = [
	"spotlight",
	"team",
	"birthdays",
	"directory"
];
const PeopleWidgetSchema = z.object({
	type: z.literal("people"),
	feedId: IdSchema,
	style: z.enum(PEOPLE_STYLES).default("team"),
	title: z.string().trim().max(80).default(""),
	/** Column keys; '' = not shown. */
	nameColumn: z.string().trim().min(1, "Choose the names column").max(40),
	photoColumn: z.string().trim().max(40).default(""),
	roleColumn: z.string().trim().max(40).default(""),
	/** A message (spotlight), department or location (directory), anything else to show under the role. */
	detailColumn: z.string().trim().max(40).default(""),
	/** Directory: a phone or extension, shown at the end of the row. */
	extraColumn: z.string().trim().max(40).default(""),
	/** Birthdays: the date column (2026-03-14, 14/03, 14/03/1990, 14 March). */
	dateColumn: z.string().trim().max(40).default(""),
	birthdayRange: z.enum([
		"today",
		"week",
		"month"
	]).default("week"),
	/** Team grid size; directory rows per page = columns × rows × 2. */
	columns: z.int().min(1).max(6).default(4),
	rows: z.int().min(1).max(4).default(2),
	/** Seconds per person (spotlight) or per page (team, birthdays, directory). */
	slideSeconds: z.int().min(3).max(120).default(10),
	accent: HexColorSchema.default("#F5B301"),
	language: AppLanguageSchema.default("en"),
	...appearance
});
/**
* Meeting room: a room-door screen fed by the room's calendar link (same iCal links and API reading as the Calendar
* app, `player/calendar`): the room name, a big FREE / BUSY, the meeting on now with its time left, the next one and
* the rest of today. `hideTitles` shows "Busy" instead of meeting titles and organisers (privacy).
*/
const MEETING_ROOM_LAYOUTS = [
	"auto",
	"landscape",
	"portrait"
];
const MeetingRoomWidgetSchema = z.object({
	type: z.literal("meeting_room"),
	icalUrl: CalendarUrlSchema,
	/** Shown at the top; blank = the calendar's own name. */
	roomName: z.string().trim().max(60).default(""),
	hideTitles: z.boolean().default(false),
	showOrganizer: z.boolean().default(true),
	/** How many of today's later meetings to list (0 = none). */
	maxUpcoming: z.int().min(0).max(8).default(4),
	format: z.enum(["12h", "24h"]).default("24h"),
	/** auto = from the zone's shape. */
	layout: z.enum(MEETING_ROOM_LAYOUTS).default("auto"),
	freeColor: HexColorSchema.default("#16A34A"),
	busyColor: HexColorSchema.default("#DC2626"),
	/** >= 5 min: protects calendar servers. */
	refreshIntervalSec: z.int().min(300).max(86400).default(300),
	language: AppLanguageSchema.default("en"),
	...appearance
});
/**
* Streaming video: a live stream (HLS .m3u8, MPEG-DASH .mpd) or a video file played from its link (https). Live
* streams can't play offline: players show the fallback (poster and/or text) while connecting or when the stream is
* down, and try again with a growing wait (streamRetryDelayMs) until it is back.
*/
const StreamWidgetSchema = z.object({
	type: z.literal("stream"),
	url: HttpsUrlSchema,
	muted: z.boolean().default(true),
	fit: z.enum(["contain", "cover"]).default("contain"),
	/** A picture shown while connecting / when the stream is down ('' = none). */
	posterUrl: z.union([z.literal(""), HttpsUrlSchema]).default(""),
	/** Text shown while connecting / when the stream is down ('' = none). */
	fallbackText: z.string().trim().max(120).default(""),
	language: AppLanguageSchema.default("en"),
	...appearance
});
/**
* Social wall from a Data Feed (a Google Sheet, or an RSS/Atom feed such as a Mastodon account's): posts as cards with
* author, handle, avatar, text, picture, time ago and platform badge. Styles: a wall paging through, one big post at a
* time, or a horizontal strip scrolling like a ticker. The feed's table reaches players as a verified file
* (`WidgetAsset.feed`); pictures load from their links and players keep them. No live network logins.
*/
const SOCIAL_WALL_STYLES = [
	"wall",
	"spotlight",
	"strip"
];
const SocialWallWidgetSchema = z.object({
	type: z.literal("social_wall"),
	feedId: IdSchema,
	style: z.enum(SOCIAL_WALL_STYLES).default("wall"),
	title: z.string().trim().max(80).default(""),
	/** Column keys; '' = not shown. */
	textColumn: z.string().trim().min(1, "Choose the post text column").max(40),
	authorColumn: z.string().trim().max(40).default(""),
	handleColumn: z.string().trim().max(40).default(""),
	avatarColumn: z.string().trim().max(40).default(""),
	imageColumn: z.string().trim().max(40).default(""),
	dateColumn: z.string().trim().max(40).default(""),
	/** A platform name per post (x, instagram, facebook…); '' = guessed from the handle or link. */
	platformColumn: z.string().trim().max(40).default(""),
	/** Newest first, at most this many. */
	maxPosts: z.int().min(1).max(100).default(20),
	/** Wall size (the strip shows `columns` cards at a time). */
	columns: z.int().min(1).max(6).default(3),
	rows: z.int().min(1).max(3).default(2),
	/** Seconds per page (wall), per post (spotlight) or per card scrolling past (strip). */
	slideSeconds: z.int().min(3).max(120).default(10),
	imagesOnly: z.boolean().default(false),
	showPlatform: z.boolean().default(true),
	accent: HexColorSchema.default("#1D9BF0"),
	language: AppLanguageSchema.default("en"),
	...appearance
});
const WidgetConfigSchema = z.discriminatedUnion("type", [
	PrayerTimeWidgetSchema,
	RssWidgetSchema,
	WeatherWidgetSchema,
	TimeWidgetSchema,
	CountdownWidgetSchema,
	QrCodeWidgetSchema,
	TextWidgetSchema,
	TickerWidgetSchema,
	MenuBoardWidgetSchema,
	WorldClockWidgetSchema,
	WebPageWidgetSchema,
	YouTubeWidgetSchema,
	GoogleSlidesWidgetSchema,
	CalendarWidgetSchema,
	ChartWidgetSchema,
	PhotosWidgetSchema,
	PeopleWidgetSchema,
	DashboardWidgetSchema,
	MeetingRoomWidgetSchema,
	StreamWidgetSchema,
	SocialWallWidgetSchema
]);
/** "https://youtu.be/ID", "…watch?v=ID&list=PL…", "…/shorts/ID", "…/embed/ID" → ids (null when none). */
function parseYouTubeUrl(input) {
	let url;
	try {
		url = new URL(input.trim());
	} catch {
		return {
			videoId: /^[A-Za-z0-9_-]{11}$/.test(input.trim()) ? input.trim() : null,
			playlistId: null
		};
	}
	const host = url.hostname.replace(/^www\.|^m\./, "");
	let videoId = null;
	if (host === "youtu.be") videoId = url.pathname.slice(1, 12);
	else if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) videoId = url.searchParams.get("v") ?? /^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})/.exec(url.pathname)?.[1] ?? null;
	const list = url.searchParams.get("list");
	return {
		videoId: videoId && /^[A-Za-z0-9_-]{11}$/.test(videoId) ? videoId : null,
		playlistId: list && /^[A-Za-z0-9_-]{10,64}$/.test(list) ? list : null
	};
}
/** "https://docs.google.com/presentation/d/<id>/edit…" (or /pub, /embed) → id. */
function parseGoogleSlidesUrl(input) {
	const m = /docs\.google\.com\/presentation\/d\/(e\/)?([A-Za-z0-9_-]{20,100})/.exec(input.trim());
	return m ? `${m[1] ?? ""}${m[2]}` : null;
}
/** The embed address the player and the CMS preview open for a Slides app. */
function googleSlidesEmbedUrl(w) {
	return `https://docs.google.com/presentation/d/${w.presentationId}/embed?start=true&loop=${w.loop}&delayms=${w.slideSeconds * 1e3}&rm=minimal`;
}
/** Which BI tool a pasted link belongs to. */
function dashboardProvider(input) {
	let host = "";
	try {
		host = new URL(input.trim()).hostname.toLowerCase();
	} catch {
		return "other";
	}
	if (host.endsWith("powerbi.com")) return "powerbi";
	if (host === "lookerstudio.google.com" || host === "datastudio.google.com") return "looker";
	if (host.endsWith("tableau.com")) return "tableau";
	if (host.endsWith("grafana.net") || /\/(d|public-dashboards)\//.test(input) && /grafana/i.test(input)) return "grafana";
	if (/\/public\/dashboard\//.test(input)) return "metabase";
	return "other";
}
/**
* The address a screen opens for a dashboard: the tool's own embed / kiosk form, without menus or toolbars.
*  - Power BI: "Publish to web" (view?r=…) and reportEmbed links, navigation and filter panes hidden.
*  - Looker Studio: /reporting/… → /embed/reporting/….
*  - Tableau Public: the view with embed on and the toolbar and Tableau home bar off.
*  - Grafana: kiosk mode in the chosen theme.
*  - Metabase public dashboards: no border, the title kept.
*/
function dashboardEmbedUrl(w) {
	let u;
	try {
		u = new URL(w.url.trim());
	} catch {
		return w.url;
	}
	switch (w.provider) {
		case "powerbi":
			if (/reportEmbed/i.test(u.pathname)) {
				u.searchParams.set("navContentPaneEnabled", "false");
				u.searchParams.set("filterPaneEnabled", "false");
			}
			u.searchParams.set("chromeless", "1");
			break;
		case "looker":
			if (!u.pathname.startsWith("/embed/")) u.pathname = `/embed${u.pathname}`;
			break;
		case "tableau":
			u.searchParams.set(":embed", "y");
			u.searchParams.set(":showVizHome", "no");
			u.searchParams.set(":toolbar", "no");
			u.searchParams.set(":tabs", "no");
			break;
		case "grafana":
			u.searchParams.set("kiosk", "");
			u.searchParams.set("theme", w.theme);
			break;
		case "metabase": u.hash = "bordered=false&titled=true";
	}
	return u.toString().replace(/([?&])kiosk=(&|$)/, "$1kiosk$2").replace(/([?&])%3A/g, "$1:");
}
/**
* What the CMS learns about a dashboard link before previewing it (`dashboard-check`):
*  - ok: it loads and may be shown inside another page;
*  - blocked: it loads, but the site forbids framing (preview, browser and ChromeOS players stay blank; Android and
*    desktop players still show it);
*  - login: it sends people to a sign-in page (screens can't sign in);
*  - not_found / error: the address answers 404 / another error, or can't be reached.
*/
const DashboardCheckSchema = z.object({
	verdict: z.enum([
		"ok",
		"blocked",
		"login",
		"not_found",
		"error"
	]),
	status: z.int().nullable(),
	detail: z.string().max(300).nullable()
});
/** Feed URLs: https only (shared by the RSS app and its CMS preview). */
const RssFeedUrlSchema = HttpUrlSchema.refine((u) => u.startsWith("https://"), "Feeds must use https");
/** First headlines of a feed, fetched by the API for the CMS live preview (the player fetches on its own). */
const RssPreviewSchema = z.object({
	title: z.string().nullable(),
	items: z.array(z.object({
		title: z.string(),
		imageUrl: z.string().nullable()
	})).max(10)
});
/** A feed document for a player that can't fetch it itself (`player/rss`); the player parses it with its own rules. */
const PlayerRssFeedSchema = z.object({ xml: z.string() });
/** One event as players get it (`player/calendar`): times in UTC; all-day events carry their dates at 00:00Z. */
const CalendarEventSchema = z.object({
	title: z.string().max(200),
	start: z.string(),
	end: z.string(),
	allDay: z.boolean(),
	location: z.string().max(200).nullable(),
	/** The organiser's name (or address) when the calendar shares it; absent from older APIs. */
	organizer: z.string().max(120).nullable().optional()
});
const CalendarFeedSchema = z.object({
	name: z.string().max(120).nullable(),
	events: z.array(CalendarEventSchema).max(50)
});
/** Widgets whose config carries a location. */
const widgetLocation = (config) => config.type === "prayer_time" || config.type === "weather" ? config.location : null;
//#endregion
//#region src/manifest.ts
/**
* Manifest wire version. v2 (Phase 5) replaced single-surface playlists with multi-zone layouts.
* Separate from CONTRACT_SCHEMA_VERSION (heartbeats, pairing) which stays at 1.
*/
const MANIFEST_SCHEMA_VERSION = 2;
/** Shortest on-screen time for any item. Gives the off-screen B-player room to pre-buffer N+1. */
const MIN_ITEM_DURATION_MS = 1e3;
/** Longest crossfade allowed; longer fades keep two decoders hot for too long on weak SoCs. */
const MAX_TRANSITION_MS = 3e3;
const MAX_ASSETS_PER_MANIFEST = 2e3;
const MAX_ITEMS_PER_ZONE = 500;
const MAX_ZONES_PER_LAYOUT = 12;
const MAX_SCHEDULES_PER_MANIFEST = 500;
/** Layouts a schedule plays in sequence (a campaign): A → B → C → A … */
const MAX_LAYOUTS_PER_SCHEDULE = 20;
/** Shortest time a layout stays on screen in a multi-layout sequence (covers pre-warm + transition). */
const MIN_LAYOUT_DURATION_MS = 3e3;
/** 24 h. */
const MAX_LAYOUT_DURATION_MS = 864e5;
/** Default dwell for a layout in a sequence. */
const DEFAULT_LAYOUT_DURATION_MS = 15e3;
/** 8K UHD — larger canvases make no sense on any player hardware. */
const MAX_CANVAS_PX = 7680;
/** Pages kept from an uploaded PDF (longer documents are refused: signage is not a document reader). */
const MAX_PDF_PAGES = 200;
/** Default time each PDF page stays on screen. */
const DEFAULT_PDF_PAGE_MS = 1e4;
/** Library kinds. `composition` never reaches a manifest: players get it as an `app` (compositions.ts). */
const MediaKindSchema = z.enum([
	"video",
	"image",
	"widget",
	"app",
	"audio",
	"pdf",
	"composition"
]);
const fileAssetBase = {
	assetId: IdSchema,
	/** Immutable, content-addressed download URL. Must support HTTP `Range` requests (resumable downloads). */
	url: HttpUrlSchema,
	/** SHA-256 of the exact file bytes. The player MUST verify this before the asset may enter a timeline. */
	sha256: Sha256HexSchema,
	/** Exact byte length — lets the player pre-check free space and validate `Content-Range` resumes. */
	sizeBytes: PositiveIntSchema,
	width: PositiveIntSchema,
	height: PositiveIntSchema
};
const VideoAssetSchema = z.object({
	...fileAssetBase,
	kind: z.literal("video"),
	mimeType: z.enum(["video/mp4", "video/webm"]),
	/** Compiler only emits codecs the screen reported in `HeartbeatPayload.capabilities.videoCodecs`. */
	codec: VideoCodecSchema,
	/** Intrinsic media duration. */
	durationMs: PositiveIntSchema,
	/**
	* A still of the video (WebP), verified like media: shown while it loads and in its place when the display can't
	* play it, so a section never goes black. Optional: added later (older servers send none).
	*/
	poster: z.object({
		url: HttpUrlSchema,
		sha256: Sha256HexSchema,
		sizeBytes: PositiveIntSchema
	}).nullable().optional()
});
const ImageAssetSchema = z.object({
	...fileAssetBase,
	kind: z.literal("image"),
	mimeType: z.enum([
		"image/jpeg",
		"image/png",
		"image/webp"
	])
});
/** A custom font file an app is drawn with (TTF/OTF, verified like media). */
const FontFileSchema = z.object({
	fontId: IdSchema,
	family: z.string().min(1).max(120),
	url: HttpUrlSchema,
	sha256: Sha256HexSchema,
	sizeBytes: PositiveIntSchema,
	format: z.enum(["ttf", "otf"])
});
/**
* Rendered natively from `config`. Nothing to verify, always available offline — except its custom `font`, a file
* downloaded and verified like media (players that don't know it, or can't load it, use their own font).
*/
const WidgetAssetSchema = z.object({
	assetId: IdSchema,
	kind: z.literal("widget"),
	config: WidgetConfigSchema,
	font: FontFileSchema.optional(),
	/** Chart apps: the bound Data Feed's approved table (`{columns, rows, version}` JSON), verified like media. */
	feed: z.object({
		url: HttpUrlSchema,
		sha256: Sha256HexSchema,
		sizeBytes: PositiveIntSchema
	}).optional()
});
/** A Data Feed's approved table as a file (`{columns, rows, version}` JSON), handed to the app that binds it. */
const AppFeedFileSchema = z.object({
	/** The app's slot key (manifest.json `feeds[].key`). */
	key: z.string().min(1).max(40),
	url: HttpUrlSchema,
	sha256: Sha256HexSchema,
	sizeBytes: PositiveIntSchema
});
/**
* Phase 16 Step 15d: a custom HTML5 app. `url` is its zip (content-addressed, verified like media, unpacked once on
* the player); it runs in a sandboxed WebView from local files. Only sent to players reporting `capabilities.apps`.
*/
const AppAssetSchema = z.object({
	assetId: IdSchema,
	kind: z.literal("app"),
	url: HttpUrlSchema,
	sha256: Sha256HexSchema,
	sizeBytes: PositiveIntSchema,
	/** The custom app version (display / logs). */
	versionId: IdSchema,
	/** File opened inside the zip. */
	entry: z.string().min(1).max(300),
	/** The content item's settings as a JSON object text (passed to the app as-is). */
	settings: z.string().max(2e5),
	feeds: z.array(AppFeedFileSchema).max(10),
	/** https origins the app may load from; any other network request is blocked. */
	network: z.array(z.string().max(300)).max(20)
});
/**
* Phase 16 Step 15f: background audio (AAC in MP4), played by a campaign's Audio Track (`layouts[].audio`), never in a
* zone. Only sent to players reporting `capabilities.audio`.
*/
const AudioAssetSchema = z.object({
	assetId: IdSchema,
	kind: z.literal("audio"),
	url: HttpUrlSchema,
	sha256: Sha256HexSchema,
	sizeBytes: PositiveIntSchema,
	mimeType: z.literal("audio/mp4"),
	durationMs: PositiveIntSchema
});
/** How a PDF turns its pages: the item transition types, at most MAX_TRANSITION_MS. */
const PdfPageTransitionSchema = z.object({
	type: z.enum([
		"cut",
		"crossfade",
		"slide",
		"flip"
	]),
	durationMs: z.int().min(0).max(MAX_TRANSITION_MS)
});
const DEFAULT_PDF_PAGE_TRANSITION = {
	type: "crossfade",
	durationMs: 400
};
/** One page of a PDF, turned into an image on the server (every player shows it like any other image). */
const PdfPageSchema = z.object({
	url: HttpUrlSchema,
	sha256: Sha256HexSchema,
	sizeBytes: PositiveIntSchema,
	width: PositiveIntSchema,
	height: PositiveIntSchema
});
/**
* A PDF document: its pages as WebP images, shown in order for `pageMs` each (the item's `durationMs` is
* pages × pageMs). Only sent to players reporting `capabilities.pdf`.
*/
const PdfAssetSchema = z.object({
	assetId: IdSchema,
	kind: z.literal("pdf"),
	pageMs: z.int().min(MIN_ITEM_DURATION_MS),
	pages: z.array(PdfPageSchema).min(1).max(200),
	/** How one page gives way to the next (absent: a 400 ms crossfade). The first page enters like the item. */
	pageTransition: PdfPageTransitionSchema.optional()
});
const MediaAssetSchema = z.discriminatedUnion("kind", [
	VideoAssetSchema,
	ImageAssetSchema,
	WidgetAssetSchema,
	AppAssetSchema,
	AudioAssetSchema,
	PdfAssetSchema
]);
const TransitionSchema = z.object({
	/**
	* `cut` = instant swap (no black frame); `crossfade` = alpha fade; `slide` = outgoing and incoming
	* translate edge-to-edge (right → left); `flip` = 3D card flip around the vertical axis.
	*
	* Since manifest Phase 14 the animated types are SCENE transitions (`layouts[].transition`): they move
	* the whole screen — every zone, widget and ticker of the layout together. Item transitions inside a
	* zone are rendered as cuts by Phase 14+ players (one surface per zone, see decoder-budget.ts).
	*/
	type: z.enum([
		"crossfade",
		"cut",
		"slide",
		"flip"
	]),
	durationMs: z.int().min(0).max(MAX_TRANSITION_MS)
});
const TimelineItemSchema = z.object({
	itemId: IdSchema,
	assetId: IdSchema,
	/**
	* On-screen duration. REQUIRED for image, widget and app items; optional for video (omitted = play to the
	* asset's intrinsic end).
	*/
	durationMs: z.int().min(MIN_ITEM_DURATION_MS).optional(),
	/**
	* Transition used when this item ENTERS the zone (the campaign item's "Enters with"). Phase 17.7 players
	* animate it within the zone; video → video and app → app stay cuts (one decoder / one WebView per zone).
	*/
	transition: TransitionSchema,
	/**
	* How this item fills its zone (the campaign's fit, or the item's own). Absent = the loop entry's `fit` (the
	* player's Display Settings); older players ignore it and use that.
	*/
	fit: z.enum([
		"fill",
		"fit",
		"stretch"
	]).optional(),
	/**
	* Mute switch: play this item without sound. Set when the media itself, or the campaign or playlist it plays in,
	* is muted (the compiler also drops a muted campaign's or playlist's Audio Track). Absent = sound as usual;
	* older players ignore it.
	*/
	muted: z.boolean().optional()
});
const ZoneRoleSchema = z.enum(["primary", "secondary"]);
const ZoneSchema = z.object({
	zoneId: IdSchema,
	/** The layout this zone belongs to; `schedules[].layoutId` selects which layout's zones render. */
	layoutId: IdSchema,
	name: z.string().min(1).max(80),
	/** Canvas pixels (origin top-left). The player scales the canvas uniformly to the physical display. */
	x: z.int().min(0),
	y: z.int().min(0),
	width: PositiveIntSchema,
	height: PositiveIntSchema,
	/** Higher draws on top. */
	zIndex: z.int().min(0).max(100),
	/**
	* `primary` = the layout's main zone: the largest video zone. Its audio is the screen's audio, and the
	* scene transition waits for ITS first rendered frame. Every zone renders on a single surface.
	*/
	role: ZoneRoleSchema,
	/** Played in order and looped, independently of other zones. */
	timeline: z.array(TimelineItemSchema).min(1).max(500),
	/**
	* false: play the timeline once per pass of the layout (its scene `durationMs`), black after the last item,
	* from the top at the next pass. Absent = true (older compilers).
	*/
	loop: z.boolean().optional()
});
const CanvasSchema = z.object({
	width: z.int().min(1).max(MAX_CANVAS_PX),
	height: z.int().min(1).max(MAX_CANVAS_PX)
});
/**
* Scene settings of one layout. A schedule plays `layoutIds` in order and loops; each layout stays
* `durationMs` and ENTERS the screen with `transition`, which sweeps the whole screen (all zones, widgets
* and tickers of the layout move together). The player starts the transition only once the incoming
* layout's primary media has rendered its first frame (images: decoded).
*/
const LayoutSceneSchema = z.object({
	layoutId: IdSchema,
	/** On-screen time in a multi-layout sequence. Ignored when a schedule plays only this layout. */
	durationMs: z.int().min(MIN_LAYOUT_DURATION_MS).max(MAX_LAYOUT_DURATION_MS),
	/** How this layout enters the screen (also when a new schedule takes over). */
	transition: TransitionSchema,
	/**
	* Phase 16: this scene's own canvas. One player can receive campaigns of different sizes (a landscape
	* campaign and a portrait one); absent = the manifest `canvas`.
	*/
	canvas: CanvasSchema.optional(),
	/**
	* Phase 16 Step 15f: the campaign's Audio Track — audio assets played in order and looped while the scene is on
	* screen (continuing across scenes with the same list). `muteVideo`: the zones' video sound is off meanwhile.
	*/
	/** Phase 17.4: `shuffle` = play the files in a new random order each time round (audio playlist "Shuffle"). */
	audio: z.object({
		assetIds: z.array(IdSchema).min(1).max(500),
		muteVideo: z.boolean(),
		shuffle: z.boolean().optional()
	}).optional()
});
/**
* Phase 16: when a loop entry may play, evaluated on the player in the manifest time zone (legacy "Add
* Restriction"). All present conditions must hold; `invert` = "Don't play if".
*/
const PlayWindowSchema = z.object({
	invert: z.boolean(),
	/** ISO weekdays; empty = every day. A window crossing midnight belongs to the day it starts. */
	days: z.array(z.int().min(1).max(7)).max(7),
	/** Both null = all day; `to` < `from` crosses midnight. */
	from: LocalTimeSchema.nullable(),
	to: LocalTimeSchema.nullable(),
	/** Inclusive calendar dates (YYYY-MM-DD); null = always. */
	startDate: z.iso.date().nullable(),
	endDate: z.iso.date().nullable()
});
/**
* Phase 16 (legacy CODOT "Content In Player"): one entry of the player's ordered list. Each entry is a scene
* (`layoutId`): a campaign, or a full-screen scene the compiler made for a single image / video / app.
*/
/**
* Phase 17.4: loop entries that come from one playlist placement share a group. The player plays the group per
* its mode: `all` = every entry each pass; `cycle` = the next `perPass` entries each pass (position kept across
* passes and restarts). `shuffle` = a new random order each pass (never the same entry twice in a row); `byType`
* is already applied by the compiler, so players treat it as `manual`. Absent = play all in order (old players
* ignore the field and do exactly that).
*/
const LoopGroupSchema = z.object({
	id: IdSchema,
	mode: z.enum(["all", "cycle"]),
	order: z.enum([
		"manual",
		"byType",
		"shuffle"
	]),
	perPass: z.int().min(1).max(100)
});
const LoopItemSchema = z.object({
	itemId: IdSchema,
	layoutId: IdSchema,
	/** How long the entry stays (already resolved: override, else the content's natural length). */
	durationMs: z.int().min(MIN_ITEM_DURATION_MS).max(MAX_LAYOUT_DURATION_MS),
	/** How it enters the screen. */
	transition: TransitionSchema,
	/** How media fills its sections: fill (cover), fit (letterbox), stretch. */
	fit: z.enum([
		"fill",
		"fit",
		"stretch"
	]),
	/** null = always (legacy "No restrictions"). Tag rules are resolved by the compiler (it knows the player's tags). */
	window: PlayWindowSchema.nullable(),
	/**
	* Phase 16 Step 10 (Logic mode): the item plays when ANY of these windows matches (each is a plain,
	* non-inverted window). Present → it replaces `window`, which then holds a never-matching window so older
	* players skip the item rather than play it at the wrong time.
	*/
	windows: z.array(PlayWindowSchema).max(200).optional(),
	group: LoopGroupSchema.optional()
});
/**
* Phase 17.7 Synchronized Playback: this player plays its group's lead loop in lock-step with the other members.
* Entries play in list order (no shuffle or cycle), each for its `durationMs`, and the list repeats; where the
* loop is at any moment is fixed by the clock: `(now − anchorMs) mod (sum of durations)` — so every member,
* starting or restarting at any time, switches content at the same instants.
*/
const LoopSyncSchema = z.object({
	groupId: IdSchema,
	leadScreenId: IdSchema,
	/** Unix epoch ms: a moment when the loop began a pass (set when sync is turned on or the lead changes). */
	anchorMs: z.int().min(0),
	/**
	* The lead's IANA time zone: every member checks the entries' day / time windows in it, so a group spread across
	* time zones keeps one list and switches together. Absent (older manifests): the player's own `timezone`.
	*/
	timezone: IanaTimeZoneSchema.optional()
});
const PlayerLoopSchema = z.object({
	/** Legacy "Loop": true = repeat the list; false = play once and hold the last entry. */
	repeat: z.boolean(),
	items: z.array(LoopItemSchema).max(2e3),
	/** Phase 17.7: present = play in lock-step with the group (see LoopSyncSchema). Older players ignore it. */
	sync: LoopSyncSchema.optional()
});
/** ISO-8601 weekday: 1 = Monday … 7 = Sunday. */
const IsoWeekdaySchema = z.int().min(1).max(7);
const DailyWindowSchema = z.object({
	/** Inclusive, in the manifest `timezone`. */
	startTime: LocalTimeSchema,
	/** Exclusive. If `endTime` < `startTime` the window crosses midnight (e.g. 22:00 → 02:00). */
	endTime: LocalTimeSchema
}).refine((w) => w.startTime !== w.endTime, {
	message: "startTime and endTime must differ; use dailyWindow = null for all-day",
	path: ["endTime"]
});
const ScheduleSchema = z.object({
	scheduleId: IdSchema,
	/** First layout of the sequence (= `layoutIds[0]`). */
	layoutId: IdSchema,
	/**
	* Layouts played in order and looped while this schedule wins (a campaign). One entry = a single
	* layout that stays on screen, with no scene transitions. Absent (manifests compiled before Phase 14,
	* still cached on devices) = `[layoutId]`. The compiler always emits it.
	*/
	layoutIds: z.array(IdSchema).min(1).max(20).optional(),
	/** Higher wins. */
	priority: z.int().min(0).max(1e3),
	/**
	* Tie-break at equal priority: `screen` (targeted at this screen) beats `group` (inherited from the
	* screen's group); then lowest `scheduleId`. Deterministic on every device.
	*/
	specificity: z.enum(["screen", "group"]),
	activeFrom: IsoDateTimeSchema,
	/** Exclusive. `null` = open-ended. */
	activeUntil: IsoDateTimeSchema.nullable(),
	/** Weekdays on which a window may START (an overnight window belongs to the day it starts). */
	daysOfWeek: z.array(IsoWeekdaySchema).min(1).max(7).refine((d) => new Set(d).size === d.length, { message: "daysOfWeek must not contain duplicates" }),
	/** `null` = all day. */
	dailyWindow: DailyWindowSchema.nullable()
}).refine((s) => s.activeUntil === null || Date.parse(s.activeUntil) > Date.parse(s.activeFrom), {
	message: "activeUntil must be after activeFrom",
	path: ["activeUntil"]
}).refine((s) => !s.layoutIds || s.layoutIds[0] === s.layoutId, {
	message: "layoutId must equal layoutIds[0]",
	path: ["layoutId"]
});
const ScreenManifestSchema = z.object({
	schemaVersion: z.literal(2),
	screenId: IdSchema,
	/** Strictly increasing per screen. Players never apply a revision <= the one they run. */
	revision: PositiveIntSchema,
	compiledAt: IsoDateTimeSchema,
	/** Zone used to evaluate `daysOfWeek` / `dailyWindow` and the default for `time` widgets. */
	timezone: IanaTimeZoneSchema,
	/** Canvas size in pixels; every layout compiled into this manifest shares it. */
	canvas: CanvasSchema,
	/**
	* Scene settings of every layout referenced by `schedules` (exactly one entry per layout). Absent in
	* manifests compiled before Phase 14 (cached on devices): every layout then enters with a `cut` and
	* stays DEFAULT_LAYOUT_DURATION_MS. The compiler always emits it.
	*/
	layouts: z.array(LayoutSceneSchema).max(500).optional(),
	/**
	* Phase 16: the player's own content list. When present and an entry's window is open, the player plays
	* the loop and ignores `schedules` (which the compiler still fills for players older than Phase 16).
	*/
	loop: PlayerLoopSchema.optional(),
	/** Phase 16 Step 8: effective Player Settings (absent = the player's built-in behaviour). */
	settings: PlayerSettingsSchema.optional(),
	/** Phase 16 Step 11: touch / remote-key interactions (absent = none). Targets' scenes are in `layouts`. */
	interactions: z.array(ManifestInteractionSchema).max(100).optional(),
	/** Zones of every layout referenced by `schedules`, each with its own linear timeline. */
	zones: z.array(ZoneSchema).max(600),
	schedules: z.array(ScheduleSchema).max(500),
	/** Deduplicated set of every asset referenced by a timeline — also the player's LRU "keep" set. */
	assets: z.array(MediaAssetSchema).max(MAX_ASSETS_PER_MANIFEST)
}).superRefine((m, ctx) => {
	const issue = (message, path) => ctx.addIssue({
		code: "custom",
		message,
		path
	});
	const assetsById = /* @__PURE__ */ new Map();
	m.assets.forEach((a, i) => {
		if (assetsById.has(a.assetId)) issue(`Duplicate assetId ${a.assetId}`, [
			"assets",
			i,
			"assetId"
		]);
		assetsById.set(a.assetId, a);
		if (a.kind === "widget" && widgetLocation(a.config)?.source === "screen") issue("Widget location must be resolved to fixed coordinates by the compiler", [
			"assets",
			i,
			"config",
			"location"
		]);
	});
	const zoneIds = /* @__PURE__ */ new Set();
	const itemIds = /* @__PURE__ */ new Set();
	const referenced = /* @__PURE__ */ new Set();
	const zonesByLayout = /* @__PURE__ */ new Map();
	const canvasOf = (layoutId) => m.layouts?.find((l) => l.layoutId === layoutId)?.canvas ?? m.canvas;
	m.zones.forEach((z, zi) => {
		if (zoneIds.has(z.zoneId)) issue(`Duplicate zoneId ${z.zoneId}`, [
			"zones",
			zi,
			"zoneId"
		]);
		zoneIds.add(z.zoneId);
		const c = canvasOf(z.layoutId);
		if (z.x + z.width > c.width || z.y + z.height > c.height) issue(`Zone “${z.name}” extends outside the ${c.width}×${c.height} canvas`, ["zones", zi]);
		zonesByLayout.set(z.layoutId, [...zonesByLayout.get(z.layoutId) ?? [], z]);
		z.timeline.forEach((item, ii) => {
			const path = [
				"zones",
				zi,
				"timeline",
				ii
			];
			if (itemIds.has(item.itemId)) issue(`Duplicate itemId ${item.itemId}`, [...path, "itemId"]);
			itemIds.add(item.itemId);
			referenced.add(item.assetId);
			const asset = assetsById.get(item.assetId);
			if (!asset) issue(`Item references unknown assetId ${item.assetId}`, [...path, "assetId"]);
			else if (asset.kind === "audio") issue("Audio plays on a campaign Audio Track, not in a zone", [...path, "assetId"]);
			else if (asset.kind !== "video" && item.durationMs === void 0) issue(`${asset.kind === "image" ? "Image" : asset.kind === "app" ? "App" : asset.kind === "pdf" ? "PDF" : "Widget"} items require durationMs`, [...path, "durationMs"]);
		});
	});
	for (const [layoutId, zones] of zonesByLayout) {
		const primaries = zones.filter((z) => z.role === "primary");
		if (primaries.length !== 1) issue(`Layout ${layoutId} must have exactly one primary zone`, ["zones"]);
		const report = analyzeDecoderBudget(zones.map((z) => ({
			zoneId: z.zoneId,
			name: z.name,
			width: z.width,
			height: z.height,
			items: z.timeline.map((t) => ({ kind: assetsById.get(t.assetId)?.kind ?? "image" }))
		})));
		if (primaries.length === 1 && report.primaryZoneId !== primaries[0].zoneId) issue(`Layout ${layoutId}: primary zone must be the largest video zone (${report.primaryZoneId})`, ["zones"]);
		for (const v of report.violations) issue(v.message, ["zones", m.zones.findIndex((z) => z.zoneId === v.zoneId)]);
	}
	const scenes = /* @__PURE__ */ new Set();
	m.layouts?.forEach((l, li) => {
		l.audio?.assetIds.forEach((id, ai) => {
			referenced.add(id);
			if (assetsById.get(id)?.kind !== "audio") issue(`Audio track references ${id}, which is not an audio asset`, [
				"layouts",
				li,
				"audio",
				"assetIds",
				ai
			]);
		});
		if (scenes.has(l.layoutId)) issue(`Duplicate layout ${l.layoutId}`, [
			"layouts",
			li,
			"layoutId"
		]);
		scenes.add(l.layoutId);
		if (!zonesByLayout.has(l.layoutId)) issue(`Layout ${l.layoutId} has no zones`, [
			"layouts",
			li,
			"layoutId"
		]);
	});
	const scheduleIds = /* @__PURE__ */ new Set();
	const scheduled = /* @__PURE__ */ new Set();
	m.schedules.forEach((s, si) => {
		if (scheduleIds.has(s.scheduleId)) issue(`Duplicate scheduleId ${s.scheduleId}`, [
			"schedules",
			si,
			"scheduleId"
		]);
		scheduleIds.add(s.scheduleId);
		(s.layoutIds ?? [s.layoutId]).forEach((id, i) => {
			const path = s.layoutIds ? [
				"schedules",
				si,
				"layoutIds",
				i
			] : [
				"schedules",
				si,
				"layoutId"
			];
			scheduled.add(id);
			if (!zonesByLayout.has(id)) issue(`Schedule references layout ${id} with no zones`, path);
			if (m.layouts && !scenes.has(id)) issue(`Schedule references layout ${id} missing from layouts`, path);
		});
	});
	m.loop?.items.forEach((it, i) => {
		scheduled.add(it.layoutId);
		if (!zonesByLayout.has(it.layoutId)) issue(`Loop entry references layout ${it.layoutId} with no zones`, [
			"loop",
			"items",
			i,
			"layoutId"
		]);
	});
	m.interactions?.forEach((x, i) => {
		if (x.action.type !== "play") return;
		scheduled.add(x.action.layoutId);
		if (!zonesByLayout.has(x.action.layoutId)) issue(`Interaction target ${x.action.layoutId} has no zones`, [
			"interactions",
			i,
			"action"
		]);
	});
	for (const layoutId of /* @__PURE__ */ new Set([...zonesByLayout.keys(), ...scenes])) if (!scheduled.has(layoutId)) issue(`Layout ${layoutId} is not used by any schedule, loop entry or interaction`, ["layouts"]);
	m.assets.forEach((a, i) => {
		if (!referenced.has(a.assetId) && a.assetId !== m.settings?.idleScreen?.assetId) issue(`Asset ${a.assetId} is not referenced by any timeline item`, [
			"assets",
			i,
			"assetId"
		]);
	});
});
const ManifestPointerSchema = z.object({
	screenId: IdSchema,
	revision: PositiveIntSchema,
	/** SHA-256 of the exact bytes served at `url`. Verify BEFORE parsing. */
	sha256: Sha256HexSchema,
	sizeBytes: PositiveIntSchema,
	/** Immutable document URL: `.../manifest_v{revision}_{sha256}.json`. */
	url: HttpUrlSchema
});
/** Canonical manifest file name produced by the compiler. */
function manifestFileName(revision, sha256) {
	return `manifest_v${revision}_${sha256}.json`;
}
//#endregion
//#region src/heartbeat.ts
const MacAddressSchema = z.string().regex(/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/, "Expected a MAC address like aa:bb:cc:dd:ee:ff");
/** Android's privacy placeholder: carries no information, stored as unknown (null). */
const PLACEHOLDER_MAC_ADDRESS = "02:00:00:00:00:00";
const IpAddressSchema = z.union([z.ipv4(), z.ipv6()]);
const ScreenResolutionSchema = z.string().regex(/^[1-9]\d{1,4}x[1-9]\d{1,4}$/, "Expected \"{width}x{height}\", e.g. 1920x1080");
/** Max heartbeats per upload. The player buffers in Room while offline and flushes in batches. */
const MAX_HEARTBEATS_PER_BATCH = 200;
/** How often the player captures a heartbeat (captured even offline; uploaded when connectivity is validated). */
const HEARTBEAT_INTERVAL_MS = 6e4;
/** CMS marks a screen offline after this much silence (3 missed heartbeats). */
const SCREEN_OFFLINE_AFTER_MS = HEARTBEAT_INTERVAL_MS * 3;
const PlaybackStateSchema = z.enum([
	"booting",
	"playing",
	"fallback"
]);
const FallbackReasonSchema = z.enum([
	"no_manifest",
	"no_active_schedule",
	"assets_pending",
	"asset_verification_failed",
	"decoder_error",
	"storage_full"
]);
const NetworkTransportSchema = z.enum([
	"ethernet",
	"wifi",
	"cellular",
	"none",
	"unknown"
]);
const PlaybackStatusSchema = z.object({
	state: PlaybackStateSchema,
	/** Required when `state === 'fallback'`, otherwise null. */
	fallbackReason: FallbackReasonSchema.nullable(),
	/** Manifest currently driving playback (null when none has ever been verified). */
	manifestRevision: PositiveIntSchema.nullable(),
	manifestSha256: Sha256HexSchema.nullable(),
	/** Manifest v2 players report the ACTIVE layoutId here (field name kept for wire compatibility). */
	currentPlaylistId: IdSchema.nullable(),
	currentAssetId: IdSchema.nullable(),
	/**
	* Is the player's own screen in front? false: it runs (and may report `playing`) but another app or the home
	* screen covers it, so the display isn't showing the content. Absent / null: not reported (older players, other
	* platforms, or the display is off).
	*/
	onScreen: z.boolean().nullable().optional()
}).refine((p) => p.state === "fallback" === (p.fallbackReason !== null), {
	message: "fallbackReason must be set if and only if state is \"fallback\"",
	path: ["fallbackReason"]
});
/**
* Why a player can't take new content: no connection to the server, refused (signed out / not allowed), the
* content's address is not its server's, the published content was rejected (invalid / failed checks), the
* storage is full, or something else.
*/
const SyncErrorCodeSchema = z.enum([
	"unreachable",
	"not_allowed",
	"wrong_address",
	"not_found",
	"rejected",
	"storage_full",
	"other"
]);
const SyncStatusSchema = z.object({
	lastSuccessfulSyncAt: IsoDateTimeSchema.nullable(),
	/** Newer revision being staged (downloading/verifying) but NOT yet playing. */
	stagedManifestRevision: PositiveIntSchema.nullable(),
	assetsVerified: NonNegativeIntSchema,
	assetsPending: NonNegativeIntSchema,
	assetsFailed: NonNegativeIntSchema,
	/** Proof-of-play rows buffered in Room awaiting upload. */
	bufferedPopEvents: NonNegativeIntSchema,
	/** Files being downloaded right now (null when idle). Optional: added later. */
	download: z.object({
		filesDone: NonNegativeIntSchema,
		filesTotal: NonNegativeIntSchema,
		bytesDone: z.number().int().nonnegative(),
		bytesTotal: z.number().int().nonnegative()
	}).nullable().optional(),
	/**
	* Why new content isn't arriving: the player's failed sync attempts since its last success (cleared by a
	* success). `since` = the first failure of this run, so the CMS can tell a blip from a player that's stuck.
	* Optional: added later.
	*/
	lastError: z.object({
		code: SyncErrorCodeSchema,
		/** The player's own words (technical), for the tooltip and support. */
		detail: z.string().max(300),
		revision: PositiveIntSchema.nullable(),
		since: IsoDateTimeSchema,
		at: IsoDateTimeSchema
	}).nullable().optional()
});
const StorageStatusSchema = z.object({
	totalBytes: PositiveIntSchema,
	freeBytes: NonNegativeIntSchema,
	/** Bytes used by verified media in app-specific storage. */
	mediaCacheBytes: NonNegativeIntSchema
}).refine((s) => s.freeBytes <= s.totalBytes, {
	message: "freeBytes cannot exceed totalBytes",
	path: ["freeBytes"]
});
/** Leak-detection telemetry for 24/7 operation. */
const MemoryStatusSchema = z.object({
	heapUsedBytes: NonNegativeIntSchema,
	heapMaxBytes: PositiveIntSchema,
	systemAvailBytes: NonNegativeIntSchema,
	lowMemory: z.boolean()
});
const NetworkStatusSchema = z.object({
	transport: NetworkTransportSchema,
	/** `NET_CAPABILITY_VALIDATED` — real internet reachability, not just link-up. */
	validated: z.boolean()
});
/** What the device proved it can decode at boot. The compiler only targets these. */
const DeviceCapabilitiesSchema = z.object({
	videoCodecs: z.array(VideoCodecSchema).min(1),
	maxVideoHeight: PositiveIntSchema,
	/** Phase 16 Step 15d: the player runs custom HTML5 apps (absent on older builds → apps are left out of its manifest). */
	apps: z.boolean().optional(),
	/** Phase 16 Step 15f: the player plays campaign Audio Tracks (absent → they are left out of its manifest). */
	audio: z.boolean().optional(),
	/** Phase 17.11: app (widget) types this build can show; absent → the four original ones. */
	widgets: z.array(z.string().max(30)).max(64).optional(),
	/**
	* A decoder plays H.264 High profile (what every upload is converted to). False on some very old or cheap boxes and
	* the Android 5 emulator: they get each video's Baseline "compatible" version instead. Absent (older builds) → true.
	*/
	h264High: z.boolean().optional(),
	/** The player shows PDF documents (their pages as images). Absent (older builds) → PDFs are left out of its manifest. */
	pdf: z.boolean().optional(),
	/**
	* Videos it can play at the same time (Samsung's web engine: often 1). A campaign playing more at once is flagged when
	* published. Absent → MAX_CONCURRENT_VIDEOS.
	*/
	maxVideos: z.int().min(1).max(8).optional()
});
/**
* Phase 17.7: what Android lets the player do on this device (set in the player's setup wizard or by
* provisioning). The CMS lists what's missing and which features it limits.
*/
const DevicePermissionsSchema = z.object({
	/** Device Owner (kiosk provisioning): remote reboot, kiosk lock, silent updates. */
	deviceOwner: z.boolean(),
	/** The kiosk lock (users can't leave the player). Needs Device Owner. */
	kioskLock: z.boolean(),
	/** The player can come back by itself after a reboot or crash (Android ≤ 9, Device Owner, or "Display over other apps"). */
	autoStart: z.boolean(),
	/** Exempt from battery optimisation: Android won't put the player to sleep or stop it. */
	batteryUnrestricted: z.boolean(),
	/** May show notifications (the "Tap to resume" fallback; Android 13+ asks). */
	notifications: z.boolean(),
	/** May install its own updates ("Install unknown apps" for Displaro on Android 8+, or Device Owner). Optional: added later. */
	installUpdates: z.boolean().optional(),
	/** Root access granted to the player (rooted boxes): silent updates, reboot and auto-start without Device Owner. */
	root: z.boolean().optional(),
	/** Live view can show the whole screen (root, or Android's screen-capture permission while it lasts), not just Displaro. */
	fullScreenCapture: z.boolean().optional(),
	/** Live view taps and swipes reach the whole screen (root, or Displaro's accessibility switch on Android 7+), not just Displaro. */
	remoteTouch: z.boolean().optional(),
	/** May read its location (Wi-Fi/GPS): sets the player's location automatically for regions and weather/prayer apps. */
	location: z.boolean().optional()
});
/** Phase 17.7: the device's own position (Android location service; no Google Play needed). */
const DeviceLocationSchema = z.object({
	latitude: z.number().min(-90).max(90),
	longitude: z.number().min(-180).max(180),
	accuracyMeters: z.number().nonnegative(),
	/** When the device fixed this position. */
	fixedAt: IsoDateTimeSchema
});
/**
* A content file this player can't download, or an item it can't play. `unreachable`: the file's address can't be
* reached (DNS, connection); `not_found`: the server says it doesn't exist; `http_error`: another server error;
* `corrupt`: the bytes don't match what was published; `playback_failed`: downloaded but won't decode or play.
*/
const ContentProblemReasonSchema = z.enum([
	"unreachable",
	"not_found",
	"http_error",
	"corrupt",
	"playback_failed"
]);
const ContentProblemSchema = z.object({
	assetId: IdSchema,
	reason: ContentProblemReasonSchema,
	/** The technical detail (for support), e.g. "HTTP 404" or the decoder's error. */
	detail: z.string().max(200),
	/** Failed attempts so far. */
	attempts: NonNegativeIntSchema,
	since: IsoDateTimeSchema
});
/**
* The player builds (spec §11): Android, the desktop app on each OS, the browser player (`web`, updated by reloading
* the page, so never offered a package), the Samsung (Tizen) and LG (webOS) signage apps, and BrightSign players.
*/
const PLAYER_PLATFORMS = [
	"android",
	"windows",
	"macos",
	"linux",
	"web",
	"tizen",
	"webos",
	"brightsign"
];
const PlayerPlatformSchema = z.enum(PLAYER_PLATFORMS);
const HeartbeatPayloadSchema = z.object({
	schemaVersion: SchemaVersionSchema,
	/** Device-generated idempotency key. Replayed batches are acked but not double-counted. */
	heartbeatId: IdSchema,
	screenId: IdSchema,
	/** Device clock at capture time. May be skewed — compare with `HeartbeatAck.serverTime`. */
	capturedAt: IsoDateTimeSchema,
	uptimeMs: NonNegativeIntSchema,
	app: z.object({
		versionName: z.string().min(1).max(64),
		versionCode: PositiveIntSchema,
		/** `modern` = minSdk 26 build, `legacy` = future minSdk 21 flavor. */
		buildFlavor: z.enum(["modern", "legacy"]),
		/** Which player build this is, so app updates offer the right package. Absent → `android` (older builds). */
		platform: PlayerPlatformSchema.optional()
	}),
	device: z.object({
		manufacturer: z.string().min(1).max(128),
		model: z.string().min(1).max(128),
		apiLevel: z.int().min(21).max(99),
		/** Phase 7 (optional): `Build.VERSION.RELEASE`, e.g. "11". */
		osVersion: z.string().min(1).max(32).optional()
	}),
	playback: PlaybackStatusSchema,
	sync: SyncStatusSchema,
	storage: StorageStatusSchema,
	memory: MemoryStatusSchema,
	network: NetworkStatusSchema,
	capabilities: DeviceCapabilitiesSchema,
	/**
	* Hardware MAC of the active interface, `aa:bb:cc:dd:ee:ff`. null when the OS hides it (Android 10+
	* returns the dummy 02:00:00:00:00:00 to non-device-owner apps — send null, never the dummy).
	*/
	macAddress: MacAddressSchema.nullable().optional(),
	/** LAN address of the active interface (the public IP is taken from the connection, server-side). */
	localIp: IpAddressSchema.nullable().optional(),
	/** Physical display mode, `"{width}x{height}"`, e.g. "3840x2160". */
	screenResolution: ScreenResolutionSchema.nullable().optional(),
	/** 0–100, or null when the device has no battery (most signage boxes). */
	batteryPercent: z.int().min(0).max(100).nullable().optional(),
	isCharging: z.boolean().nullable().optional(),
	/** Last time ALL assets of the playing manifest were downloaded and verified. */
	lastContentSyncAt: IsoDateTimeSchema.nullable().optional(),
	/** Phase 17.7 (optional: older players don't report it). */
	permissions: DevicePermissionsSchema.optional(),
	/** Phase 17.7: the device's position, when location is allowed and known. */
	location: DeviceLocationSchema.optional(),
	/** Broken content: files given up on (the rest of the content plays without them) and items that won't play. */
	contentProblems: z.array(ContentProblemSchema).max(50).optional()
});
/**
* The OS as people read it. Android reports its release ("11"), so it gets the name; the desktop and browser players
* report the OS by name already ("Ubuntu 24.04.4 LTS", "Windows 11", "Chrome OS 16093.68.0"). No platform = Android.
*/
function playerOsLabel(h) {
	if (!((h.app.platform ?? "android") === "android")) return h.device.osVersion ?? h.app.platform ?? "Unknown";
	return h.device.osVersion ? `Android ${h.device.osVersion}` : `Android API ${h.device.apiLevel}`;
}
/** Request body for `POST /api/v1/player/heartbeats`. */
const HeartbeatBatchSchema = z.object({
	screenId: IdSchema,
	heartbeats: z.array(HeartbeatPayloadSchema).min(1).max(200)
}).superRefine((b, ctx) => {
	b.heartbeats.forEach((h, i) => {
		if (h.screenId !== b.screenId) ctx.addIssue({
			code: "custom",
			message: "heartbeat screenId does not match batch screenId",
			path: [
				"heartbeats",
				i,
				"screenId"
			]
		});
	});
});
/** Response to a heartbeat batch. The heartbeat doubles as the lightweight sync check. */
/**
* Phase 17.7: a newer player app is available. The player downloads it, checks `sha256`, and installs it
* (silently as Device Owner; otherwise it shows "Update ready — tap to install" on the screen).
*/
const PlayerUpdateSchema = z.object({
	versionCode: PositiveIntSchema,
	versionName: z.string().min(1).max(64),
	url: HttpUrlSchema,
	sha256: Sha256HexSchema,
	sizeBytes: PositiveIntSchema
});
const HeartbeatAckSchema = z.object({
	/** For device clock-skew detection (on-device scheduling depends on a sane clock). */
	serverTime: IsoDateTimeSchema,
	/** Player deletes exactly these rows from its Room buffer. */
	acceptedHeartbeatIds: z.array(IdSchema),
	/** Current manifest for this screen; player downloads only if `sha256` differs from what it runs. */
	manifest: ManifestPointerSchema.nullable(),
	/** Phase 17.7: present only when a newer player app than the one reporting is available. */
	update: PlayerUpdateSchema.nullable().optional()
});
/**
* Phase 7: durable device snapshot (columns on `screens`), bulk-copied from the newest heartbeat every
* 30 s. Survives Redis expiry, unlike `lastHeartbeat`. Every field is null until reported.
*/
const ScreenSnapshotSchema = z.object({
	macAddress: z.string().nullable(),
	localIp: z.string().nullable(),
	/** Source address of the most recent heartbeat as seen by the API. */
	publicIp: z.string().nullable(),
	appVersion: z.string().nullable(),
	osVersion: z.string().nullable(),
	deviceModel: z.string().nullable(),
	resolution: z.string().nullable(),
	batteryPercent: z.int().min(0).max(100).nullable(),
	isCharging: z.boolean().nullable(),
	lastSyncAt: IsoDateTimeSchema.nullable(),
	/** When this snapshot was last written. */
	updatedAt: IsoDateTimeSchema.nullable(),
	/** Phase 17.7: what the player may do on this device; null until a player that reports it checks in. */
	permissions: DevicePermissionsSchema.nullable().default(null)
});
/** CMS read model: latest known state of a screen. */
const ScreenStatusSchema = z.object({
	screenId: IdSchema,
	/** Server receive time of the most recent heartbeat (authoritative, not device clock). */
	lastSeenAt: IsoDateTimeSchema.nullable(),
	lastHeartbeat: HeartbeatPayloadSchema.nullable(),
	/** Phase 7 (additive). */
	snapshot: ScreenSnapshotSchema.nullable(),
	/** Phase 17.12: when the server last received this player's play records (proof of play), within the last 7 days. */
	lastPlaysReceivedAt: IsoDateTimeSchema.nullable().optional()
});
/**
* Why a downloaded item won't play, in plain categories, from the player's own words (ContentProblem.detail, e.g.
* "video format not supported: PIPELINE_ERROR_INITIALIZATION_FAILED", "took too long to load",
* ExoPlayer "ERROR_CODE_DECODER_INIT_FAILED"). Used by Health Check and the CMS so both say the same thing.
*/
const PLAYBACK_CAUSES = [
	"decoder",
	"slow_start",
	"format",
	"refused",
	"other"
];
function playbackCause(detail) {
	const d = (detail ?? "").toLowerCase();
	if (/pipeline_error_initialization|decoder_init|decoder init|exceeds_capabilities|no decoder|decode error|decoding_failed|decoder/.test(d)) return "decoder";
	if (/too long|timed out|timeout|stalled/.test(d)) return "slow_start";
	if (/not supported|unsupported|format|codec|src_not_supported|parsing/.test(d)) return "format";
	if (/refused|notallowed|not allowed/.test(d)) return "refused";
	return "other";
}
//#endregion
//#region src/api-routes.ts
/**
* Single source of truth for HTTP paths so NestJS decorators and CMS/Android
* clients can never drift apart.
*/
const API_GLOBAL_PREFIX = "api/v1";
const ApiRoutes = {
	/** POST  body: HeartbeatBatch → HeartbeatAck */
	playerHeartbeats: "player/heartbeats",
	/** GET   → ManifestPointer */
	screenManifestPointer: "screens/:screenId/manifest",
	/** GET   → ScreenStatus */
	screenStatus: "screens/:screenId/status",
	/** GET   → ScreenManifest (immutable document; long-lived cache) */
	manifestDocument: "manifests/:screenId/:fileName",
	/** POST  SignupRequest → AuthSession (starts a 14-day TRIAL org) */
	authSignup: "auth/signup",
	/** POST  LoginRequest → AuthSession (sets HttpOnly cookies) */
	/** GET → AdminMe (403 for non-staff) */
	adminMe: "admin/me",
	/** POST → TotpSetup (first time only) · POST TotpCode → 204 (turns it on and verifies this session) */
	adminTotpSetup: "admin/totp/setup",
	adminTotpEnable: "admin/totp/enable",
	/** POST TotpCode → AdminMe (opens the admin area for this session) */
	adminTotpVerify: "admin/totp/verify",
	/** GET → AdminOverview */
	adminOverview: "admin/overview",
	/** GET AdminOrgListQuery → AdminOrgPage */
	adminOrgs: "admin/organizations",
	/** GET → AdminOrgDetail · PATCH AdminUpdateOrg → AdminOrgDetail */
	adminOrg: "admin/organizations/:organizationId",
	/** POST AdminReasonBody → AdminOrgDetail */
	adminOrgSuspend: "admin/organizations/:organizationId/suspend",
	adminOrgReactivate: "admin/organizations/:organizationId/reactivate",
	adminOrgDelete: "admin/organizations/:organizationId/delete",
	adminOrgRestore: "admin/organizations/:organizationId/restore",
	/** POST AdminReasonBody → 204: this session works inside the organization for SUPPORT_VISIT_MINUTES ("view as customer") */
	adminOrgSupport: "admin/organizations/:organizationId/support",
	/** POST → 204: end the visit, back to your own organization */
	adminSupportEnd: "admin/support/end",
	/** GET AdminUserListQuery → AdminUserSummary[] */
	adminUsers: "admin/users",
	/** POST AdminReasonBody → AdminResetLink */
	adminUserResetLink: "admin/users/:userId/reset-link",
	/** POST AdminReasonBody → AdminUserSummary */
	adminUserSignOut: "admin/users/:userId/sign-out",
	adminUserLock: "admin/users/:userId/lock",
	adminUserUnlock: "admin/users/:userId/unlock",
	/** GET AdminActionQuery → AdminActionPage */
	adminActions: "admin/actions",
	authLogin: "auth/login",
	/** POST TwoFactorLogin → AuthSession (second step after a TwoFactorChallenge) */
	authLogin2fa: "auth/login/2fa",
	/** GET → TwoFactorStatus · POST setup → TotpSetup · POST enable TotpCode → RecoveryCodes · POST disable TwoFactorDisable → 204 */
	me2fa: "me/2fa",
	me2faSetup: "me/2fa/setup",
	me2faEnable: "me/2fa/enable",
	me2faDisable: "me/2fa/disable",
	/** POST TotpCode → RecoveryCodes (new set; the old ones stop working) */
	me2faRecoveryCodes: "me/2fa/recovery-codes",
	/** GET/PUT OrganizationSecurity (OWNER/ADMIN to change) */
	organizationSecurity: "organizations/current/security",
	/** Platform admin: POST AdminReasonBody → AdminUserSummary (lost phone: two-factor off, set it up again) */
	adminUserReset2fa: "admin/users/:userId/reset-2fa",
	/** POST  PasswordResetRequest → 202 (always: never reveals whether the email has an account) */
	authPasswordReset: "auth/password-reset",
	/** POST  PasswordResetConfirm → 204 (sets the password, signs out every session) */
	authPasswordResetConfirm: "auth/password-reset/confirm",
	/** GET   → PasswordResetPreview (public, token-gated; 410 when used or expired) */
	authPasswordResetToken: "auth/password-reset/:token",
	/** POST  → 204 (revokes the session, clears cookies) */
	authLogout: "auth/logout",
	/** POST  → AuthSession (rotates the refresh token) */
	authRefresh: "auth/refresh",
	/** GET   → AuthSession */
	authMe: "auth/me",
	/** POST  SwitchOrganizationRequest → AuthSession */
	authSwitchOrganization: "auth/switch-organization",
	/** GET   → InvitationPreview (public, token-gated) */
	invitationPreview: "auth/invitations/:token",
	/** POST  AcceptInvitationRequest → AuthSession */
	invitationAccept: "auth/invitations/accept",
	/** GET   → OrganizationSettings (active org) */
	organizationCurrent: "organizations/current",
	/** GET   → Member[] */
	organizationMembers: "organizations/current/members",
	/** PATCH UpdateMemberRoleRequest → Member · DELETE → 204 */
	organizationMember: "organizations/current/members/:userId",
	/** PUT MemberAccess → Member (ADMIN): limit a member to certain player groups */
	organizationMemberAccess: "organizations/current/members/:userId/access",
	/** GET   → Invitation[] · POST CreateInvitationRequest → InvitationCreated */
	organizationInvitations: "organizations/current/invitations",
	/** DELETE → 204 */
	organizationInvitation: "organizations/current/invitations/:invitationId",
	/** GET ScreenListQuery (?ids, ?located, ?limit) → ScreenSummary[] · POST CreateScreenRequest → ScreenSummary (QuotaGuard) */
	screens: "screens",
	/** GET PlayersQuery → PlayersPage: the Players page, paged, with search/filters/sort done by the server */
	playersPage: "players-page",
	/** POST PlayersReachRequest → PlayersReach (how many players a publish to these targets reaches) */
	playersReach: "players-reach",
	/** GET → GroupsHealth (per group: players online / offline / not connected) */
	groupsHealth: "groups-health",
	/** POST  DevicePairingInitiateRequest → DevicePairingInitiateResponse (device, unauthenticated) */
	devicePairingInitiate: "devices/pairing",
	/** POST  DevicePairingExchangeRequest → DevicePairingExchangeResponse (device, nonce-gated) */
	devicePairingExchange: "devices/pairing/exchange",
	/** GET   → DevicePairingPreview (CMS, ADMIN+) */
	devicePairingPreview: "devices/pairing/:pairingCode",
	/** POST  DevicePairingClaim → ScreenSummary (CMS, ADMIN+, QuotaGuard) */
	devicePairingClaim: "devices/pairing/claim",
	/** POST  DeviceRepairRequest → ScreenSummary (re-pair, no seat) · DELETE → 204 (unpair) */
	screenDevice: "screens/:screenId/device",
	/** DELETE → 204 (removes the screen, frees its seat) · PATCH UpdateScreenRequest → ScreenSummary (Phase 5, ADMIN+) */
	screen: "screens/:screenId",
	/** GET   → ManifestPointer for the AUTHENTICATED DEVICE's own screen */
	deviceManifestPointer: "device/manifest",
	/** GET → ScreenGroup[] · POST UpsertScreenGroupRequest → ScreenGroup (ADMIN+) */
	groups: "groups",
	/** GET → ScreenGroup · PATCH UpsertScreenGroupRequest → ScreenGroup · DELETE → 204 (screens become ungrouped) */
	group: "groups/:groupId",
	/** POST → CompileResult | 422 CompileFailure (atomic: all screens or none) (EDITOR+) */
	/** POST → CompileResult | 422 CompileFailure (EDITOR+) */
	screenPublish: "screens/:screenId/publish",
	/** GET → Layout[] · POST UpsertLayoutRequest → Layout (EDITOR+) */
	layouts: "layouts",
	/** GET → Layout · PUT UpsertLayoutRequest → Layout (recompiles affected screens) · DELETE → 204 | 409 LAYOUT_IN_USE */
	layout: "layouts/:layoutId",
	/** Phase 16: GET → LayoutTemplate[] · POST UpsertLayoutTemplateRequest → LayoutTemplate (EDITOR+) */
	layoutTemplates: "layout-templates",
	/** Phase 16: PUT UpsertLayoutTemplateRequest → LayoutTemplate · DELETE → 204 (campaigns keep their copied sections) */
	layoutTemplate: "layout-templates/:templateId",
	/** GET → Playlist[] · POST UpsertPlaylistRequest → Playlist (EDITOR+) */
	playlists: "playlists",
	/** GET → Playlist · PUT UpsertPlaylistRequest → Playlist (recompiles players) · DELETE → 204 | 409 PLAYLIST_IN_USE */
	playlist: "playlists/:playlistId",
	/** POST PublishRequest → PublishResult (EDITOR+). Appends to every target player's list (groups/tags fan out). */
	publish: "publish",
	/** GET → PlayerContent · PATCH { loop } → PlayerContent (EDITOR+) */
	playerContent: "screens/:screenId/content",
	/** PUT ReorderRequest → PlayerContent (EDITOR+) */
	playerContentOrder: "screens/:screenId/content/order",
	/** GET → GroupContent */
	groupContent: "groups/:groupId/content",
	/** PUT ReorderRequest → GroupContent (EDITOR+) */
	groupContentOrder: "groups/:groupId/content/order",
	/** PATCH UpdatePlacementRequest → Placement · DELETE → 204 (unpublish; a group placement leaves every member) */
	placement: "placements/:placementId",
	/** POST BulkPlacementRequest → 204 */
	placementsBulk: "placements/bulk",
	/** DELETE → 204: unpublish a tag publication from every player it reached (EDITOR+) */
	tagPublication: "tag-publications/:publicationId",
	/** DELETE → 204: restore a group/tag item a player had removed (EDITOR+) */
	placementExclusion: "placement-exclusions/:exclusionId",
	/** PUT UpdateTagsRequest → ScreenSummary (ADMIN+, hardware) */
	screenTags: "screens/:screenId/tags",
	/** PUT UpdateTagsRequest → ScreenGroup (ADMIN+) */
	groupTags: "groups/:groupId/tags",
	groupSync: "groups/:groupId/sync",
	/** GET → string[] (every tag used in the organization; Publish → Tags quick picks) */
	tags: "tags",
	/** Phase 17.8 Tags page. GET → TagSummary[] */
	tagOverview: "tags/overview",
	/** GET ?tag= → TagDetail */
	tagDetail: "tags/detail",
	/** POST TagMembersRequest → TagDetail (ADMIN+, like player tags) */
	tagMembers: "tags/members",
	/** POST RenameTagRequest → TagSummary (ADMIN+; onto an existing tag = merge) */
	tagRename: "tags/rename",
	/** GET ?tag= → TagDeleteImpact · DELETE ?tag= → 204 (ADMIN+) */
	tagDelete: "tags/delete",
	/** GET → BillingOverview */
	billing: "billing",
	/** POST PlanRequest → BillingQuote */
	billingQuote: "billing/quote",
	/** POST CheckoutRequest → CheckoutResult (gateway page, or applied when nothing is due) */
	billingCheckout: "billing/checkout",
	/** POST { cancel: boolean } → BillingOverview (cancel at period end / keep) */
	billingCancel: "billing/cancel",
	/** POST → CheckoutResult: pay the failed renewal now (new card) */
	billingPayNow: "billing/pay-now",
	/** PUT BillingContact → BillingOverview */
	billingContact: "billing/contact",
	/** GET → Invoice (status after returning from the gateway; verifies with the gateway while open) */
	billingInvoice: "billing/invoices/:invoiceId",
	/** POST (gateway → us, public, signature-checked) */
	billingWebhook: "billing/webhook/tap",
	/** POST { invoiceId, outcome } (test gateway only) */
	billingTestPay: "billing/test-pay",
	/** GET ?kind=media|campaign|playlist&id= → ContentUsage */
	contentUsage: "content-usage",
	/** POST ContentReplaceRequest → ContentReplaceResult (EDITOR): put another item in a media item's place everywhere. */
	contentReplace: "content-replace",
	/** POST ContentDetachRequest → ContentDetachResult (EDITOR): take a media item out of everything; it stays in the library. */
	contentDetach: "content-detach",
	/** GET ?kind=media|campaign|playlist&id= → ContentDeleteImpact (what a delete would touch and leave empty) */
	contentDeleteImpact: "content-delete-impact",
	/** PUT ContentRestrictionRequest → 204 (EDITOR+; recompiles every player the content plays on) */
	contentRestriction: "content-restriction",
	/** PUT ContentMuteRequest → 204 (EDITOR+; recompiles every player the content plays on) */
	contentMute: "content-mute",
	/** GET → PublishedCounts (content id → players) */
	publishedCounts: "published-counts",
	/** GET LibraryQuery → LibraryPage: the Content page, paged, with search/filters/sort done by the server */
	contentLibrary: "content-library",
	/** GET ?kind ?ids → MediaAssetRecord[] · POST CreateMediaAssetRequest → MediaAssetRecord (EDITOR+, storage quota) */
	mediaAssets: "media-assets",
	/** GET → MediaAssetRecord · PATCH UpdateMediaAssetRequest → MediaAssetRecord (recompiles users) · DELETE → 204 | 409 ASSET_IN_USE */
	mediaAsset: "media-assets/:assetId",
	/** GET → Report[] · POST UpsertReportRequest → Report (EDITOR+; audit_log: ADMIN; a one-time report is queued at once) */
	reports: "reports",
	/** GET ?kind&id&from&to&tz → ContentPlayback (one item's plays, for its information panel) */
	contentPlayback: "content-playback",
	/** GET → Report · PUT UpsertReportRequest → Report · DELETE → 204 */
	report: "reports/:reportId",
	/** POST → ReportRun (EDITOR+; queues a run now) */
	reportRun: "reports/:reportId/run",
	/** GET → ReportRun[] (newest first) */
	reportRuns: "reports/:reportId/runs",
	/** GET → the file (streamed; tenant-checked) */
	reportRunDownload: "report-runs/:runId/download",
	/** POST UpsertReportRequest → ReportPreview (the first rows, for the wizard) */
	reportPreview: "reports/preview",
	/** GET → DataFeedSummary[] (VIEWER+) · POST UpsertDataFeedRequest → DataFeed (EDITOR+; a source feed is read at once) */
	dataFeeds: "data-feeds",
	/** POST DataFeedPreviewRequest → DataFeedPreview (EDITOR+; nothing saved) */
	dataFeedPreview: "data-feeds/preview",
	/** GET → DataFeed · PUT UpsertDataFeedRequest → DataFeed · DELETE → 204 (EDITOR+) */
	dataFeed: "data-feeds/:feedId",
	/** POST → DataFeed (EDITOR+; reads the source now) */
	dataFeedSync: "data-feeds/:feedId/sync",
	/** POST → DataFeed (EDITOR+; the pending update becomes the approved table) */
	dataFeedApprove: "data-feeds/:feedId/approve",
	/** POST → DataFeed (EDITOR+; the pending update is dropped; the same data won't be offered again) */
	dataFeedReject: "data-feeds/:feedId/reject",
	/** PATCH UpdateProfileRequest → Profile */
	meProfile: "me/profile",
	/** PUT multipart (file) → Profile · DELETE → Profile (Phase 17.6) */
	meAvatar: "me/avatar",
	/** POST ChangePasswordRequest → 204 (signs out every other session) */
	mePassword: "me/password",
	/** GET → AccountSession[] (newest use first) */
	meSessions: "me/sessions",
	/** DELETE → 204 (sign out that session; not the current one) */
	meSession: "me/sessions/:sessionId",
	/** POST → { revoked } (sign out everywhere else) */
	meSessionsRevokeOthers: "me/sessions/revoke-others",
	/** POST multipart (capturedAt + image) → 201 Screenshot (device token; 429 when sooner than 60 s after the last) */
	playerScreenshots: "player/screenshots",
	/** GET → MonitoringTile[] (VIEWER+) */
	monitoring: "monitoring",
	/** GET MonitoringQuery → MonitoringPage: the Monitoring wall, paged (search and group filter on the server) */
	monitoringPage: "monitoring-page",
	/** GET ?before=&limit= → ScreenshotPage, newest first (VIEWER+) */
	screenScreenshots: "screens/:screenId/screenshots",
	/** GET → CustomAppSummary[] (VIEWER+) · POST multipart (CustomAppUploadFields + zip) → CustomApp (EDITOR+) */
	/** Custom fonts (brand fonts for apps). */
	/** GET → CompositionTemplate[] (built-in + the organization's) · POST UpsertCompositionTemplateRequest → CompositionTemplate (EDITOR+) */
	compositionTemplates: "composition-templates",
	/** PUT UpsertCompositionTemplateRequest → CompositionTemplate · DELETE → 204 (EDITOR+) */
	compositionTemplate: "composition-templates/:templateId",
	fonts: "fonts",
	font: "fonts/:fontId",
	customApps: "custom-apps",
	/** GET → CustomApp · PATCH UpdateCustomAppRequest → CustomApp · DELETE → 204 (EDITOR+; refused while content uses it) */
	customApp: "custom-apps/:appId",
	/** POST multipart (changelog + zip) → CustomApp (EDITOR+; the new version becomes current) */
	customAppVersions: "custom-apps/:appId/versions",
	/** POST → CustomApp (EDITOR+; this version becomes current again: rollback) */
	customAppVersionCurrent: "custom-apps/:appId/versions/:versionId/current",
	/** GET → AttributeDefinition[] (VIEWER+) · POST CreateAttributeRequest → AttributeDefinition (ADMIN) */
	attributes: "attributes",
	/** PUT UpdateAttributeRequest → AttributeDefinition · DELETE → 204, its values removed everywhere (ADMIN) */
	attribute: "attributes/:attributeId",
	/** PUT SetAttributeValuesRequest → AttributeValues (ADMIN, like other player properties) */
	playerAttributes: "screens/:screenId/attributes",
	/** PUT SetContentAttributesRequest → AttributeValues (EDITOR+) */
	contentAttributes: "content/attributes",
	/** GET → Alert[] (VIEWER+) · POST UpsertAlertRequest → Alert (ADMIN) */
	alerts: "alerts",
	/** GET → Alert · PUT UpsertAlertRequest → Alert · DELETE → 204 (ADMIN) */
	alert: "alerts/:alertId",
	/** GET ?state=open|all → AlertIncident[] (newest first, 200 max) */
	alertIncidents: "alerts/:alertId/incidents",
	/** POST UpsertAlertRequest → AlertCheckResult (ADMIN; the editor's live check, nothing saved) */
	alertCheck: "alerts/check",
	/** POST UpsertAlertRequest → AlertTestResult (ADMIN; sends a sample email/webhook) */
	alertTest: "alerts/test",
	/** GET → NotificationPrefs · PUT NotificationPrefs → NotificationPrefs (the signed-in member) */
	notificationPrefs: "me/notifications",
	/** GET ?state&kinds&severities&screenId&q&cursor → HealthIssuesPage (VIEWER+) */
	healthIssues: "health/issues",
	/** POST AcknowledgeIssuesRequest → 204 (EDITOR+) */
	healthAcknowledge: "health/issues/acknowledge",
	/** GET → HealthSummary (VIEWER+; sidebar badge) */
	healthSummary: "health/summary",
	/** GET → PlaybackHealth (VIEWER+): playback problems grouped, for the Players page strip. */
	healthPlayback: "health/playback",
	/** POST → HealthSummary (EDITOR+; runs the sweep now if no other instance is running it) */
	healthRecheck: "health/recheck",
	/** GET → HealthSettings (VIEWER+) · PUT HealthSettings → HealthSettings (ADMIN) */
	healthSettings: "health/settings",
	/** GET → HealthNotifications (VIEWER+; the bell) · POST health/notifications/seen → 204 */
	healthNotifications: "health/notifications",
	healthNotificationsSeen: "health/notifications/seen",
	/** GET ActivityQuery → ActivityPage (system: VIEWER+, audit: ADMIN+) */
	activity: "activity",
	/** GET ActivityQuery (no cursor) → text/csv, streamed */
	activityExport: "activity/export.csv",
	/** GET ActivityFacetsQuery → ActivityFacets: event types, people/players and items in the period (same roles as activity) */
	activityFacets: "activity/facets",
	/** GET ?kind&id → ContentActivity (owner + that item's history; any member) */
	contentActivity: "content-activity",
	/** GET ?from&to&tz → AnalyticsOverview (VIEWER+) */
	analyticsOverview: "analytics/overview",
	/** GET → Region[] · POST UpsertRegionRequest → Region (EDITOR+) */
	regions: "regions",
	/** PUT UpsertRegionRequest → Region (recompiles players whose restrictions use it) · DELETE → 204 | 409 REGION_IN_USE */
	region: "regions/:regionId",
	regionDetail: "regions/:regionId/detail",
	regionPlayers: "regions/:regionId/players",
	regionContent: "regions/:regionId/content",
	/** PATCH UpdateOrganizationRequest → OrganizationSettings (ADMIN) */
	organizationProfile: "organizations/current/profile",
	/** GET → PlayerInteractions (own + inherited from its campaigns) · POST UpsertInteractionRequest → Interaction (EDITOR+) */
	screenInteractions: "screens/:screenId/interactions",
	/** GET → IceServer[] for a live view (STUN + TURN with short-lived credentials) */
	/** GET → PresenceSnapshot: which of the organization's players hold a live connection now, and recent drops */
	presence: "live/presence",
	liveIceServers: "live/ice-servers",
	/** GET → Interaction[] · POST UpsertInteractionRequest → Interaction (EDITOR+) */
	layoutInteractions: "layouts/:layoutId/interactions",
	/** PUT UpsertInteractionRequest → Interaction · DELETE → 204 (EDITOR+) */
	interaction: "interactions/:interactionId",
	/** PUT PlayerSettingsOverrides → ScreenSummary (ADMIN; replaces the player's overrides) */
	screenSettings: "screens/:screenId/settings",
	/** PUT PlayerSettingsOverrides → ScreenGroup (ADMIN+): the group's Player Settings; its players are recompiled */
	groupSettings: "groups/:groupId/settings",
	/** PUT PlayerSettings → PlayerSettings (ADMIN; recompiles the organization's players) */
	playerDefaults: "organization/player-defaults",
	/** GET → ContentFolder[] (whole tree) · POST CreateFolderRequest → ContentFolder (EDITOR+, 409 FOLDER_NAME_TAKEN) */
	folders: "folders",
	/** PATCH UpdateFolderRequest → ContentFolder (422 FOLDER_INVALID_MOVE) · DELETE → 204 (contents move up to the parent) */
	folder: "folders/:folderId",
	/** POST MoveContentRequest → 204 (EDITOR+) */
	contentMove: "content/move",
	/** POST UpdateContentTagsRequest → 204 (EDITOR+) */
	contentTags: "content/tags",
	/** GET ?url= → RssPreview (VIEWER+; server-side fetch for the Customize App live preview, https only, cached 5 min) */
	rssPreview: "rss-preview",
	/**
	* GET ?url= → PlayerRssFeed (device token): the feed of one of the organization's RSS apps, fetched by the API for
	* players that can't read feeds cross-origin (web players). https only, cached 5 min; any other URL → 404.
	*/
	playerRssFeed: "player/rss",
	/** A calendar app's next events (the API reads and expands the iCal link). */
	playerCalendar: "player/calendar",
	/** The same, for the CMS live preview. */
	calendarPreview: "calendar-preview",
	/** BI dashboard: can this link be shown (reachable, not a login page, allowed in a frame)? */
	dashboardCheck: "dashboard-check",
	/**
	* GET ?url= → DashboardCheck (device token): may this web page / dashboard app's page be shown inside a frame? Browser
	* players ask, to show a message instead of a blank frame. Only the organization's own apps' links; others → 404.
	*/
	playerFrameCheck: "player/frame-check",
	/** POST multipart (file + name?) → 202 MediaAssetRecord{status: PENDING} (EDITOR+, storage quota, 415/413/402) */
	mediaUpload: "media",
	/** POST ProofOfPlayBatch → TelemetryIngestAck (device token) */
	playerProofOfPlay: "player/proof-of-play",
	/** POST DeviceMetricsBatch → TelemetryIngestAck (device token) */
	playerMetrics: "player/metrics",
	/** POST DeviceLogsBatch → TelemetryIngestAck (device token) */
	playerLogs: "player/logs",
	/** GET ?cursor&limit&from&to → ScreenActivityPage (VIEWER+) */
	screenActivity: "screens/:screenId/activity",
	/** GET ?range=24h|7d → ScreenMetrics (VIEWER+) */
	screenMetrics: "screens/:screenId/metrics",
	/** GET ?cursor&limit&level → ScreenLogsPage (VIEWER+) */
	screenLogs: "screens/:screenId/logs",
	/** GET ?url= → StreamCheck (VIEWER+): does this https stream link answer, and is it HLS / DASH / a video file? */
	streamCheck: "stream-check"
};
/**
* Build a client-side path: `apiPath('screenStatus', { screenId })` → `/api/v1/screens/<id>/status`.
*/
function apiPath(route, params = {}) {
	const path = ApiRoutes[route].replace(/:([A-Za-z]+)/g, (_, key) => {
		const value = params[key];
		if (value === void 0) throw new Error(`Missing path param "${key}" for route "${route}"`);
		return encodeURIComponent(value);
	});
	return `/${API_GLOBAL_PREFIX}/${path}`;
}
//#endregion
//#region src/tenancy.ts
const RoleSchema = z.enum([
	"OWNER",
	"ADMIN",
	"EDITOR",
	"VIEWER"
]);
const ROLE_RANK = {
	OWNER: 40,
	ADMIN: 30,
	EDITOR: 20,
	VIEWER: 10
};
/** `roleAtLeast('ADMIN', 'EDITOR') === true` */
const roleAtLeast = (role, minimum) => ROLE_RANK[role] >= ROLE_RANK[minimum];
/**
* Who may grant/change/revoke a given role. OWNER manages everyone; ADMIN manages ADMIN and below
* but can never create, promote to, demote or remove an OWNER. EDITOR/VIEWER manage nobody.
*/
function canManageRole(actor, target) {
	if (actor === "OWNER") return true;
	if (actor === "ADMIN") return target !== "OWNER";
	return false;
}
const PlanTierSchema = z.enum([
	"TRIAL",
	"STANDARD",
	"ADVANCED"
]);
const GIB = 1024 ** 3;
/**
* Phase 17.10: features that need the Advanced plan (Standard has the core product). The trial includes
* everything so people can try it all. A downgrade keeps the data: locked features stop running and can't be
* changed until the organization upgrades again.
*/
const PlanFeatureSchema = z.enum([
	"liveControl",
	"interactivity",
	"dataFeeds",
	"customApps",
	"logicRestrictions",
	"regions",
	"syncPlayback",
	"monitoringWall",
	"alerts",
	"scheduledReports",
	"auditLog"
]);
const ADVANCED_FEATURES = PlanFeatureSchema.options;
/** Does this plan include the feature? */
function planHasFeature(planTier, feature) {
	return planTier === "TRIAL" || planTier === "ADVANCED" || !ADVANCED_FEATURES.includes(feature);
}
const PLAN_CATALOG = {
	TRIAL: {
		label: "14-day free trial",
		pricePerPlayerCents: 0,
		trialDays: 14,
		defaultSeats: 3,
		storageBytesPerSeat: 2 * GIB,
		features: { userAccessManagement: true }
	},
	STANDARD: {
		label: "Standard",
		pricePerPlayerCents: 999,
		trialDays: null,
		defaultSeats: 1,
		storageBytesPerSeat: 5 * GIB,
		features: { userAccessManagement: true }
	},
	ADVANCED: {
		label: "Advanced",
		pricePerPlayerCents: 1299,
		trialDays: null,
		defaultSeats: 1,
		storageBytesPerSeat: 10 * GIB,
		features: { userAccessManagement: true }
	}
};
/**
* Derived: `trial_expired` and `suspended` block quota-consuming writes (new screens, invites, uploads).
* Phase 17.9: `past_due` = a renewal failed and the grace period is running (everything still works);
* `suspended` = grace is over or the subscription was canceled.
*/
const SubscriptionStatusSchema = z.enum([
	"trialing",
	"trial_expired",
	"active",
	"past_due",
	"suspended"
]);
const BillingStatusSchema = z.enum([
	"active",
	"past_due",
	"suspended",
	"canceled"
]);
function subscriptionStatus(planTier, trialEndsAt, now = /* @__PURE__ */ new Date(), billing = null) {
	if (billing === "past_due") return "past_due";
	if (billing === "suspended" || billing === "canceled") return "suspended";
	if (planTier !== "TRIAL") return "active";
	if (trialEndsAt === null) return "trial_expired";
	return new Date(trialEndsAt).getTime() > now.getTime() ? "trialing" : "trial_expired";
}
/** Stable machine-readable error codes (the `code` field of 4xx bodies). */
const ApiErrorCode = {
	QUOTA_SCREENS_EXCEEDED: "QUOTA_SCREENS_EXCEEDED",
	TRIAL_EXPIRED: "TRIAL_EXPIRED",
	SUBSCRIPTION_SUSPENDED: "SUBSCRIPTION_SUSPENDED",
	PLAN_FEATURE_REQUIRED: "PLAN_FEATURE_REQUIRED",
	NO_ACTIVE_ORGANIZATION: "NO_ACTIVE_ORGANIZATION",
	ORGANIZATION_SUSPENDED: "ORGANIZATION_SUSPENDED",
	ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
	ADMIN_VERIFY_REQUIRED: "ADMIN_VERIFY_REQUIRED",
	TWO_FACTOR_REQUIRED: "TWO_FACTOR_REQUIRED",
	OUTSIDE_YOUR_GROUPS: "OUTSIDE_YOUR_GROUPS",
	INSUFFICIENT_ROLE: "INSUFFICIENT_ROLE",
	LAST_OWNER: "LAST_OWNER",
	RATE_LIMITED: "RATE_LIMITED",
	PAIRING_CODE_INVALID: "PAIRING_CODE_INVALID",
	DEVICE_UNAUTHORIZED: "DEVICE_UNAUTHORIZED",
	DEVICE_SCREEN_MISMATCH: "DEVICE_SCREEN_MISMATCH",
	MANIFEST_COMPILE_FAILED: "MANIFEST_COMPILE_FAILED",
	ASSET_IN_USE: "ASSET_IN_USE",
	LAYOUT_IN_USE: "LAYOUT_IN_USE",
	PLAYLIST_IN_USE: "PLAYLIST_IN_USE",
	QUOTA_STORAGE_EXCEEDED: "QUOTA_STORAGE_EXCEEDED",
	SCHEDULE_CONFLICT: "SCHEDULE_CONFLICT",
	ASSET_NOT_READY: "ASSET_NOT_READY",
	UNSUPPORTED_MEDIA_TYPE: "UNSUPPORTED_MEDIA_TYPE",
	UPLOAD_TOO_LARGE: "UPLOAD_TOO_LARGE"
};
const ApiErrorBodySchema = z.object({
	statusCode: z.int(),
	error: z.string(),
	message: z.string().optional(),
	code: z.string().optional()
});
const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, "Expected a two-letter country code");
/** PATCH semantics: omitted = unchanged. `name` needs ADMIN like the rest. */
const UpdateOrganizationRequestSchema = z.object({
	name: z.string().trim().min(1, "Enter a name").max(120).optional(),
	country: CountryCodeSchema.nullable().optional()
}).refine((b) => b.name !== void 0 || b.country !== void 0, "Nothing to update");
const OrganizationSettingsSchema = z.object({
	id: IdSchema,
	name: z.string().min(1).max(120),
	planTier: PlanTierSchema,
	subscriptionStatus: SubscriptionStatusSchema,
	trialEndsAt: IsoDateTimeSchema.nullable(),
	purchasedSeats: NonNegativeIntSchema,
	maxStorageBytes: NonNegativeIntSchema,
	usage: z.object({
		screens: NonNegativeIntSchema,
		members: PositiveIntSchema,
		pendingInvitations: NonNegativeIntSchema,
		/** Media library arrives in a later phase; always 0 until then. */
		storageBytes: NonNegativeIntSchema
	}),
	/** purchasedSeats × plan price (0 during trial). */
	estimatedMonthlyCents: NonNegativeIntSchema,
	/** Phase 16 Step 8: Player Settings every player inherits unless it overrides a field. */
	playerDefaults: PlayerSettingsSchema.default(DEFAULT_PLAYER_SETTINGS),
	/** Phase 16 Step 12: ISO 3166-1 alpha-2; map default and place-search bias (null = not set). */
	country: CountryCodeSchema.nullable().default(null)
});
const EmailSchema = z.string().trim().toLowerCase().max(254).pipe(z.email());
const MemberSchema = z.object({
	userId: IdSchema,
	email: z.string(),
	name: z.string(),
	role: RoleSchema,
	joinedAt: IsoDateTimeSchema,
	/** Phase 17.6. */
	avatarUrl: z.string().nullable().default(null),
	/** Access limited by group: only these player groups' players (owners and admins are never limited). */
	access: z.object({
		restricted: z.boolean(),
		groupIds: z.array(IdSchema)
	}).default({
		restricted: false,
		groupIds: []
	})
});
/** Limit a member to certain player groups (or give back access to everything). */
const MemberAccessSchema = z.object({
	restricted: z.boolean(),
	groupIds: z.array(IdSchema).max(500)
});
const UpdateMemberRoleRequestSchema = z.object({ role: RoleSchema });
const InvitationSchema = z.object({
	id: IdSchema,
	email: z.string(),
	role: RoleSchema,
	invitedByName: z.string(),
	createdAt: IsoDateTimeSchema,
	expiresAt: IsoDateTimeSchema
});
const CreateInvitationRequestSchema = z.object({
	email: EmailSchema,
	role: RoleSchema
});
const InvitationCreatedSchema = z.object({
	invitation: InvitationSchema,
	/** One-time link. Returned to the inviter until transactional email is wired up. */
	acceptUrl: z.string()
});
//#endregion
//#region src/auth.ts
/** NIST 800-63B: length over composition rules. Upper bound caps hashing cost (DoS). */
const PasswordSchema = z.string().min(12, "Use at least 12 characters").max(128);
const LoginRequestSchema = z.object({
	email: EmailSchema,
	password: z.string().min(1).max(128)
});
/** Self-serve signup starts a 14-day TRIAL organization owned by the new user. */
const SignupRequestSchema = z.object({
	name: z.string().trim().min(1).max(120),
	email: EmailSchema,
	password: PasswordSchema,
	organizationName: z.string().trim().min(1).max(120)
});
/**
* Forgot password: ask for a link (the reply never says whether the email has an account), open it, set a new
* password. Links are single-use, valid PASSWORD_RESET_TTL_MIN minutes; a reset signs out every session.
*/
const PASSWORD_RESET_TTL_MIN = 60;
const PasswordResetRequestSchema = z.object({ email: EmailSchema });
const PasswordResetPreviewSchema = z.object({
	email: z.string(),
	expiresAt: IsoDateTimeSchema
});
const PasswordResetConfirmSchema = z.object({
	token: z.string().min(20).max(200),
	password: PasswordSchema
});
/**
* Two-factor sign-in (authenticator app). A correct password for a person with it on answers with a short-lived
* challenge instead of a session; the code (or a one-time recovery code) then finishes signing in.
*/
const TwoFactorChallengeSchema = z.object({
	twoFactorRequired: z.literal(true),
	challenge: z.string()
});
const TwoFactorLoginSchema = z.object({
	challenge: z.string().min(20).max(200),
	/** The 6-digit code, or a recovery code (xxxx-xxxx-xxxx). */
	code: z.string().trim().min(6).max(20)
});
const TwoFactorStatusSchema = z.object({
	enabled: z.boolean(),
	recoveryCodesLeft: z.number().int(),
	requiredBy: z.array(z.string())
});
const RecoveryCodesSchema = z.object({ recoveryCodes: z.array(z.string()) });
const TwoFactorDisableSchema = z.object({
	password: z.string().min(1).max(128),
	code: z.string().trim().min(6).max(20)
});
const OrganizationSecuritySchema = z.object({ require2fa: z.boolean() });
const SwitchOrganizationRequestSchema = z.object({ organizationId: IdSchema });
const OrganizationMembershipSchema = z.object({
	id: IdSchema,
	name: z.string(),
	role: RoleSchema
});
/**
* Returned by login / signup / refresh / switch-org / me. Tokens themselves are NEVER in the body —
* they travel only as HttpOnly cookies.
*/
const AuthSessionSchema = z.object({
	user: z.object({
		id: IdSchema,
		email: z.string(),
		name: z.string(),
		/** Phase 17.6/17.7: the person's display preferences (null zone = the browser's). */
		timeZone: z.string().nullable().default(null),
		language: z.enum(["en", "ar"]).default("en"),
		numerals: z.enum(["arab", "latn"]).default("arab"),
		avatarUrl: z.string().nullable().default(null),
		/** Two-factor sign-in is on for this person. */
		twoFactorEnabled: z.boolean().default(false),
		/** Displaro staff: may open /admin (null for customers). */
		platformRole: z.enum([
			"SUPER_ADMIN",
			"SUPPORT",
			"BILLING",
			"READ_ONLY"
		]).nullable().default(null)
	}),
	activeOrganization: z.object({
		id: IdSchema,
		name: z.string(),
		role: RoleSchema,
		planTier: PlanTierSchema,
		subscriptionStatus: SubscriptionStatusSchema,
		/** Suspended by Displaro: the CMS is locked (players keep playing what they have). */
		suspended: z.boolean().default(false),
		/** Everyone must use two-factor sign-in (until this person turns it on, only their account settings open). */
		require2fa: z.boolean().default(false),
		/** Access limited by group: this member works only with these player groups (null = everything). */
		groupIds: z.array(IdSchema).nullable().default(null),
		/** Displaro staff working here through "view as customer": until when and why (the CMS shows a banner). */
		support: z.object({
			until: IsoDateTimeSchema,
			reason: z.string()
		}).nullable().default(null)
	}).nullable(),
	organizations: z.array(OrganizationMembershipSchema),
	/** When the access cookie expires; the client refreshes shortly before. */
	accessTokenExpiresAt: IsoDateTimeSchema
});
const InvitationPreviewSchema = z.object({
	organizationName: z.string(),
	email: z.string(),
	role: RoleSchema,
	invitedByName: z.string(),
	expiresAt: IsoDateTimeSchema,
	/** true → "log in to accept"; false → "create your account". */
	accountExists: z.boolean()
});
/**
* New users send name + password. Existing users must be logged in as the invited email and send
* only the token.
*/
const AcceptInvitationRequestSchema = z.object({
	token: z.string().min(32).max(128),
	name: z.string().trim().min(1).max(120).optional(),
	password: PasswordSchema.optional()
});
//#endregion
//#region src/publishing.ts
/** Legacy tag chips ("East Region", "High Traffic" …). Case-insensitive; stored trimmed. */
const TagSchema = z.string().trim().min(1).max(40);
const TagsSchema = z.array(TagSchema).max(20);
/** How media fills its section: Fill = cover (crop), Fit = letterbox (default), Stretch = distort to fill. */
const FitSchema = z.enum([
	"fill",
	"fit",
	"stretch"
]);
/** Calendar date (YYYY-MM-DD), evaluated in the player's time zone. */
const LocalDateSchema = z.iso.date();
const weekday = z.int().min(1).max(7);
/** One condition; `not` negates it ("is not"). */
const RuleConditionSchema = z.discriminatedUnion("var", [
	z.object({
		var: z.literal("days"),
		not: z.boolean().default(false),
		days: z.array(weekday).min(1, "Choose at least one day").max(7)
	}),
	z.object({
		var: z.literal("time"),
		not: z.boolean().default(false),
		from: LocalTimeSchema,
		to: LocalTimeSchema
	}),
	z.object({
		var: z.literal("dates"),
		not: z.boolean().default(false),
		start: LocalDateSchema.nullable(),
		end: LocalDateSchema.nullable()
	}),
	z.object({
		var: z.literal("tags"),
		not: z.boolean().default(false),
		match: z.enum(["all", "any"]),
		tags: TagsSchema.min(1, "Add at least one tag")
	}),
	z.object({
		var: z.literal("groups"),
		not: z.boolean().default(false),
		groupIds: z.array(IdSchema).min(1, "Choose at least one player group").max(50)
	}),
	z.object({
		var: z.literal("players"),
		not: z.boolean().default(false),
		screenIds: z.array(IdSchema).min(1, "Choose at least one player").max(200)
	}),
	z.object({
		var: z.literal("regions"),
		not: z.boolean().default(false),
		regionIds: z.array(IdSchema).min(1, "Choose at least one region").max(50)
	}),
	z.object({
		var: z.literal("orientation"),
		not: z.boolean().default(false),
		values: z.array(z.enum([
			"landscape",
			"portrait",
			"landscape_flipped",
			"portrait_flipped"
		])).min(1, "Choose at least one orientation")
	})
]);
const RuleGroupSchema = z.lazy(() => z.object({
	op: z.enum(["all", "any"]),
	not: z.boolean().default(false),
	items: z.array(z.union([RuleConditionSchema, RuleGroupSchema])).min(1, "A group needs at least one condition").max(20, "At most 20 conditions per group")
}));
const isRuleGroup = (x) => "op" in x;
const ruleDepth = (g) => 1 + Math.max(0, ...g.items.filter(isRuleGroup).map(ruleDepth));
const MAX_RULE_DEPTH = 4;
/**
* Legacy "Add Restriction": Play if / Don't play if days, time, player tags (all), date range.
* Phase 16 Step 10 adds `logic` mode: the rule is `logic` (the visual fields are ignored).
*/
const RestrictionSchema = z.object({
	mode: z.enum([
		"none",
		"playIf",
		"dontPlayIf",
		"logic"
	]),
	/** ISO weekdays 1 = Mon … 7 = Sun; empty = every day. */
	days: z.array(z.int().min(1).max(7)).max(7),
	/** Both null = all day. `to` < `from` crosses midnight. */
	from: LocalTimeSchema.nullable(),
	to: LocalTimeSchema.nullable(),
	/** The player must have ALL of these tags (directly or through a group). */
	tags: TagsSchema,
	/** Step 12: the player must be inside ANY of these regions (empty = anywhere). */
	regions: z.array(IdSchema).max(50).default([]),
	/** null = "Always active". */
	dateRange: z.object({
		start: LocalDateSchema,
		end: LocalDateSchema
	}).nullable(),
	/** Logic mode only. */
	logic: RuleGroupSchema.nullable().default(null)
}).refine((r) => r.mode !== "logic" || r.logic !== null, {
	message: "Add at least one condition",
	path: ["logic"]
}).refine((r) => !r.logic || ruleDepth(r.logic) <= 4, {
	message: `Groups can be nested 4 levels deep at most`,
	path: ["logic"]
}).refine((r) => r.from === null === (r.to === null), {
	message: "Set both From and To, or neither",
	path: ["to"]
}).refine((r) => !r.dateRange || r.dateRange.start <= r.dateRange.end, {
	message: "End date is before start date",
	path: ["dateRange"]
});
const NO_RESTRICTION = {
	mode: "none",
	days: [],
	from: null,
	to: null,
	tags: [],
	regions: [],
	dateRange: null,
	logic: null
};
/** What can be published: any playable media (image, video, app), a campaign or a playlist. */
const ContentRefSchema = z.object({
	kind: z.enum([
		"media",
		"campaign",
		"playlist"
	]),
	id: IdSchema
});
/** Where a placement came from: published straight to the player, through a group, or by tag. */
const PlacementSourceSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("direct") }),
	z.object({
		type: z.literal("group"),
		groupId: IdSchema,
		groupName: z.string()
	}),
	z.object({
		type: z.literal("tag"),
		tag: TagSchema
	})
]);
/** One row of "Content In Player" / "Content in Group". */
const PlacementSchema = z.object({
	id: IdSchema,
	content: ContentRefSchema,
	contentType: z.enum([
		"image",
		"video",
		"pdf",
		"app",
		"campaign",
		"playlist"
	]),
	contentName: z.string(),
	thumbnailUrl: z.string().nullable(),
	/** Pixel size of the media or campaign canvas, when known. */
	width: PositiveIntSchema.nullable(),
	height: PositiveIntSchema.nullable(),
	position: NonNegativeIntSchema,
	/** Display Setting override; null = the item's natural length (video length, campaign length, 10 s default). */
	durationMs: PositiveIntSchema.nullable(),
	/** What actually plays: the override, else the natural length. */
	effectiveDurationMs: PositiveIntSchema,
	/** null = the content's own (`contentTransition`). */
	transition: TransitionSchema.nullable(),
	/** What "Default" plays: a campaign's Enter Transition, a playlist's default; null = none (images, videos, apps). */
	contentTransition: TransitionSchema.nullable().default(null),
	fit: FitSchema,
	/** Playlist placements: `transition`/`fit` replace the playlist's defaults here (items' own settings still win). */
	displayOverride: z.boolean().default(false),
	paused: z.boolean(),
	restriction: RestrictionSchema,
	/** Phase 17.5: restrictions on the content itself (and on a playlist's), applied on every player — the compiler ANDs them with `restriction`. */
	contentRestrictions: z.array(RestrictionSchema).default([]),
	source: PlacementSourceSchema,
	createdAt: IsoDateTimeSchema
});
/**
* Phase 17.2: an item this player removed although it came from a group or a tag. It stays removed when tags or
* group membership change; "Restore" (DELETE placementExclusion) brings it back.
*/
const PlacementExclusionSchema = z.object({
	id: IdSchema,
	content: ContentRefSchema,
	contentName: z.string(),
	source: PlacementSourceSchema,
	createdAt: IsoDateTimeSchema
});
const PlayerContentSchema = z.object({
	/** Legacy "Loop": on = repeat the list; off = play it once and hold the last item. */
	loop: z.boolean(),
	items: z.array(PlacementSchema),
	excluded: z.array(PlacementExclusionSchema).default([])
});
const GroupContentSchema = z.object({ items: z.array(PlacementSchema) });
/** One publish, any mix of targets (legacy had exclusive Players / Groups / Tags tabs). */
const PublishRequestSchema = z.object({
	content: z.array(ContentRefSchema).min(1).max(50),
	players: z.array(IdSchema).max(1e3).default([]),
	groups: z.array(IdSchema).max(200).default([]),
	tags: TagsSchema.default([]),
	/** "Publish on all Players". */
	allPlayers: z.boolean().default(false),
	/** End of each list (legacy default) or start. */
	placement: z.enum(["end", "start"]).default("end"),
	/** Off: skip targets that already have this content directly (legacy had no guard). */
	allowDuplicates: z.boolean().default(false)
}).refine((r) => r.allPlayers || r.players.length + r.groups.length + r.tags.length > 0, {
	message: "Choose at least one player, player group or tag",
	path: ["players"]
});
const PublishResultSchema = z.object({
	/** Distinct players that received at least one new placement. */
	playersReached: NonNegativeIntSchema,
	placementsCreated: NonNegativeIntSchema,
	/** Targets skipped because the content was already there (allowDuplicates = false). */
	skippedDuplicates: NonNegativeIntSchema,
	/**
	* The players this publish changed and the content version each must now play: the CMS follows them until every
	* one is playing it (the sync tray). `unchanged` players are left out. Optional: added later.
	*/
	players: z.array(z.object({
		screenId: IdSchema,
		name: z.string(),
		revision: PositiveIntSchema,
		/** Not published to: it follows this lead in a synchronized group, so it plays the new content too. */
		syncedWith: z.string().optional()
	})).optional(),
	/** Published, but worth knowing per player (e.g. more videos at once than it can play). Optional: added later. */
	warnings: z.array(z.object({
		screenName: z.string(),
		message: z.string(),
		screenId: IdSchema.optional(),
		code: z.string().optional()
	})).optional()
});
/** Display Setting / Restriction / Pause edits on one placement. */
const UpdatePlacementRequestSchema = z.object({
	durationMs: z.int().min(1e3).nullable().optional(),
	/** null = back to the content's own transition. */
	transition: TransitionSchema.nullable().optional(),
	fit: FitSchema.optional(),
	/** Playlist placements: true = `transition`/`fit` replace the playlist's defaults on this player. */
	displayOverride: z.boolean().optional(),
	paused: z.boolean().optional(),
	restriction: RestrictionSchema.optional()
}).refine((b) => Object.keys(b).length > 0, "Nothing to update");
/** Labelled bulk bar: Unpublish · Pause · Resume · Restrictions. */
const BulkPlacementRequestSchema = z.discriminatedUnion("action", [
	z.object({
		action: z.literal("unpublish"),
		ids: z.array(IdSchema).min(1).max(500)
	}),
	z.object({
		action: z.literal("pause"),
		ids: z.array(IdSchema).min(1).max(500)
	}),
	z.object({
		action: z.literal("resume"),
		ids: z.array(IdSchema).min(1).max(500)
	}),
	z.object({
		action: z.literal("restrict"),
		ids: z.array(IdSchema).min(1).max(500),
		restriction: RestrictionSchema
	})
]);
/** Drag-to-reorder: the full new order of the list (saved instantly; the CMS offers Undo). */
const ReorderRequestSchema = z.object({ ids: z.array(IdSchema).min(1).max(2e3) });
const UpdatePlayerContentRequestSchema = z.object({ loop: z.boolean() });
const UpdateTagsRequestSchema = z.object({ tags: TagsSchema });
/** Usage: where an item is published (Content page ⋮ → Usage, "Published on N players"). */
const ContentUsageSchema = z.object({
	/**
	* One row per placement on a player (a player can have it directly and through a group). `placementId` is what
	* Unpublish takes: a player's copy of a group or tag item leaves that player only (and doesn't come back).
	*/
	players: z.array(z.object({
		placementId: IdSchema,
		id: IdSchema,
		name: z.string(),
		via: PlacementSourceSchema
	})),
	/** Group placements: unpublishing one removes it from the group and every member. */
	groups: z.array(z.object({
		placementId: IdSchema,
		id: IdSchema,
		name: z.string(),
		members: NonNegativeIntSchema
	})),
	campaigns: z.array(z.object({
		id: IdSchema,
		name: z.string()
	})),
	/** Playlists this item is inside (Step 4). */
	playlists: z.array(z.object({
		id: IdSchema,
		name: z.string()
	})).default([]),
	/** Phase 17.2: tag publications of this item; unpublishing one removes it from every player it reached. */
	tags: z.array(z.object({
		id: IdSchema,
		tag: TagSchema,
		playersReached: NonNegativeIntSchema
	})).default([])
});
/**
* Deleting a piece of content: everywhere it's used, and which PUBLISHED campaigns and playlists the delete would leave
* with nothing to show — a playlist whose only item it is, a campaign section (or every section) it alone fills. The
* delete dialog offers to unpublish those in the same step (no blank area on a screen, no Health Check warning).
*/
const ContentDeleteImpactSchema = z.object({
	usage: ContentUsageSchema,
	/** Interactions that open it or only apply while it plays (deleted with it). */
	interactions: NonNegativeIntSchema,
	emptied: z.array(z.object({
		kind: z.enum(["campaign", "playlist"]),
		id: IdSchema,
		name: z.string(),
		/** Campaigns: sections it alone fills (a playlist: 0). */
		sections: NonNegativeIntSchema,
		/** Nothing at all left to play (a playlist, or a campaign whose every section it fills). */
		allEmpty: z.boolean(),
		/** Players it is published on. */
		players: NonNegativeIntSchema
	}))
});
/**
* The delete endpoints (media, campaign, playlist) take `?force=true&unpublish=campaign:<id>,playlist:<id>`: everything
* is taken out as by hand, and the chosen emptied containers are unpublished in the same step.
*/
const DeleteUnpublishSchema = z.array(z.object({
	kind: z.enum(["campaign", "playlist"]),
	id: IdSchema
})).max(200);
const unpublishQuery = (list) => list.map((u) => `${u.kind}:${u.id}`).join(",");
function parseUnpublishQuery(q) {
	if (!q) return [];
	return DeleteUnpublishSchema.parse(q.split(",").filter(Boolean).map((x) => {
		const [kind, id] = x.split(":");
		return {
			kind,
			id
		};
	}));
}
/** Phase 17.5: PUT content-restriction — a restriction on the content itself, applied on every player it plays on. */
const ContentRestrictionRequestSchema = z.object({
	content: ContentRefSchema,
	restriction: RestrictionSchema
});
/** The mute switch on a video, campaign or playlist: no sound from it on any player. */
const ContentMuteRequestSchema = z.object({
	content: ContentRefSchema,
	muted: z.boolean()
});
/** Content id → number of players it is published on (card badges). */
const PublishedCountsSchema = z.record(z.string(), NonNegativeIntSchema);
//#endregion
//#region src/attributes.ts
const ATTRIBUTE_TARGETS = ["player", "content"];
const AttributeTargetSchema = z.enum(ATTRIBUTE_TARGETS);
const ATTRIBUTE_TYPES = [
	"text",
	"text_list",
	"number",
	"number_list"
];
const AttributeTypeSchema = z.enum(ATTRIBUTE_TYPES);
const ATTRIBUTE_TYPE_LABEL = {
	text: "Text",
	text_list: "Text list",
	number: "Number",
	number_list: "Number list"
};
const MAX_ATTRIBUTES_PER_TARGET = 50;
const MAX_ATTRIBUTE_TEXT = 200;
const MAX_ATTRIBUTE_LIST = 50;
const editable = {
	name: z.string().trim().min(1, "Enter a name").max(80),
	/** Attributes with the same group are shown together. Blank = "Other". */
	group: z.string().trim().max(60).default(""),
	helpText: z.string().trim().max(300).default(""),
	/** Offered as suggestions while typing (numbers for number types). */
	suggestedValues: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
	/** Players: must be filled when a player is added. Content: must be filled when saving the item's attributes. */
	required: z.boolean().default(false),
	/** Players only: a column in the Players list. */
	showInList: z.boolean().default(false),
	showInReports: z.boolean().default(true)
};
const CreateAttributeRequestSchema = z.object({
	target: AttributeTargetSchema,
	type: AttributeTypeSchema,
	...editable
});
const UpdateAttributeRequestSchema = z.object(editable);
const AttributeDefinitionSchema = CreateAttributeRequestSchema.extend({
	id: IdSchema,
	/** Players / content items with a value. */
	usedBy: z.int().nonnegative(),
	createdAt: IsoDateTimeSchema
});
const AttributeValueSchema = z.union([
	z.string(),
	z.number(),
	z.array(z.string()),
	z.array(z.number())
]);
/** definition id → value. Missing = not set. */
const AttributeValuesSchema = z.record(IdSchema, AttributeValueSchema);
/** PUT body: the full set for the item (null or missing = clear). */
const SetAttributeValuesRequestSchema = z.object({ values: z.record(IdSchema, AttributeValueSchema.nullable()) });
const SetContentAttributesRequestSchema = SetAttributeValuesRequestSchema.extend({ item: ContentRefSchema });
/**
* Cleans raw input against the definitions: trims, parses numbers ("12" → 12), splits list text on commas or new
* lines, drops empties and duplicates. Returns the stored form and a message per definition id that is invalid
* (unknown ids are dropped). `requireRequired` enforces `required`.
*/
function cleanAttributeValues(defs, raw, requireRequired) {
	const values = {};
	const errors = {};
	for (const d of defs) {
		const v = raw[d.id];
		const list = d.type === "text_list" || d.type === "number_list";
		const parts = (Array.isArray(v) ? v : v === null || v === void 0 ? [] : list && typeof v === "string" ? v.split(/[,\n]/) : [v]).map((x) => typeof x === "string" ? x.trim() : x).filter((x) => x !== "" && x !== null && x !== void 0);
		if (!parts.length) {
			if (requireRequired && d.required) errors[d.id] = `${d.name} is required`;
			continue;
		}
		if (!list && parts.length > 1) {
			errors[d.id] = `${d.name} takes one value`;
			continue;
		}
		if (parts.length > 50) {
			errors[d.id] = `${d.name}: at most 50 values`;
			continue;
		}
		if (d.type === "number" || d.type === "number_list") {
			const nums = parts.map((x) => typeof x === "number" ? x : typeof x === "string" && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : NaN);
			if (nums.some((n) => !Number.isFinite(n))) {
				errors[d.id] = `${d.name} must be a number`;
				continue;
			}
			values[d.id] = list ? [...new Set(nums)] : nums[0];
		} else {
			const texts = parts.map(String);
			if (texts.some((t) => t.length > 200)) {
				errors[d.id] = `${d.name}: at most 200 characters`;
				continue;
			}
			values[d.id] = list ? [...new Set(texts)] : texts[0];
		}
	}
	return {
		values,
		errors
	};
}
/** How a value reads in lists, reports and CSV: lists joined with ", ". */
const formatAttributeValue = (v) => v === void 0 || v === null ? "" : Array.isArray(v) ? v.join(", ") : String(v);
//#endregion
//#region src/devices.ts
/** No 0/O/1/I/L — unambiguous on a TV across a room. 31^6 ≈ 887 M combinations. */
const PAIRING_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
const PAIRING_CODE_LENGTH = 6;
const PAIRING_TTL_SEC = 600;
const PAIRING_POLL_INTERVAL_MS = 5e3;
const PairingCodeSchema = z.string().regex(new RegExp(`^[${PAIRING_CODE_ALPHABET}]{6}$`), "Enter the 6-character code shown on the screen");
/** CMS input → canonical code: upper-case, strip spaces/dashes. "k7m-q2x" → "K7MQ2X". */
const normalizePairingCode = (input) => input.toUpperCase().replace(/[\s-]/g, "");
/** Display form: "K7MQ2X" → "K7M-Q2X". */
const formatPairingCode = (code) => code.length === 6 ? `${code.slice(0, 3)}-${code.slice(3)}` : code;
/** 256-bit secret, base64url (43 chars). Proves the exchanger is the device that asked for the code. */
const PairingNonceSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, "Invalid pairing nonce");
const DeviceInfoSchema = z.object({
	manufacturer: z.string().min(1).max(128),
	model: z.string().min(1).max(128),
	apiLevel: z.int().min(21).max(99),
	appVersionName: z.string().min(1).max(64),
	appVersionCode: PositiveIntSchema,
	buildFlavor: z.enum(["modern", "legacy"]),
	videoCodecs: z.array(VideoCodecSchema).min(1)
});
/** Player → `POST devices/pairing` (unauthenticated, rate-limited per IP). */
const DevicePairingInitiateRequestSchema = z.object({
	schemaVersion: SchemaVersionSchema,
	device: DeviceInfoSchema
});
const DevicePairingInitiateResponseSchema = z.object({
	/** Show on screen as formatPairingCode(code). */
	pairingCode: PairingCodeSchema,
	/** Keep in memory only; send to `exchange`. Never display or log it. */
	pairingNonce: PairingNonceSchema,
	expiresAt: IsoDateTimeSchema,
	pollIntervalMs: PositiveIntSchema
});
/** Player → `POST devices/pairing/exchange` (poll until paired or expired → 410). */
const DevicePairingExchangeRequestSchema = z.object({ pairingNonce: PairingNonceSchema });
/** Returned once per exchange. Store `deviceToken` in Keystore-backed encrypted storage. */
const DeviceCredentialsSchema = z.object({
	screenId: IdSchema,
	screenName: z.string(),
	/** Opaque bearer secret: `Authorization: Bearer <deviceToken>` on every device request. */
	deviceToken: z.string().min(40).max(200),
	tokenType: z.literal("Bearer"),
	issuedAt: IsoDateTimeSchema
});
const DevicePairingExchangeResponseSchema = z.discriminatedUnion("status", [z.object({
	status: z.literal("pending"),
	expiresAt: IsoDateTimeSchema,
	pollIntervalMs: PositiveIntSchema
}), z.object({
	status: z.literal("paired"),
	credentials: DeviceCredentialsSchema
})]);
/** CMS → `GET devices/pairing/:pairingCode` — confirm WHICH device before spending a seat. */
const DevicePairingPreviewSchema = z.object({
	pairingCode: PairingCodeSchema,
	device: DeviceInfoSchema,
	expiresAt: IsoDateTimeSchema
});
/** CMS (ADMIN+) → `POST devices/pairing/claim` — creates a NEW screen, consumes one seat. */
const DevicePairingClaimSchema = z.object({
	pairingCode: PairingCodeSchema,
	name: z.string().trim().min(1).max(200),
	timezone: IanaTimeZoneSchema,
	/** Phase 16 Step 15b: player custom attributes (the required ones must be filled). */
	attributes: z.record(z.string(), z.unknown()).optional()
});
/** CMS (ADMIN+) → `POST screens/:screenId/device` — replace hardware on an EXISTING screen (no new seat). */
const DeviceRepairRequestSchema = z.object({ pairingCode: PairingCodeSchema });
/** Device binding shown in the CMS screen list. */
const ScreenDeviceSchema = z.object({
	manufacturer: z.string(),
	model: z.string(),
	pairedAt: IsoDateTimeSchema,
	/** First authenticated request made with the credential; null = token issued but never used yet. */
	confirmedAt: IsoDateTimeSchema.nullable()
});
//#endregion
//#region src/screens.ts
/** CMS list row for a screen (player seat). */
const ScreenSummarySchema = z.object({
	id: IdSchema,
	name: z.string(),
	timezone: z.string(),
	createdAt: IsoDateTimeSchema,
	lastSeenAt: IsoDateTimeSchema.nullable(),
	manifestRevision: PositiveIntSchema.nullable(),
	/** Phase 4 (additive): the paired player, or null when no active device credential exists. */
	device: ScreenDeviceSchema.nullable(),
	/**
	* Phase 16 Step 6: the player's groups, in membership order (that order decides whose content comes first
	* after the player's own). Replaces Phase 5 `groupId` (one group per player).
	*/
	groupIds: z.array(IdSchema).default([]),
	/** Coordinates (used by location-aware widgets). */
	latitude: z.number().nullable(),
	longitude: z.number().nullable(),
	/**
	* Phase 17.7: where the location came from — set by hand in the CMS (never overwritten), reported by the player's
	* device (Wi-Fi/GPS), or estimated from its internet address (approximate). Null = no location yet.
	*/
	locationSource: z.enum([
		"manual",
		"device",
		"ip"
	]).nullable().default(null),
	/** Approximate accuracy in metres (device: as reported; internet address: city-level estimate). */
	locationAccuracyM: z.int().nonnegative().nullable().default(null),
	/** Phase 16 (additive): player tags (Publish → Tags, restrictions). */
	tags: z.array(z.string()).default([]),
	/** Phase 16: items in "Content In Player" (legacy "No. Content"). */
	contentCount: z.int().nonnegative().default(0),
	/** Phase 16 Step 8: this player's own settings overrides (missing keys inherit the organization defaults). */
	settings: PlayerSettingsOverridesSchema.default({}),
	/** Phase 16: app version from the latest heartbeat (legacy "App Version"). */
	appVersion: z.string().nullable().default(null),
	/**
	* What content this player can show, as it last reported (null: it hasn't reported yet, so nothing is assumed).
	* `apps`: compositions and custom HTML5 apps; `pdf`: PDFs; `widgets`: built-in app types (empty: the original four).
	*/
	plays: z.object({
		apps: z.boolean(),
		pdf: z.boolean(),
		widgets: z.array(z.string())
	}).nullable().default(null),
	/**
	* What the screen is doing, from its latest heartbeat (null: no recent heartbeat). `downloadPercent` while it
	* fetches content; `playing` false while it shows its standby / downloading screen instead of content.
	*/
	activity: z.object({
		state: z.string(),
		fallbackReason: z.string().nullable(),
		downloadPercent: z.number().int().min(0).max(100).nullable(),
		filesDone: z.number().int().nonnegative().nullable(),
		filesTotal: z.number().int().nonnegative().nullable()
	}).nullable().default(null),
	/**
	* Is the player playing its latest content? `up_to_date`; `updating` (downloading or about to switch); `problem`
	* (files failed — `reason` says why; may still be playing the latest without them); `waiting` (sent, not picked up
	* yet); `failing` (online, but it keeps failing to take it: `syncError` says why); `offline` (behind and not
	* connected); `none` (nothing published). Computed from its latest heartbeat.
	*/
	content: z.object({
		status: z.enum([
			"up_to_date",
			"updating",
			"problem",
			"waiting",
			"failing",
			"offline",
			"none"
		]),
		playingRevision: z.number().int().nullable(),
		latestRevision: z.number().int().nullable(),
		downloadPercent: z.number().int().min(0).max(100).nullable(),
		filesFailed: z.number().int().nonnegative(),
		/** Why files failed: unreachable / not_found / http_error / corrupt / playback_failed. */
		reason: z.string().nullable(),
		/** `failing`: online, but every attempt to take the new content has failed for a while — and why. */
		syncError: z.object({
			code: z.string(),
			detail: z.string(),
			since: z.string()
		}).nullable().default(null)
	}).nullable().default(null),
	/** Phase 16 Step 15b: custom attribute values (definition id → value). */
	attributes: AttributeValuesSchema.default({})
});
/** Consumes one purchased seat — rejected with QUOTA_SCREENS_EXCEEDED when seats are full. */
const CreateScreenRequestSchema = z.object({
	name: z.string().trim().min(1).max(200),
	timezone: IanaTimeZoneSchema,
	/** Phase 16 Step 15b: player custom attributes; the required ones must be filled here. */
	attributes: z.record(IdSchema, z.unknown()).default({})
});
//#endregion
//#region src/calendar.ts
const DAY = 864e5;
/** yyyy-mm-dd of an instant in a time zone. */
function localDate(ms, timeZone) {
	const p = new Intl.DateTimeFormat("en-CA", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit"
	}).formatToParts(ms);
	const g = (t) => p.find((x) => x.type === t)?.value ?? "00";
	return `${g("year")}-${g("month")}-${g("day")}`;
}
const dayNumber = (date) => Math.round(Date.parse(`${date}T00:00:00Z`) / DAY);
function agenda(events, nowMs, timeZone, daysAhead, maxEvents) {
	const today = localDate(nowMs, timeZone);
	const days = /* @__PURE__ */ new Map();
	let count = 0;
	for (const e of events) {
		if (count >= maxEvents) break;
		const start = Date.parse(e.start);
		const end = Date.parse(e.end);
		let date;
		let now;
		if (e.allDay) {
			const first = e.start.slice(0, 10);
			if (dayNumber(e.end.slice(0, 10)) - 1 < dayNumber(today)) continue;
			date = dayNumber(first) < dayNumber(today) ? today : first;
			now = date === today;
		} else {
			if (end <= nowMs && !(end === start && start >= nowMs)) continue;
			now = start <= nowMs && nowMs < end;
			date = start < nowMs ? today : localDate(start, timeZone);
		}
		const offset = dayNumber(date) - dayNumber(today);
		if (offset < 0 || offset >= daysAhead) continue;
		let d = days.get(date);
		if (!d) days.set(date, d = {
			date,
			offset,
			events: []
		});
		d.events.push({
			...e,
			now
		});
		count++;
	}
	return [...days.values()].sort((a, b) => a.offset - b.offset);
}
//#endregion
//#region src/chart.ts
/** "1,234.5", "AED 12", "45%", "(3)" → a number; anything else → null. */
function parseChartNumber(cell) {
	if (cell == null) return null;
	let s = cell.trim();
	if (!s) return null;
	const negative = /^\(.*\)$/.test(s);
	s = s.replace(/[()]/g, "").replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d))).replace(/[^\d.,\-]/g, "");
	if (s.lastIndexOf(",") > s.lastIndexOf(".") && /,\d{1,2}$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
	else s = s.replace(/,/g, "");
	const n = Number(s);
	return s && Number.isFinite(n) ? negative ? -Math.abs(n) : n : null;
}
function chartData(table, cfg) {
	const labelAt = table.columns.findIndex((c) => c.key === cfg.labelColumn);
	const valueAt = cfg.valueColumns.map((k) => table.columns.findIndex((c) => c.key === k)).filter((i) => i >= 0);
	if (labelAt < 0 || !valueAt.length) return {
		labels: [],
		series: []
	};
	let rows = table.rows.filter((r) => (r[labelAt] ?? "").trim() !== "");
	if (cfg.sort !== "none") {
		const v = (r) => parseChartNumber(r[valueAt[0]]) ?? -Infinity;
		rows = [...rows].sort((a, b) => cfg.sort === "desc" ? v(b) - v(a) : v(a) - v(b));
	}
	rows = rows.slice(0, cfg.maxRows);
	return {
		labels: rows.map((r) => r[labelAt].trim()),
		series: valueAt.map((i, n) => ({
			name: table.columns[i].name,
			color: cfg.colors[n % cfg.colors.length],
			values: rows.map((r) => parseChartNumber(r[i]))
		}))
	};
}
function formatChartValue(v, cfg) {
	if (v == null) return "–";
	const n = v.toLocaleString("en-US", {
		minimumFractionDigits: cfg.decimals,
		maximumFractionDigits: cfg.decimals
	});
	return `${cfg.prefix}${n}${cfg.suffix}`;
}
const esc = (s) => s.replace(/[&<>"']/g, (c) => ({
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	"\"": "&quot;",
	"'": "&#39;"
})[c]);
const clip = (s, n) => s.length > n ? `${s.slice(0, Math.max(1, n - 1))}…` : s;
/**
* The value axis: about four intervals of a round step (1, 2, 2.5 or 5 × 10ⁿ), its ends on that step, and the
* decimals its labels need (a 2.5 step shows "2.5", not "3"). Shared with Android (ChartData.axis).
*/
function chartAxis(values) {
	const top = Math.max(0, ...values);
	const bottom = Math.min(0, ...values);
	const p = 10 ** Math.floor(Math.log10((top - bottom || 1) / 4));
	const count = (st) => Math.ceil(top / st - 1e-9) - Math.floor(bottom / st + 1e-9);
	const step = [
		p / 10,
		p,
		p * 10
	].flatMap((q) => [
		1,
		2,
		2.5,
		5
	].map((m) => m * q)).filter((st) => count(st) >= 3 && count(st) <= 6).sort((x, y) => Math.abs(count(x) - 4) - Math.abs(count(y) - 4) || x - y)[0] ?? p;
	const hi = Math.ceil(top / step - 1e-9) * step || step;
	const lo = Math.floor(bottom / step + 1e-9) * step;
	const fraction = String(Number(step.toFixed(10))).split(".")[1];
	return {
		lo,
		hi,
		step,
		decimals: fraction ? fraction.length : 0
	};
}
/**
* The chart as an SVG document of `width` × `height` (the zone's pixels): title on top, then the chart; colours from
* the config, text in `foreground`, the zone background behind it (not drawn here). `font` is a CSS font-family.
*/
function chartSvg(data, cfg, width, height, font = "sans-serif") {
	const W = Math.max(1, width);
	const H = Math.max(1, height);
	const unit = Math.min(W, H);
	const pad = unit * .05;
	const fg = cfg.foreground;
	const rtl = cfg.direction === "rtl";
	const out = [];
	const text = (x, y, s, size, o = {}) => out.push(`<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" font-size="${size.toFixed(1)}" fill="${o.fill ?? fg}" text-anchor="${o.anchor ?? "start"}"${o.weight ? ` font-weight="${o.weight}"` : ""}${o.opacity != null ? ` fill-opacity="${o.opacity}"` : ""} dominant-baseline="${o.baseline ?? "alphabetic"}">${esc(s)}</text>`);
	let top = pad;
	if (cfg.title) {
		const size = Math.min(H * .085, W * .05);
		text(rtl ? W - pad : pad, top + size, clip(cfg.title, 60), size, {
			anchor: rtl ? "end" : "start",
			weight: 700
		});
		top += size * 1.6;
	}
	const n = data.labels.length;
	const fmt = (v) => formatChartValue(v, cfg);
	if (!n || !data.series.length) text(W / 2, (top + H) / 2, "–", Math.min(H * .2, W * .1), {
		anchor: "middle",
		opacity: .6,
		baseline: "middle"
	});
	else if (cfg.kind === "number") {
		const v = data.series[0].values[0] ?? null;
		const big = Math.min((H - top) * .42, W / Math.max(4, fmt(v).length) * 1.7);
		const mid = top + (H - top) / 2;
		text(W / 2, mid, fmt(v), big, {
			anchor: "middle",
			weight: 700,
			fill: data.series[0].color,
			baseline: "middle"
		});
		text(W / 2, mid + big * .85, clip(data.labels[0], 40), big * .3, {
			anchor: "middle",
			opacity: .8,
			baseline: "middle"
		});
	} else if (cfg.kind === "pie") {
		const s = data.series[0];
		const vals = s.values.map((v) => Math.max(0, v ?? 0));
		const total = vals.reduce((a, b) => a + b, 0) || 1;
		const legendW = W * .42;
		const r = Math.min((H - top - pad) / 2, (W - legendW - pad * 3) / 2);
		const cx = rtl ? W - pad - r : pad + r;
		const cy = top + (H - top - pad) / 2;
		const palette = (i) => [
			s.color,
			...cfg.colors.filter((c) => c !== s.color),
			"#A78BFA",
			"#FB923C",
			"#94A3B8"
		][i % (cfg.colors.length + 3)];
		let a0 = -Math.PI / 2;
		vals.forEach((v, i) => {
			const a1 = a0 + v / total * Math.PI * 2;
			if (v > 0) {
				const large = a1 - a0 > Math.PI ? 1 : 0;
				const [x0, y0, x1, y1] = [
					cx + r * Math.cos(a0),
					cy + r * Math.sin(a0),
					cx + r * Math.cos(a1 - 1e-6),
					cy + r * Math.sin(a1 - 1e-6)
				];
				out.push(`<path d="M${cx.toFixed(1)},${cy.toFixed(1)} L${x0.toFixed(1)},${y0.toFixed(1)} A${r.toFixed(1)},${r.toFixed(1)} 0 ${large} 1 ${x1.toFixed(1)},${y1.toFixed(1)} Z" fill="${palette(i)}"/>`);
			}
			a0 = a1;
		});
		out.push(`<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(r * .55).toFixed(1)}" fill="${cfg.background}"/>`);
		const row = Math.min((H - top - pad) / Math.max(n, 4), H * .09);
		const lx = rtl ? pad + legendW : W - pad - legendW;
		data.labels.forEach((label, i) => {
			const y = top + row * (i + .5) + Math.max(0, (H - top - pad - row * n) / 2);
			const sw = row * .45;
			out.push(`<rect x="${(rtl ? lx - sw : lx).toFixed(1)}" y="${(y - sw / 2).toFixed(1)}" width="${sw.toFixed(1)}" height="${sw.toFixed(1)}" rx="${(sw * .2).toFixed(1)}" fill="${palette(i)}"/>`);
			const tx = rtl ? lx - sw * 1.6 : lx + sw * 1.6;
			const pct = `${Math.round(vals[i] / total * 100)}%`;
			text(tx, y, `${clip(label, 22)}${cfg.showValues ? `  ${fmt(s.values[i] ?? null)} · ${pct}` : ""}`, row * .5, {
				anchor: rtl ? "end" : "start",
				baseline: "middle"
			});
		});
	} else {
		const all = data.series.flatMap((s) => s.values.filter((v) => v != null));
		const axis = chartAxis(all);
		const { lo, hi: max } = axis;
		const fmtAxis = (v) => formatChartValue(v, {
			...cfg,
			decimals: Math.max(cfg.decimals, axis.decimals)
		});
		const legend = data.series.length > 1;
		const legendH = legend ? H * .07 : 0;
		const fs = Math.min(H * .045, W * .025);
		const horizontal = cfg.kind === "bar";
		const labelSpace = horizontal ? Math.min(W * .3, Math.max(...data.labels.map((l) => clip(l, 24).length)) * fs * .6 + fs) : fs * 2.2;
		const axisSpace = horizontal ? fs * 2 : Math.max(...[max, lo].map((v) => fmtAxis(v).length)) * fs * .6 + fs;
		const valueRoom = horizontal && cfg.showValues ? Math.max(...all.map((v) => fmt(v).length)) * fs * .62 + fs * .6 : 0;
		const axisEnd = horizontal ? fmtAxis(max).length * fs * .3 : 0;
		const x0 = horizontal ? rtl ? pad + Math.max(valueRoom, axisEnd) : pad + labelSpace : rtl ? pad : pad + axisSpace;
		const x1 = horizontal ? rtl ? W - pad - labelSpace : W - pad - Math.max(valueRoom, axisEnd) : rtl ? W - pad - axisSpace : W - pad;
		const y0 = top + legendH + (legend ? fs * .6 : 0) + (!horizontal && cfg.showValues ? fs * 1.2 : fs * .4);
		const y1 = H - pad - (horizontal ? axisSpace : labelSpace);
		const scale = (v, a, b) => a + (v - lo) / (max - lo || 1) * (b - a);
		const ticks = Math.round((max - lo) / axis.step);
		for (let i = 0; i <= ticks; i++) {
			const v = lo + axis.step * i;
			if (horizontal) {
				const x = rtl ? scale(v, x1, x0) : scale(v, x0, x1);
				out.push(`<line x1="${x.toFixed(1)}" y1="${y0.toFixed(1)}" x2="${x.toFixed(1)}" y2="${y1.toFixed(1)}" stroke="${fg}" stroke-opacity="0.15"/>`);
				text(x, y1 + fs * 1.4, fmtAxis(v), fs * .85, {
					anchor: "middle",
					opacity: .7
				});
			} else {
				const y = scale(v, y1, y0);
				out.push(`<line x1="${x0.toFixed(1)}" y1="${y.toFixed(1)}" x2="${x1.toFixed(1)}" y2="${y.toFixed(1)}" stroke="${fg}" stroke-opacity="0.15"/>`);
				text(rtl ? x1 + fs * .5 : x0 - fs * .5, y, fmtAxis(v), fs * .85, {
					anchor: rtl ? "start" : "end",
					opacity: .7,
					baseline: "middle"
				});
			}
		}
		const k = data.series.length;
		const band = (horizontal ? y1 - y0 : x1 - x0) / n;
		const slot = (i) => horizontal ? y0 + band * i : rtl ? x1 - band * (i + 1) : x0 + band * i;
		data.labels.forEach((label, i) => {
			if (horizontal) text(rtl ? x1 + fs * .5 : x0 - fs * .5, slot(i) + band / 2, clip(label, 24), fs, {
				anchor: rtl ? "start" : "end",
				baseline: "middle"
			});
			else if (n <= 16 || i % Math.ceil(n / 16) === 0) text(slot(i) + band / 2, y1 + fs * 1.3, clip(label, Math.max(4, Math.floor(band / (fs * .55)))), fs, { anchor: "middle" });
		});
		if (cfg.kind === "line") data.series.forEach((s) => {
			const pts = s.values.map((v, i) => v == null ? null : [slot(i) + band / 2, scale(v, y1, y0)]);
			const d = pts.reduce((acc, p, i) => p ? `${acc}${acc && pts[i - 1] ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)} ` : acc, "");
			out.push(`<path d="${d.trim()}" fill="none" stroke="${s.color}" stroke-width="${(unit * .008).toFixed(1)}" stroke-linejoin="round" stroke-linecap="round"/>`);
			pts.forEach((p, i) => {
				if (!p) return;
				out.push(`<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="${(unit * .011).toFixed(1)}" fill="${s.color}"/>`);
				if (cfg.showValues && k === 1) text(p[0], p[1] - unit * .025, fmt(s.values[i] ?? null), fs * .85, {
					anchor: "middle",
					weight: 600
				});
			});
		});
		else {
			const gap = band * .22;
			const each = (band - gap) / k;
			data.series.forEach((s, si) => {
				s.values.forEach((v, i) => {
					if (v == null) return;
					const a = slot(i) + gap / 2 + each * si;
					if (horizontal) {
						const [xa, xb] = rtl ? [scale(Math.max(0, v), x1, x0), scale(Math.min(0, v), x1, x0)] : [scale(Math.min(0, v), x0, x1), scale(Math.max(0, v), x0, x1)];
						out.push(`<rect x="${xa.toFixed(1)}" y="${a.toFixed(1)}" width="${Math.max(0, xb - xa).toFixed(1)}" height="${each.toFixed(1)}" rx="${Math.min(each * .12, unit * .012).toFixed(1)}" fill="${s.color}"/>`);
						if (cfg.showValues) text(rtl ? xa - fs * .4 : xb + fs * .4, a + each / 2, fmt(v), Math.min(fs, each * .8), {
							anchor: rtl ? "end" : "start",
							baseline: "middle",
							weight: 600
						});
					} else {
						const [ya, yb] = [scale(Math.max(0, v), y1, y0), scale(Math.min(0, v), y1, y0)];
						out.push(`<rect x="${a.toFixed(1)}" y="${ya.toFixed(1)}" width="${each.toFixed(1)}" height="${Math.max(0, yb - ya).toFixed(1)}" rx="${Math.min(each * .12, unit * .012).toFixed(1)}" fill="${s.color}"/>`);
						const vs = Math.min(fs, each * 1.05 / (fmt(v).length * .6));
						if (cfg.showValues && vs >= fs * .6) text(a + each / 2, ya - fs * .4, fmt(v), vs, {
							anchor: "middle",
							weight: 600
						});
					}
				});
			});
		}
		if (legend) {
			let x = rtl ? W - pad : pad;
			const y = top + legendH * .45;
			for (const s of data.series) {
				const sw = fs * .8;
				out.push(`<rect x="${(rtl ? x - sw : x).toFixed(1)}" y="${(y - sw / 2).toFixed(1)}" width="${sw.toFixed(1)}" height="${sw.toFixed(1)}" rx="${(sw * .2).toFixed(1)}" fill="${s.color}"/>`);
				const label = clip(s.name, 20);
				text(rtl ? x - sw * 1.5 : x + sw * 1.5, y, label, fs, {
					anchor: rtl ? "end" : "start",
					baseline: "middle"
				});
				const step = sw * 1.5 + label.length * fs * .6 + fs * 1.2;
				x = rtl ? x - step : x + step;
			}
		}
	}
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W.toFixed(0)} ${H.toFixed(0)}" width="100%" height="100%" font-family="${esc(font)}" direction="${rtl ? "rtl" : "ltr"}">${out.join("")}</svg>`;
}
//#endregion
//#region src/photos.ts
/** Only https links are shown (players refuse plain http, and a cell can hold anything). */
function photoUrl(cell) {
	const s = (cell ?? "").trim();
	return /^https:\/\/[a-z0-9.-]+(:\d+)?(\/\S*)?$/i.test(s) ? s : null;
}
/** Rows with a picture link, without repeats, shuffled when asked (`random` for tests), first `maxPhotos`. */
function photoList(table, cfg, random = Math.random) {
	const imageAt = table.columns.findIndex((c) => c.key === cfg.imageColumn);
	if (imageAt < 0) return [];
	const captionAt = cfg.captionColumn ? table.columns.findIndex((c) => c.key === cfg.captionColumn) : -1;
	const seen = /* @__PURE__ */ new Set();
	const out = [];
	for (const r of table.rows) {
		const url = photoUrl(r[imageAt]);
		if (!url || seen.has(url)) continue;
		seen.add(url);
		out.push({
			url,
			caption: captionAt >= 0 ? (r[captionAt] ?? "").trim() : ""
		});
	}
	if (cfg.shuffle) for (let i = out.length - 1; i > 0; i--) {
		const j = Math.floor(random() * (i + 1));
		[out[i], out[j]] = [out[j], out[i]];
	}
	return out.slice(0, cfg.maxPhotos);
}
/** The wall's tile count: columns × rows, or fewer when there are fewer photos. */
const wallTiles = (cfg, photos) => Math.max(1, Math.min(cfg.wallColumns * cfg.wallRows, photos));
//#endregion
//#region src/people.ts
const MONTHS = [
	"jan",
	"feb",
	"mar",
	"apr",
	"may",
	"jun",
	"jul",
	"aug",
	"sep",
	"oct",
	"nov",
	"dec"
];
/** Month and day of a birthday cell: 2026-03-14, 14/03, 14/03/1990, 14.03, 14 March, March 14. null if unreadable. */
function birthday(cell) {
	const s = (cell ?? "").trim().toLowerCase();
	let m;
	let month = 0;
	let day = 0;
	if (m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s)) [month, day] = [Number(m[2]), Number(m[3])];
	else if (m = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-]\d{2,4})?$/.exec(s)) [day, month] = [Number(m[1]), Number(m[2])];
	else if (m = /^(\d{1,2})\s+([a-z]+)/.exec(s)) [day, month] = [Number(m[1]), MONTHS.indexOf(m[2].slice(0, 3)) + 1];
	else if (m = /^([a-z]+)\s+(\d{1,2})/.exec(s)) [month, day] = [MONTHS.indexOf(m[1].slice(0, 3)) + 1, Number(m[2])];
	else return null;
	return month >= 1 && month <= 12 && day >= 1 && day <= 31 ? {
		month,
		day
	} : null;
}
/** Days from `today` (yyyy-mm-dd) to the next month/day (0 = today; 29 Feb falls on 1 Mar in other years). */
function daysUntil(today, month, day) {
	const [y, m, d] = today.split("-").map(Number);
	const at = (year) => {
		const t = Date.UTC(year, month - 1, day);
		return new Date(t).getUTCMonth() === month - 1 ? t : Date.UTC(year, month - 1, day + 1);
	};
	const now = Date.UTC(y, m - 1, d);
	const next = at(y) >= now ? at(y) : at(y + 1);
	return Math.round((next - now) / 864e5);
}
/** The people to show. `today` (yyyy-mm-dd, the player's zone) only matters for birthdays. */
function peopleList(table, cfg, today) {
	const col = (key) => key ? table.columns.findIndex((c) => c.key === key) : -1;
	const [n, p, r, de, ex, dt] = [
		cfg.nameColumn,
		cfg.photoColumn,
		cfg.roleColumn,
		cfg.detailColumn,
		cfg.extraColumn,
		cfg.dateColumn
	].map(col);
	if (n < 0) return [];
	const cell = (row, i) => i >= 0 ? (row[i] ?? "").trim() : "";
	let out = table.rows.filter((row) => cell(row, n)).map((row) => {
		const b = cfg.style === "birthdays" ? birthday(row[dt]) : null;
		return {
			name: cell(row, n),
			photo: p >= 0 ? photoUrl(row[p]) : null,
			role: cell(row, r),
			detail: cell(row, de),
			extra: cell(row, ex),
			inDays: b ? daysUntil(today, b.month, b.day) : null,
			month: b?.month ?? null,
			day: b?.day ?? null
		};
	});
	if (cfg.style === "birthdays") {
		const thisMonth = Number(today.slice(5, 7));
		out = out.filter((x) => x.inDays !== null && (cfg.birthdayRange === "today" ? x.inDays === 0 : cfg.birthdayRange === "week" ? x.inDays < 7 : x.month === thisMonth)).sort((a, b) => (cfg.birthdayRange === "month" ? a.day - b.day : a.inDays - b.inDays) || a.name.localeCompare(b.name));
	} else if (cfg.style === "directory") out.sort((a, b) => a.name.localeCompare(b.name, cfg.language));
	return out;
}
/** How many people one page shows. */
function peoplePerPage(cfg) {
	switch (cfg.style) {
		case "spotlight": return 1;
		case "directory": return cfg.columns * cfg.rows * 2;
		default: return cfg.columns * cfg.rows;
	}
}
/** Fixed labels, so every player says the same thing. */
function peopleLabels(language) {
	return language === "ar" ? {
		today: "اليوم",
		none: "لا توجد أعياد ميلاد",
		birthdaysTitle: "عيد ميلاد سعيد"
	} : {
		today: "Today",
		none: "No birthdays",
		birthdaysTitle: "Happy birthday"
	};
}
/** The grid a page really uses: no empty rows or columns when it has fewer people than fit. */
function peopleGrid(cfg, count) {
	const cols = Math.max(1, Math.min(cfg.columns, count));
	return {
		cols,
		rows: Math.max(1, Math.min(cfg.rows, Math.ceil(count / cols)))
	};
}
/** Directory page layout: one or two columns, filled evenly (at least 5 rows tall, so text stays a sensible size). */
function directoryGrid(cfg, count) {
	const cols = cfg.columns > 1 && count > 5 ? 2 : 1;
	return {
		cols,
		rows: Math.max(5, Math.ceil(count / cols))
	};
}
//#endregion
//#region src/meeting-room.ts
function roomStatus(events, nowMs, timeZone, maxUpcoming) {
	const today = localDate(nowMs, timeZone);
	const meetings = [];
	for (const e of events) {
		if (e.allDay) continue;
		const start = Date.parse(e.start);
		const end = Date.parse(e.end);
		if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end <= nowMs) continue;
		meetings.push({
			title: e.title,
			organizer: e.organizer ?? null,
			start,
			end
		});
	}
	meetings.sort((a, b) => a.start - b.start || a.end - b.end);
	const cur = meetings.filter((m) => m.start <= nowMs).reduce((best, m) => !best || m.end > best.end ? m : best, null);
	const upcoming = meetings.filter((m) => m.start > nowMs && localDate(m.start, timeZone) === today);
	return {
		busy: cur !== null,
		current: cur ? {
			...cur,
			progress: Math.min(1, Math.max(0, (nowMs - cur.start) / (cur.end - cur.start))),
			minutesLeft: Math.ceil((cur.end - nowMs) / 6e4)
		} : null,
		next: upcoming[0] ?? null,
		later: upcoming.slice(0, Math.max(0, maxUpcoming))
	};
}
/** Portrait when chosen, or `auto` in a zone taller than wide. */
const meetingRoomPortrait = (cfg, width, height) => cfg.layout === "portrait" || cfg.layout !== "landscape" && height > width;
/** The status colour while the calendar is unknown (neither free nor busy). */
const MEETING_ROOM_UNKNOWN_COLOR = "#475569";
function meetingRoomLabels(language) {
	return language === "ar" ? {
		free: "متاحة",
		busy: "مشغولة",
		private: "مشغولة",
		nextAt: (t) => `الاجتماع التالي الساعة ${t}`,
		freeRestOfDay: "متاحة لبقية اليوم",
		endsIn: (n) => `ينتهي خلال ${n} دقيقة`,
		laterToday: "لاحقًا اليوم",
		noMeetings: "لا توجد اجتماعات أخرى اليوم",
		unknown: "التقويم غير متاح"
	} : {
		free: "FREE",
		busy: "BUSY",
		private: "Busy",
		nextAt: (t) => `Next meeting at ${t}`,
		freeRestOfDay: "Free for the rest of the day",
		endsIn: (n) => n === 1 ? "Ends in 1 min" : `Ends in ${n} min`,
		laterToday: "Later today",
		noMeetings: "No more meetings today",
		unknown: "Calendar unavailable"
	};
}
//#endregion
//#region src/stream.ts
/** hls = HTTP Live Streaming (.m3u8), dash = MPEG-DASH (.mpd), file = a progressive video file (.mp4, .webm…). */
const STREAM_KINDS = [
	"hls",
	"dash",
	"file",
	"unknown"
];
/** What kind of stream a link is, from its address alone ('unknown' when it doesn't say). */
function streamKind(url) {
	let u;
	try {
		u = new URL(url);
	} catch {
		return "unknown";
	}
	const path = u.pathname.toLowerCase();
	const query = u.search.toLowerCase();
	if (path.endsWith(".m3u8") || /[(=]m3u8/.test(path + query)) return "hls";
	if (path.endsWith(".mpd") || /[(=]mpd/.test(path + query)) return "dash";
	if (/\.(mp4|m4v|mov|webm)$/.test(path)) return "file";
	return "unknown";
}
/** What kind of stream a response is, from its Content-Type ('unknown' for anything else, e.g. a web page). */
function streamKindOfContentType(contentType) {
	const t = (contentType ?? "").split(";")[0].trim().toLowerCase();
	if (t === "application/vnd.apple.mpegurl" || t === "application/x-mpegurl" || t === "audio/mpegurl" || t === "audio/x-mpegurl") return "hls";
	if (t === "application/dash+xml") return "dash";
	if (t.startsWith("video/")) return "file";
	return "unknown";
}
/** How long a player waits before trying a dropped stream again: 2 s, 4 s, 8 s … at most a minute. */
function streamRetryDelayMs(attempt) {
	return Math.min(6e4, 2e3 * 2 ** Math.min(Math.max(0, Math.floor(attempt)), 5));
}
/**
* What the CMS learns about a stream link before previewing it (`stream-check`):
*  - ok: it answers and is (or looks like) a stream of `kind`;
*  - not_stream: it answers with a web page or other content, not a video stream;
*  - login: it needs signing in (401/403) — screens can't sign in;
*  - not_found / error: the address answers 404 / another error, or can't be reached.
*/
const StreamCheckSchema = z.object({
	verdict: z.enum([
		"ok",
		"not_stream",
		"login",
		"not_found",
		"error"
	]),
	kind: z.enum(STREAM_KINDS),
	status: z.int().nullable(),
	detail: z.string().max(300).nullable()
});
//#endregion
//#region src/social-wall.ts
/** Known platforms: badge label and colour. Anything else shows no badge. */
const SOCIAL_PLATFORMS = {
	x: {
		label: "X",
		color: "#000000"
	},
	instagram: {
		label: "Instagram",
		color: "#E1306C"
	},
	facebook: {
		label: "Facebook",
		color: "#1877F2"
	},
	linkedin: {
		label: "LinkedIn",
		color: "#0A66C2"
	},
	tiktok: {
		label: "TikTok",
		color: "#111111"
	},
	youtube: {
		label: "YouTube",
		color: "#FF0000"
	},
	mastodon: {
		label: "Mastodon",
		color: "#6364FF"
	},
	threads: {
		label: "Threads",
		color: "#101010"
	},
	bluesky: {
		label: "Bluesky",
		color: "#1185FE"
	}
};
/** A platform key from a platform cell, or failing that from text that names one (a link or handle). */
function socialPlatform(cell, hint = "") {
	const s = `${cell ?? ""}`.trim().toLowerCase();
	const find = (v) => {
		if (!v) return "";
		if (/(^|[^a-z])(x|twitter)([^a-z]|$)|twitter\.com|x\.com/.test(v)) return "x";
		for (const k of [
			"instagram",
			"facebook",
			"linkedin",
			"tiktok",
			"youtube",
			"mastodon",
			"threads",
			"bluesky"
		]) if (v.includes(k)) return k;
		if (v.includes("bsky")) return "bluesky";
		if (v.includes("youtu.be")) return "youtube";
		return "";
	};
	return find(s) || find(hint.trim().toLowerCase());
}
/**
* A date cell → epoch ms: 2026-10-14, 2026-10-14 08:30, 2026-10-14T08:30:00Z / +04:00 (what RSS feeds become),
* 14/10/2026 [08:30]. A time without a zone is read as UTC. null if unreadable.
*/
function postTime(cell) {
	const s = (cell ?? "").trim();
	let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i.exec(s);
	let y, mo, d;
	if (m) [y, mo, d] = [
		Number(m[1]),
		Number(m[2]),
		Number(m[3])
	];
	else if (m = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?()$/.exec(s)) [d, mo, y] = [
		Number(m[1]),
		Number(m[2]),
		Number(m[3])
	];
	else return null;
	if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
	const [h, mi, se] = [
		Number(m[4] ?? 0),
		Number(m[5] ?? 0),
		Number(m[6] ?? 0)
	];
	if (h > 23 || mi > 59 || se > 59) return null;
	let t = Date.UTC(y, mo - 1, d, h, mi, se);
	const z = m[7];
	if (z && z.toUpperCase() !== "Z") {
		const zm = /^([+-])(\d{2}):?(\d{2})$/.exec(z);
		t -= (zm[1] === "-" ? -1 : 1) * (Number(zm[2]) * 60 + Number(zm[3])) * 6e4;
	}
	return t;
}
const ENTITIES = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: "\"",
	apos: "'",
	nbsp: " "
};
/**
* A post's text as plain words: RSS/Atom summaries (Mastodon's always) arrive as HTML. Drops Mastodon's hidden link
* parts (`<span class="invisible">`), turns breaks into spaces, strips tags and decodes entities. Mirrored in Android
* SocialPosts.plainText.
*/
function postPlainText(s) {
	if (!/[<&]/.test(s)) return s.replace(/\s+/g, " ").trim();
	return s.replace(/<span\b[^>]*\bclass="[^"]*\binvisible\b[^"]*"[^>]*>[^<]*<\/span>/gi, "").replace(/<br\s*\/?>|<\/(p|div|li)>/gi, " ").replace(/<[^>]*>/g, "").replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e) => {
		if (e[0] !== "#") return ENTITIES[e.toLowerCase()] ?? m;
		const n = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : Number(e.slice(1));
		return n > 0 && n <= 1114111 ? String.fromCodePoint(n) : m;
	}).replace(/\s+/g, " ").trim();
}
/** The posts to show: rows with text or a picture, newest first (undated ones after, in feed order), first `maxPosts`. */
function socialPostList(table, cfg) {
	const col = (key) => key ? table.columns.findIndex((c) => c.key === key) : -1;
	const [tx, au, ha, av, im, dt, pl] = [
		cfg.textColumn,
		cfg.authorColumn,
		cfg.handleColumn,
		cfg.avatarColumn,
		cfg.imageColumn,
		cfg.dateColumn,
		cfg.platformColumn
	].map(col);
	if (tx < 0) return [];
	const cell = (row, i) => i >= 0 ? (row[i] ?? "").replace(/\s+/g, " ").trim() : "";
	const seen = /* @__PURE__ */ new Set();
	const out = [];
	table.rows.forEach((row, i) => {
		const text = postPlainText(row[tx] ?? "");
		const image = im >= 0 ? photoUrl(row[im]) : null;
		if (!text && !image) return;
		if (cfg.imagesOnly && !image) return;
		const rawHandle = cell(row, ha);
		const handle = rawHandle && !rawHandle.startsWith("@") && !/\s/.test(rawHandle) ? `@${rawHandle}` : rawHandle;
		const author = cell(row, au) || handle;
		const key = `${author}\u0000${text}\u0000${image ?? ""}`;
		if (seen.has(key)) return;
		seen.add(key);
		out.push({
			author,
			handle: handle === author ? "" : handle,
			avatar: av >= 0 ? photoUrl(row[av]) : null,
			text,
			image,
			at: dt >= 0 ? postTime(row[dt]) : null,
			platform: pl >= 0 ? socialPlatform(row[pl], rawHandle) : socialPlatform("", rawHandle),
			i
		});
	});
	out.sort((a, b) => a.at === null ? b.at === null ? a.i - b.i : 1 : b.at === null ? -1 : b.at - a.at || a.i - b.i);
	return out.slice(0, cfg.maxPosts).map(({ i: _i, ...p }) => p);
}
/** "now", "5m", "3h", "2d", "3w", then a date ("14 Oct"). Arabic: "الآن", "5 د"… */
function timeAgo(at, now, language) {
	if (at === null) return "";
	const min = Math.floor((now - at) / 6e4);
	const ar = language === "ar";
	if (min < 1) return ar ? "الآن" : "now";
	if (min < 60) return ar ? `${min} د` : `${min}m`;
	const h = Math.floor(min / 60);
	if (h < 24) return ar ? `${h} س` : `${h}h`;
	const d = Math.floor(h / 24);
	if (d < 7) return ar ? `${d} ي` : `${d}d`;
	if (d < 35) return ar ? `${Math.floor(d / 7)} أ` : `${Math.floor(d / 7)}w`;
	const t = new Date(at);
	return ar ? `${t.getUTCDate()} ${AR_MONTHS[t.getUTCMonth()]}` : `${t.getUTCDate()} ${EN_MONTHS[t.getUTCMonth()]}`;
}
const EN_MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec"
];
const AR_MONTHS = [
	"يناير",
	"فبراير",
	"مارس",
	"أبريل",
	"مايو",
	"يونيو",
	"يوليو",
	"أغسطس",
	"سبتمبر",
	"أكتوبر",
	"نوفمبر",
	"ديسمبر"
];
/** Posts per page: the wall's grid, one for the spotlight; the strip scrolls through all of them. */
function socialPerPage(cfg) {
	return cfg.style === "spotlight" ? 1 : cfg.style === "strip" ? cfg.maxPosts : cfg.columns * cfg.rows;
}
/** The grid a wall page really uses: no empty rows or columns when it has fewer posts than fit. */
function socialGrid(cfg, count) {
	const cols = Math.max(1, Math.min(cfg.columns, count));
	return {
		cols,
		rows: Math.max(1, Math.min(cfg.rows, Math.ceil(count / cols)))
	};
}
/** Up to two initials for an avatar without a picture ("@" dropped). */
function postInitials(name) {
	return name.replace(/^@/, "").split(/[\s._-]+/).filter(Boolean).map((w) => [...w][0] ?? "").slice(0, 2).join("").toUpperCase();
}
/** True when the text starts (after any @handles, links, digits) with Arabic/Hebrew script, so its card reads right to left. */
function isRtlText(text) {
	for (const ch of text.replace(/@\S+|https?:\/\/\S+/g, "")) {
		if (/[֐-ࣿיִ-﷿ﹰ-﻿]/.test(ch)) return true;
		if (/[A-Za-zÀ-ɏ]/.test(ch)) return false;
	}
	return false;
}
/** Fixed labels, so every player says the same thing. */
function socialLabels(language) {
	return language === "ar" ? { none: "لا توجد منشورات بعد" } : { none: "No posts yet" };
}
/**
* The CMS's first guess at which column is which, from names (English or Arabic) and types: an RSS feed's
* Title/Summary/Published/Image, a sheet's author/handle/avatar/text/image/date/platform.
*/
function guessSocialColumns(cols) {
	const used = /* @__PURE__ */ new Set();
	const find = (re, type) => {
		const c = cols.find((x) => !used.has(x.key) && re.test(`${x.key} ${x.name}`)) ?? (type ? cols.find((x) => !used.has(x.key) && x.type === type) : void 0);
		if (!c) return "";
		used.add(c.key);
		return c.key;
	};
	const avatarColumn = find(/avatar|profile|pfp|الصورة الشخصية|الملف/i);
	const handleColumn = find(/handle|username|user ?name|account|@|المعرف|الحساب/i);
	const platformColumn = find(/platform|network|source|site|المنصة|الشبكة/i) || find(/link|url|رابط/i);
	const dateColumn = find(/date|time|published|posted|created|updated|تاريخ|الوقت|نشر/i, "date");
	const authorColumn = find(/author|name|\bby\b|creator|\bfrom\b|الكاتب|الاسم|المؤلف/i);
	const imageColumn = find(/image|picture|photo|media|pic|img|thumbnail|صورة/i, "image");
	return {
		textColumn: find(/text|content|summary|post|message|body|description|caption|tweet|status|النص|المحتوى|المنشور|الرسالة/i) || find(/title|عنوان/i) || (cols.find((c) => !used.has(c.key) && c.type === "text") ?? cols.find((c) => !used.has(c.key)))?.key || "",
		authorColumn,
		handleColumn,
		avatarColumn,
		imageColumn,
		dateColumn,
		platformColumn
	};
}
//#endregion
//#region src/renditions.ts
/**
* Smaller versions ("renditions") of uploaded videos and images, so a player downloads a file sized for its screen
* instead of the full original.
*
* Sizes are named by the SHORT edge, so a portrait 1080×1920 video is "1080p" like a landscape 1920×1080 one.
* Only versions noticeably smaller than the original are made (never a near-duplicate, never an enlargement):
*   4K original → 1080p + 720p · 1080p original → 720p · 720p or smaller → none.
* Images get a 1080p version only (they are small already; 720p players use it too).
*/
const VIDEO_RENDITION_SHORT_EDGES = [1080, 720];
const IMAGE_RENDITION_SHORT_EDGES = [1080];
/** A version is made only if the original's short edge is at least this much larger (1200p gets no "1080p"). */
const RENDITION_MIN_STEP = 1.15;
/**
* Every video also gets one "compatible" version: H.264 Baseline at ≤ 720p, which any Android decoder plays
* (some old boxes and the Android 5 emulator can't decode High profile). Only players that need it get it.
*/
const COMPAT_SHORT_EDGE = 720;
/** The compatible version's size: 720p, or the original's own size when it's smaller. */
function planCompatRendition(width, height) {
	const short = Math.min(width, height);
	const edge = Math.min(720, short);
	const scale = edge / short;
	return {
		shortEdge: edge,
		width: even(width * scale),
		height: even(height * scale)
	};
}
const MediaVersionSchema = z.object({
	/** The version's short edge: 1080 or 720. */
	shortEdge: PositiveIntSchema,
	/** The Baseline "compatible" version (for players that can't decode High profile). */
	compat: z.boolean().optional(),
	width: PositiveIntSchema,
	height: PositiveIntSchema,
	sizeBytes: NonNegativeIntSchema
});
const even = (n) => Math.max(2, Math.round(n / 2) * 2);
/** The versions to make for an original of `width`×`height`, with their exact (even) dimensions. */
function planRenditions(kind, width, height) {
	const short = Math.min(width, height);
	return (kind === "video" ? VIDEO_RENDITION_SHORT_EDGES : IMAGE_RENDITION_SHORT_EDGES).filter((e) => short >= e * RENDITION_MIN_STEP).map((e) => {
		const scale = e / short;
		return {
			shortEdge: e,
			width: even(width * scale),
			height: even(height * scale)
		};
	});
}
/**
* Which file a screen of `resolution` ("1920x1080", null = unknown) should get: the smallest one that still fills
* the screen at full sharpness, else the largest there is. Fit is checked in both orientations, because a player
* may be set to rotate its content.
*/
function pickRendition(original, allVersions, resolution, baselineOnly = false) {
	if (baselineOnly) return allVersions.find((v) => v.profile === "baseline") ?? original;
	const versions = allVersions.filter((v) => v.profile !== "baseline");
	const m = resolution ? /^(\d+)x(\d+)$/.exec(resolution) : null;
	if (!m || !versions.length) return original;
	const sw = Number(m[1]);
	const sh = Number(m[2]);
	const scale = Math.min(1, Math.max(Math.min(sw / original.width, sh / original.height), Math.min(sh / original.width, sw / original.height)));
	const neededWidth = original.width * scale * .95;
	return [...versions].filter((v) => v.width >= neededWidth).sort((a, b) => a.width - b.width)[0] ?? original;
}
//#endregion
//#region src/custom-apps.ts
const CUSTOM_APP_LIMITS = {
	zipBytes: 52428800,
	unpackedBytes: 104857600,
	files: 2e3,
	settings: 40,
	feeds: 10,
	networkOrigins: 20,
	changelogChars: 1e3
};
/** The file the player opens; at the root of the app (a single top folder in the zip is stripped). */
const CUSTOM_APP_ENTRY = "index.html";
const CUSTOM_APP_MANIFEST_FILE = "manifest.json";
/** Zip entries skipped (never part of an app). */
const CUSTOM_APP_IGNORED = /(^|\/)(__MACOSX\/|\.DS_Store$|Thumbs\.db$|desktop\.ini$)/i;
const settingKey = z.string().trim().min(1).max(40).regex(/^[A-Za-z][A-Za-z0-9_]*$/, "Keys start with a letter and use letters, digits or _");
const label = z.string().trim().min(1).max(80);
const help = z.string().trim().max(200).optional();
/** One field of an app's settings form (manifest.json `settings`). */
const AppSettingSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("text"),
		key: settingKey,
		label,
		help,
		default: z.string().max(2e3).default(""),
		multiline: z.boolean().default(false),
		required: z.boolean().default(false)
	}),
	z.object({
		type: z.literal("number"),
		key: settingKey,
		label,
		help,
		default: z.number().default(0),
		min: z.number().optional(),
		max: z.number().optional(),
		step: z.number().positive().optional()
	}),
	z.object({
		type: z.literal("boolean"),
		key: settingKey,
		label,
		help,
		default: z.boolean().default(false)
	}),
	z.object({
		type: z.literal("color"),
		key: settingKey,
		label,
		help,
		default: HexColorSchema.default("#FFFFFF")
	}),
	z.object({
		type: z.literal("select"),
		key: settingKey,
		label,
		help,
		options: z.array(z.object({
			value: z.string().min(1).max(80),
			label
		})).min(1).max(50),
		default: z.string().max(80).optional()
	})
]);
/** A Data Feed slot (manifest.json `feeds`): the content item binds it to one of the organization's feeds. */
const AppFeedSlotSchema = z.object({
	key: settingKey,
	label,
	help,
	required: z.boolean().default(false)
});
const origin = z.string().trim().regex(/^https:\/\/(\*\.)?[a-z0-9.-]+(:\d{1,5})?$/i, "Use an https origin such as https://api.example.com (no path)");
/** `manifest.json` at the app's root. Every field is optional; an app without one has no settings. */
const AppPackageManifestSchema = z.object({
	name: z.string().trim().max(120).optional(),
	description: z.string().trim().max(500).optional(),
	/** The app's own version label ("1.2.0"); display only. */
	version: z.string().trim().max(40).optional(),
	settings: z.array(AppSettingSchema).max(CUSTOM_APP_LIMITS.settings).default([]),
	feeds: z.array(AppFeedSlotSchema).max(CUSTOM_APP_LIMITS.feeds).default([]),
	/** https origins the app may call while playing. Everything else is blocked on the player. */
	network: z.array(origin).max(CUSTOM_APP_LIMITS.networkOrigins).default([])
}).superRefine((m, ctx) => {
	const keys = /* @__PURE__ */ new Set();
	m.settings.forEach((s, i) => {
		if (keys.has(s.key)) ctx.addIssue({
			code: "custom",
			message: `Two settings use the key “${s.key}”`,
			path: [
				"settings",
				i,
				"key"
			]
		});
		keys.add(s.key);
		if (s.type === "select" && s.default !== void 0 && !s.options.some((o) => o.value === s.default)) ctx.addIssue({
			code: "custom",
			message: `The default of “${s.key}” isn't one of its options`,
			path: [
				"settings",
				i,
				"default"
			]
		});
		if (s.type === "number" && s.min !== void 0 && s.max !== void 0 && s.min > s.max) ctx.addIssue({
			code: "custom",
			message: `“${s.key}”: min is above max`,
			path: [
				"settings",
				i,
				"min"
			]
		});
	});
	const slots = /* @__PURE__ */ new Set();
	m.feeds.forEach((f, i) => {
		if (slots.has(f.key)) ctx.addIssue({
			code: "custom",
			message: `Two feeds use the key “${f.key}”`,
			path: [
				"feeds",
				i,
				"key"
			]
		});
		slots.add(f.key);
	});
});
/** Setting values of one content item (key → value). */
const AppSettingValuesSchema = z.record(z.string(), z.union([
	z.string().max(2e3),
	z.number(),
	z.boolean()
]));
/** A content item's app: which custom app, its setting values and feed bindings (slot key → feed id). */
const AppInstanceConfigSchema = z.object({
	customAppId: IdSchema,
	settings: AppSettingValuesSchema.default({}),
	feeds: z.record(z.string(), IdSchema).default({})
});
/**
* Settings as the app receives them: every declared key, filled with the default when unset or invalid; numbers
* clamped; unknown keys dropped. Errors list what the CMS should show (required text left blank).
*/
function resolveAppSettings(declared, values) {
	const out = {};
	const errors = {};
	for (const s of declared) {
		const v = values[s.key];
		switch (s.type) {
			case "text": {
				const t = typeof v === "string" ? v.slice(0, 2e3) : s.default;
				if (s.required && !t.trim()) errors[s.key] = `Enter ${s.label.toLowerCase()}`;
				out[s.key] = t;
				break;
			}
			case "number": {
				let n = typeof v === "number" && Number.isFinite(v) ? v : s.default;
				if (s.min !== void 0) n = Math.max(s.min, n);
				if (s.max !== void 0) n = Math.min(s.max, n);
				out[s.key] = n;
				break;
			}
			case "boolean":
				out[s.key] = typeof v === "boolean" ? v : s.default;
				break;
			case "color":
				out[s.key] = typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v) ? v : s.default;
				break;
			case "select": out[s.key] = typeof v === "string" && s.options.some((o) => o.value === v) ? v : s.default ?? s.options[0].value;
		}
	}
	return {
		values: out,
		errors
	};
}
const CustomAppFileSchema = z.object({
	path: z.string(),
	sizeBytes: NonNegativeIntSchema
});
const CustomAppVersionSchema = z.object({
	id: IdSchema,
	/** 1, 2, 3… per app. */
	number: PositiveIntSchema,
	changelog: z.string(),
	/** Re-packed zip players download. */
	sizeBytes: NonNegativeIntSchema,
	unpackedBytes: NonNegativeIntSchema,
	fileCount: NonNegativeIntSchema,
	manifest: AppPackageManifestSchema,
	/** Unpacked files for the CMS preview (sandboxed): `{filesUrl}index.html`. */
	filesUrl: z.string(),
	createdAt: IsoDateTimeSchema,
	createdBy: z.string().nullable()
});
const CustomAppSummarySchema = z.object({
	id: IdSchema,
	name: z.string(),
	description: z.string(),
	tags: z.array(z.string()),
	/** The version content plays (always set: an app is created with its first version). */
	currentVersion: CustomAppVersionSchema,
	versionCount: PositiveIntSchema,
	/** Content items made from this app. */
	instanceCount: NonNegativeIntSchema,
	updatedAt: IsoDateTimeSchema
});
const CustomAppSchema = CustomAppSummarySchema.extend({
	/** Newest first. */
	versions: z.array(CustomAppVersionSchema),
	files: z.array(CustomAppFileSchema),
	/** Content items using the app, and players that can't show apps yet (older player build). */
	instances: z.array(z.object({
		id: IdSchema,
		name: z.string()
	})),
	playersNeedingUpdate: z.array(z.object({
		id: IdSchema,
		name: z.string()
	}))
});
/** Multipart text fields sent BEFORE the `file` part of a create / new-version upload. */
const CustomAppUploadFieldsSchema = z.object({
	name: z.string().trim().min(1).max(120).optional(),
	changelog: z.string().trim().max(CUSTOM_APP_LIMITS.changelogChars).optional()
});
const CUSTOM_APP_UPLOAD_FILE_FIELD = "file";
const UpdateCustomAppRequestSchema = z.object({
	name: z.string().trim().min(1, "Enter a name").max(120).optional(),
	description: z.string().trim().max(500).optional(),
	tags: TagsSchema.optional()
}).refine((b) => b.name !== void 0 || b.description !== void 0 || b.tags !== void 0, "Nothing to update");
/**
* Normalizes and checks the entry list of an uploaded zip: refuses unsafe paths, applies the limits, finds the app
* root (`index.html` at the top, or inside one top folder) and returns the files relative to it.
*/
function checkAppZipEntries(entries) {
	const files = [];
	for (const e of entries) {
		const p = e.path;
		if (p.endsWith("/")) continue;
		if (CUSTOM_APP_IGNORED.test(p)) continue;
		if (p.includes("\\") || p.startsWith("/") || /^[a-zA-Z]:/.test(p) || p.split("/").some((s) => s === ".." || s === "." || s === "")) return {
			ok: false,
			error: `The zip contains an unsafe path: “${p.slice(0, 120)}”.`
		};
		if (/[\u0000-\u001f]/.test(p) || p.length > 300) return {
			ok: false,
			error: "The zip contains a file name that is too long or not valid."
		};
		files.push(e);
	}
	if (!files.length) return {
		ok: false,
		error: "The zip is empty."
	};
	if (files.length > CUSTOM_APP_LIMITS.files) return {
		ok: false,
		error: `The app has ${files.length} files; the limit is ${CUSTOM_APP_LIMITS.files}.`
	};
	const unpackedBytes = files.reduce((s, f) => s + f.sizeBytes, 0);
	if (unpackedBytes > CUSTOM_APP_LIMITS.unpackedBytes) return {
		ok: false,
		error: `The app unpacks to ${Math.ceil(unpackedBytes / 1024 ** 2)} MB; the limit is ${CUSTOM_APP_LIMITS.unpackedBytes / 1024 ** 2} MB.`
	};
	let root = "";
	if (!files.some((f) => f.path === "index.html")) {
		const tops = new Set(files.map((f) => f.path.includes("/") ? f.path.slice(0, f.path.indexOf("/") + 1) : ""));
		const [only] = [...tops];
		if (tops.size === 1 && only && files.some((f) => f.path === `${only}index.html`)) root = only;
		else return {
			ok: false,
			error: `Put ${CUSTOM_APP_ENTRY} at the top of the zip (or inside a single folder).`
		};
	}
	return {
		ok: true,
		root,
		files: files.map((f) => ({
			path: f.path.slice(root.length),
			sizeBytes: f.sizeBytes
		})).sort((a, b) => a.path.localeCompare(b.path)),
		unpackedBytes
	};
}
/** manifest.json → its parsed form, or a readable error. Absent = an app without settings. */
function parseAppPackageManifest(text) {
	if (text === null) return {
		ok: true,
		manifest: AppPackageManifestSchema.parse({})
	};
	let json;
	try {
		json = JSON.parse(text.replace(/^﻿/, ""));
	} catch (e) {
		return {
			ok: false,
			error: `${CUSTOM_APP_MANIFEST_FILE} isn't valid JSON (${e.message}).`
		};
	}
	const r = AppPackageManifestSchema.safeParse(json);
	if (!r.success) {
		const i = r.error.issues[0];
		return {
			ok: false,
			error: `${CUSTOM_APP_MANIFEST_FILE}: ${i.path.length ? `${i.path.join(".")}: ` : ""}${i.message}`
		};
	}
	return {
		ok: true,
		manifest: r.data
	};
}
//#endregion
//#region src/media.ts
const MEDIA_STATUSES = [
	"PENDING",
	"PROCESSING",
	"READY",
	"FAILED"
];
/**
* PENDING    uploaded to the API's temp storage, queued
* PROCESSING a worker is normalizing it (`progress` 0–100)
* READY      normalized file is published; the asset can be placed in layouts
* FAILED     rejected or could not be processed (`processingError` says why); never usable
*/
const MediaStatusSchema = z.enum(MEDIA_STATUSES);
/** Hardware targets of the normalizer — shared so the CMS can explain them before upload. */
const MEDIA_LIMITS = {
	/** Hard cap per upload (also bounded by the organization's remaining storage). */
	maxUploadBytes: 2 * 1024 ** 3,
	/** Output frame box: larger media is scaled down to fit, aspect ratio preserved, never upscaled. */
	maxWidth: 3840,
	maxHeight: 2160,
	/** H.264 peak bitrate (VBV maxrate). Keeps 4K inside what signage SoCs decode reliably. */
	maxVideoBitrateBps: 15e6,
	/** ≤ 1080p may keep up to 60 fps; above 1080p is capped at 30 fps (H.264 level 5.1 on common SoCs). */
	maxVideoFps: 60,
	maxVideoFpsAbove1080p: 30,
	/** Longest accepted video. */
	maxVideoDurationMs: 36e5,
	thumbnailWidth: 480,
	/** Phase 16 Step 15f: longest accepted audio file. */
	maxAudioDurationMs: 72e5
};
/** Sniffed from the file's magic bytes — the client-supplied Content-Type is never trusted. */
const ACCEPTED_IMAGE_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/avif",
	"image/tiff",
	"image/gif"
];
const ACCEPTED_VIDEO_TYPES = [
	"video/mp4",
	"video/quicktime",
	"video/webm",
	"video/matroska"
];
/** Phase 16 Step 15f: normalized to AAC in an .m4a (audio/mp4). */
const ACCEPTED_AUDIO_TYPES = [
	"audio/mpeg",
	"audio/mp4",
	"audio/x-m4a",
	"audio/aac",
	"audio/wav",
	"audio/x-wav",
	"audio/ogg",
	"audio/flac",
	"audio/x-flac"
];
/** PDF documents: each page becomes an image on the server. */
const ACCEPTED_DOCUMENT_TYPES = ["application/pdf"];
const ACCEPTED_UPLOAD_TYPES = [
	...ACCEPTED_IMAGE_TYPES,
	...ACCEPTED_VIDEO_TYPES,
	...ACCEPTED_AUDIO_TYPES,
	...ACCEPTED_DOCUMENT_TYPES
];
/** Multipart form of `POST /media`: one `file` part, optional `name` text field (defaults to the file name). */
const MEDIA_UPLOAD_FILE_FIELD = "file";
const MediaUploadFieldsSchema = z.object({
	name: z.string().trim().min(1).max(200).optional(),
	/** Phase 16 Step 6: create inside this library folder (send before the file part). */
	folderId: IdSchema.optional()
});
/** CMS polling cadence while any asset is PENDING/PROCESSING. */
const MEDIA_STATUS_POLL_MS = 2e3;
//#endregion
//#region src/compositions.ts
const COMPOSITION_LIMITS = {
	elements: 100,
	textChars: 2e3,
	/** Bound Data Feeds per composition. */
	feeds: 5,
	/** Images and videos copied into one bundle (players unpack at most 200 MB). */
	bundleBytes: 157286400
};
/** Bundle layout. The runtime reads `composition.json`; media files sit in `media/`. */
const COMPOSITION_ENTRY = "index.html";
const COMPOSITION_DOC_FILE = "composition.json";
/** Weather comes from Open-Meteo (no key), the same service the native weather app uses. */
const COMPOSITION_WEATHER_ORIGIN = "https://api.open-meteo.com";
const px = z.number().min(-7680).max(MAX_CANVAS_PX * 2);
const size = z.number().min(1).max(MAX_CANVAS_PX * 2);
/** Short element id, unique within the document (the editor makes them). */
const ElementIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,24}$/);
/**
* Show a Data Feed cell instead of the element's own text or image. `row` is 0-based; when the feed cycles
* (`CompositionFeed.cycleMs`), `row` is an offset from the current row, so row 0 and row 1 show "this" and "next".
*/
const FeedBindingSchema = z.object({
	feedId: IdSchema,
	column: z.string().min(1).max(40),
	row: z.int().min(0).max(999).default(0)
});
const base = {
	id: ElementIdSchema,
	/** Shown in the Layers list; blank = a name from the kind. */
	name: z.string().trim().max(60).default(""),
	x: px,
	y: px,
	width: size,
	height: size,
	/** Degrees, clockwise, around the element's centre. */
	rotation: z.number().min(-360).max(360).default(0),
	opacity: z.number().min(0).max(1).default(1),
	/** Only admins can change or remove a locked element (a brand logo, the layout); editors fill in the rest. */
	locked: z.boolean().default(false),
	hidden: z.boolean().default(false)
};
const FONT_FAMILIES = [
	"sans",
	"serif",
	"mono",
	"display"
];
const textStyle = {
	/** An organization font (Fonts); null = `fontFamily`. */
	fontId: IdSchema.nullable().default(null),
	fontFamily: z.enum(FONT_FAMILIES).default("sans"),
	/** Canvas pixels. */
	fontSize: z.number().min(4).max(2e3).default(64),
	bold: z.boolean().default(false),
	italic: z.boolean().default(false),
	color: HexColorSchema.default("#FFFFFF"),
	align: z.enum([
		"left",
		"center",
		"right"
	]).default("center"),
	valign: z.enum([
		"top",
		"middle",
		"bottom"
	]).default("middle"),
	lineHeight: z.number().min(.6).max(4).default(1.2),
	/** Box fill behind the text; null = transparent. */
	background: HexColorSchema.nullable().default(null),
	direction: z.enum([
		"auto",
		"ltr",
		"rtl"
	]).default("auto")
};
const STICKERS = [
	"star",
	"heart",
	"check",
	"arrow",
	"burst",
	"badge",
	"tag",
	"bubble"
];
const StickerSchema = z.enum(STICKERS);
const CompositionElementSchema = z.discriminatedUnion("kind", [
	z.object({
		...base,
		kind: z.literal("text"),
		text: z.string().max(COMPOSITION_LIMITS.textChars).default(""),
		...textStyle,
		feed: FeedBindingSchema.nullable().default(null)
	}),
	z.object({
		...base,
		kind: z.literal("shape"),
		shape: z.enum([
			"rect",
			"ellipse",
			"line"
		]),
		fill: HexColorSchema.nullable().default("#1D4ED8"),
		stroke: HexColorSchema.nullable().default(null),
		strokeWidth: z.number().min(0).max(200).default(0),
		/** Rounded corners (rect), canvas pixels. */
		radius: z.number().min(0).max(MAX_CANVAS_PX).default(0)
	}),
	z.object({
		...base,
		kind: z.literal("drawing"),
		/**
		* Freehand pen strokes. Each stroke is a flat list of points x0, y0, x1, y1… as fractions of the element's box
		* (0..1), so moving or resizing the element moves or stretches the drawing with it.
		*/
		strokes: z.array(z.array(z.number().min(0).max(1)).min(2).max(4e3)).min(1).max(50),
		color: HexColorSchema.default("#F43F5E"),
		/** Canvas pixels. */
		strokeWidth: z.number().min(1).max(200).default(8)
	}),
	z.object({
		...base,
		kind: z.literal("sticker"),
		sticker: StickerSchema,
		color: HexColorSchema.default("#FACC15"),
		label: z.string().max(20).default("")
	}),
	z.object({
		...base,
		kind: z.literal("image"),
		/** A READY image of the organization; null = a placeholder until one is chosen (or the feed supplies one). */
		assetId: IdSchema.nullable().default(null),
		fit: z.enum([
			"cover",
			"contain",
			"fill"
		]).default("cover"),
		radius: z.number().min(0).max(MAX_CANVAS_PX).default(0),
		/** An image column (a picture's address): shown when the player is online, else `assetId`. */
		feed: FeedBindingSchema.nullable().default(null)
	}),
	z.object({
		...base,
		kind: z.literal("video"),
		assetId: IdSchema,
		fit: z.enum([
			"cover",
			"contain",
			"fill"
		]).default("cover"),
		/** Always looped; sound is off unless asked (the screen's main audio usually comes from elsewhere). */
		muted: z.boolean().default(true)
	}),
	z.object({
		...base,
		kind: z.literal("clock"),
		/** Digital: the time as text (`format`). Analog: a clock face; `HH:mm:ss` adds a seconds hand. */
		style: z.enum(["digital", "analog"]).default("digital"),
		format: z.enum([
			"HH:mm",
			"HH:mm:ss",
			"h:mm a"
		]).default("HH:mm"),
		...textStyle
	}),
	z.object({
		...base,
		kind: z.literal("date"),
		format: z.enum([
			"long",
			"short",
			"weekday",
			"numeric"
		]).default("long"),
		locale: z.enum(["en", "ar"]).default("en"),
		...textStyle
	}),
	z.object({
		...base,
		kind: z.literal("countdown"),
		/** Counts down to this instant; afterwards shows `doneText`. */
		target: IsoDateTimeSchema,
		showSeconds: z.boolean().default(false),
		doneText: z.string().max(200).default(""),
		...textStyle
	}),
	z.object({
		...base,
		kind: z.literal("qr"),
		data: z.string().min(1).max(1e3),
		color: HexColorSchema.default("#000000"),
		background: HexColorSchema.default("#FFFFFF")
	}),
	z.object({
		...base,
		kind: z.literal("weather"),
		/** The playing screen's location (each screen of a group gets its own), or a fixed place. */
		location: z.discriminatedUnion("source", [z.object({ source: z.literal("screen") }), z.object({
			source: z.literal("fixed"),
			latitude: z.number().min(-90).max(90),
			longitude: z.number().min(-180).max(180)
		})]).default({ source: "screen" }),
		units: z.enum(["c", "f"]).default("c"),
		showIcon: z.boolean().default(true),
		...textStyle
	})
]);
/** A Data Feed the composition reads. `cycleMs`: step through its rows every N ms; null = fixed rows. */
const CompositionFeedSchema = z.object({
	feedId: IdSchema,
	cycleMs: z.int().min(MIN_ITEM_DURATION_MS).max(36e5).nullable().default(null)
});
const CompositionDocSchema = z.object({
	version: z.literal(1).default(1),
	width: z.int().min(16).max(MAX_CANVAS_PX),
	height: z.int().min(16).max(MAX_CANVAS_PX),
	background: HexColorSchema.default("#000000"),
	/** A READY image behind everything; null = the colour only. */
	backgroundAssetId: IdSchema.nullable().default(null),
	/** How long it stays on screen when placed in a list or campaign. */
	durationMs: z.int().min(MIN_ITEM_DURATION_MS).max(864e5).default(15e3),
	/** Bottom → top. */
	elements: z.array(CompositionElementSchema).max(COMPOSITION_LIMITS.elements),
	feeds: z.array(CompositionFeedSchema).max(COMPOSITION_LIMITS.feeds).default([])
}).superRefine((d, ctx) => {
	const ids = /* @__PURE__ */ new Set();
	d.elements.forEach((e, i) => {
		if (ids.has(e.id)) ctx.addIssue({
			code: "custom",
			message: "Duplicate element id",
			path: [
				"elements",
				i,
				"id"
			]
		});
		ids.add(e.id);
		const feed = "feed" in e ? e.feed : null;
		if (feed && !d.feeds.some((f) => f.feedId === feed.feedId)) ctx.addIssue({
			code: "custom",
			message: "The element reads a data feed the composition does not list",
			path: [
				"elements",
				i,
				"feed"
			]
		});
	});
	if (new Set(d.feeds.map((f) => f.feedId)).size !== d.feeds.length) ctx.addIssue({
		code: "custom",
		message: "Duplicate data feed",
		path: ["feeds"]
	});
});
/** Every library item (image / video / background) the document uses. */
function compositionAssetIds(doc) {
	const ids = /* @__PURE__ */ new Set();
	if (doc.backgroundAssetId) ids.add(doc.backgroundAssetId);
	for (const e of doc.elements) if ((e.kind === "image" || e.kind === "video") && e.assetId) ids.add(e.assetId);
	return [...ids];
}
/** Fonts the document uses. */
function compositionFontIds(doc) {
	return [...new Set(doc.elements.flatMap((e) => "fontId" in e && e.fontId ? [e.fontId] : []))];
}
/**
* Locked elements an editor (below ADMIN) changed, removed or added: they must come back exactly as they were.
* Returns the names of the offending elements (empty = allowed). Order changes are allowed.
*/
function lockedElementChanges(before, after) {
	const prev = new Map(before.elements.map((e) => [e.id, e]));
	const next = new Map(after.elements.map((e) => [e.id, e]));
	const bad = [];
	for (const [id, e] of prev) {
		if (!e.locked) continue;
		const now = next.get(id);
		if (!now || JSON.stringify(canon(now)) !== JSON.stringify(canon(e))) bad.push(elementLabel(e));
	}
	for (const [id, e] of next) if (e.locked && !prev.get(id)?.locked) bad.push(elementLabel(e));
	return bad;
}
function canon(v) {
	if (Array.isArray(v)) return v.map(canon);
	if (v && typeof v === "object") return Object.fromEntries(Object.keys(v).sort().map((k) => [k, canon(v[k])]));
	return v;
}
function elementLabel(e) {
	return e.name || e.kind;
}
/** On `MediaAssetRecord.composition`. */
const CompositionInfoSchema = z.object({
	doc: CompositionDocSchema,
	/** Size of what players download (the runtime + copies of the media). */
	bundleBytes: NonNegativeIntSchema
});
const CompositionTemplateSchema = z.object({
	/** Built-in starters have ids like `builtin:sale`. */
	id: z.string(),
	name: z.string(),
	builtIn: z.boolean(),
	doc: CompositionDocSchema,
	createdAt: IsoDateTimeSchema.nullable()
});
const UpsertCompositionTemplateRequestSchema = z.object({
	name: z.string().trim().min(1).max(120),
	doc: CompositionDocSchema
});
/** What the runtime gets in `settings` on each player (per screen: the compiler fills it). */
const CompositionRuntimeSettingsSchema = z.object({
	timezone: IanaTimeZoneSchema.nullable(),
	latitude: z.number().nullable(),
	longitude: z.number().nullable()
});
const t = (id, name, doc) => ({
	id: `builtin:${id}`,
	name,
	builtIn: true,
	doc: CompositionDocSchema.parse(doc),
	createdAt: null
});
const BUILT_IN_COMPOSITION_TEMPLATES = [
	t("blank-landscape", "Blank — landscape", {
		width: 1920,
		height: 1080,
		background: "#0F172A",
		elements: []
	}),
	t("blank-portrait", "Blank — portrait", {
		width: 1080,
		height: 1920,
		background: "#0F172A",
		elements: []
	}),
	t("sale", "Sale", {
		width: 1920,
		height: 1080,
		background: "#B91C1C",
		elements: [
			{
				id: "burst",
				kind: "sticker",
				sticker: "burst",
				color: "#FACC15",
				x: 1240,
				y: 140,
				width: 560,
				height: 560,
				rotation: 12,
				label: "50%"
			},
			{
				id: "title",
				kind: "text",
				text: "BIG SALE",
				x: 120,
				y: 260,
				width: 1100,
				height: 260,
				fontSize: 220,
				bold: true,
				align: "left",
				fontFamily: "display"
			},
			{
				id: "sub",
				kind: "text",
				text: "This weekend only",
				x: 130,
				y: 540,
				width: 1e3,
				height: 120,
				fontSize: 80,
				align: "left",
				color: "#FEE2E2"
			},
			{
				id: "bar",
				kind: "shape",
				shape: "rect",
				fill: "#7F1D1D",
				x: 0,
				y: 900,
				width: 1920,
				height: 180,
				locked: true
			},
			{
				id: "foot",
				kind: "text",
				text: "Ask our staff for details",
				x: 120,
				y: 930,
				width: 1300,
				height: 120,
				fontSize: 60,
				align: "left",
				locked: false
			},
			{
				id: "clock",
				kind: "clock",
				x: 1500,
				y: 930,
				width: 300,
				height: 120,
				fontSize: 72,
				align: "right"
			}
		]
	}),
	t("welcome", "Welcome", {
		width: 1920,
		height: 1080,
		background: "#0F387A",
		elements: [
			{
				id: "title",
				kind: "text",
				text: "Welcome",
				x: 160,
				y: 300,
				width: 1600,
				height: 260,
				fontSize: 200,
				bold: true
			},
			{
				id: "date",
				kind: "date",
				x: 360,
				y: 600,
				width: 1200,
				height: 100,
				fontSize: 64,
				color: "#BFDBFE"
			},
			{
				id: "clock",
				kind: "clock",
				x: 660,
				y: 720,
				width: 600,
				height: 140,
				fontSize: 110
			},
			{
				id: "line",
				kind: "shape",
				shape: "line",
				fill: null,
				stroke: "#60A5FA",
				strokeWidth: 6,
				x: 660,
				y: 580,
				width: 600,
				height: 6
			}
		]
	}),
	t("menu", "Menu board (Data Feed)", {
		width: 1920,
		height: 1080,
		background: "#1C1917",
		elements: [
			{
				id: "title",
				kind: "text",
				text: "Today’s specials",
				x: 100,
				y: 60,
				width: 1720,
				height: 160,
				fontSize: 120,
				bold: true,
				color: "#FBBF24",
				fontFamily: "serif"
			},
			{
				id: "card",
				kind: "shape",
				shape: "rect",
				fill: "#292524",
				radius: 32,
				x: 100,
				y: 260,
				width: 1720,
				height: 700
			},
			{
				id: "name",
				kind: "text",
				text: "Item name",
				x: 160,
				y: 320,
				width: 1100,
				height: 160,
				fontSize: 96,
				align: "left",
				bold: true
			},
			{
				id: "desc",
				kind: "text",
				text: "Description",
				x: 160,
				y: 500,
				width: 1100,
				height: 300,
				fontSize: 56,
				align: "left",
				valign: "top",
				color: "#D6D3D1"
			},
			{
				id: "price",
				kind: "text",
				text: "0.00",
				x: 1300,
				y: 320,
				width: 460,
				height: 200,
				fontSize: 140,
				bold: true,
				color: "#FBBF24",
				align: "right"
			}
		]
	}),
	t("portrait-promo", "Promo — portrait", {
		width: 1080,
		height: 1920,
		background: "#111827",
		elements: [
			{
				id: "img",
				kind: "image",
				assetId: null,
				x: 0,
				y: 0,
				width: 1080,
				height: 1100
			},
			{
				id: "tag",
				kind: "sticker",
				sticker: "tag",
				color: "#22C55E",
				x: 680,
				y: 960,
				width: 340,
				height: 260,
				label: "NEW"
			},
			{
				id: "title",
				kind: "text",
				text: "Your product",
				x: 80,
				y: 1200,
				width: 920,
				height: 200,
				fontSize: 120,
				bold: true,
				align: "left"
			},
			{
				id: "sub",
				kind: "text",
				text: "A short line about it",
				x: 80,
				y: 1420,
				width: 920,
				height: 160,
				fontSize: 64,
				align: "left",
				valign: "top",
				color: "#9CA3AF"
			},
			{
				id: "qr",
				kind: "qr",
				data: "https://example.com",
				x: 780,
				y: 1640,
				width: 220,
				height: 220
			}
		]
	})
];
/**
* A QR code as SVG from its module grid (the API and the CMS both use qrcode-generator for the grid): one path, the
* element's colours, a quiet zone of 2 modules. Same output in the editor and on players.
*/
function qrMatrixSvg(count, isDark, color, background) {
	const q = 2;
	const n = count + 4;
	let d = "";
	for (let r = 0; r < count; r++) for (let c = 0; c < count; c++) if (isDark(r, c)) d += `M${c + q} ${r + q}h1v1h-1z`;
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges"><rect width="${n}" height="${n}" fill="${background}"/><path d="${d}" fill="${color}"/></svg>`;
}
//#endregion
//#region src/content.ts
/**
* Ad-network pack: a building (site) is a player group with these details. Its players are the group's members;
* building owners, ads history and live preview work per building.
*/
const BuildingInfoSchema = z.object({
	address: z.string().trim().max(300).default(""),
	/** Map pin (both or neither). */
	latitude: LatitudeSchema.nullable().default(null),
	longitude: LongitudeSchema.nullable().default(null),
	contactName: z.string().trim().max(120).default(""),
	contactEmail: z.union([z.literal(""), z.email().max(200)]).default(""),
	contactPhone: z.string().trim().max(40).default(""),
	notes: z.string().trim().max(2e3).default("")
});
const ScreenGroupSchema = z.object({
	id: IdSchema,
	name: z.string(),
	screenCount: NonNegativeIntSchema,
	createdAt: IsoDateTimeSchema,
	/** Phase 16 (additive). */
	tags: z.array(z.string()).default([]),
	/** Phase 16: items in "Content in Group". */
	contentCount: NonNegativeIntSchema.default(0),
	/**
	* Phase 17.7 Synchronized Playback: every member plays the lead player's loop, switching content at the same
	* moments (each player places itself in the loop by the clock).
	*/
	sync: z.object({
		enabled: z.boolean(),
		leadScreenId: IdSchema.nullable()
	}).default({
		enabled: false,
		leadScreenId: null
	}),
	/** Ad-network pack: set when this group is a building. */
	building: BuildingInfoSchema.nullable().default(null),
	/** Player Settings for its players, between the organization defaults and each player's own (see inheritedSettings). */
	settings: PlayerSettingsOverridesSchema.default({})
});
const UpsertScreenGroupRequestSchema = z.object({
	name: z.string().trim().min(1).max(120),
	/** Omitted: unchanged (a new group: not a building). null: an ordinary group. */
	building: BuildingInfoSchema.nullable().optional()
});
/** Phase 17.7: turn Synchronized Playback on (a lead player, one of the members, is required) or off. */
const GroupSyncRequestSchema = z.object({
	enabled: z.boolean(),
	leadScreenId: IdSchema.nullable()
}).refine((b) => !b.enabled || b.leadScreenId !== null, {
	message: "Choose the lead player",
	path: ["leadScreenId"]
});
/**
* PATCH semantics: omitted = unchanged. `groupIds` is the player's full, ordered group list (Phase 16 Step 6:
* a player can be in several groups; [] removes it from all). Replaces `groupId`.
*/
const UpdateScreenRequestSchema = z.object({
	name: z.string().trim().min(1).max(200).optional(),
	timezone: IanaTimeZoneSchema.optional(),
	latitude: LatitudeSchema.nullable().optional(),
	longitude: LongitudeSchema.nullable().optional(),
	/**
	* Phase 17.7: true = stop using the hand-set location and follow the player again (its device position, else an
	* estimate from its internet address). Don't send with latitude/longitude.
	*/
	locationAuto: z.literal(true).optional(),
	groupIds: z.array(IdSchema).max(50).refine((ids) => new Set(ids).size === ids.length, "Duplicate group").optional()
}).refine((b) => b.latitude === void 0 === (b.longitude === void 0), {
	message: "Send latitude and longitude together",
	path: ["longitude"]
}).refine((b) => b.latitude === null === (b.longitude === null), {
	message: "Clear latitude and longitude together",
	path: ["longitude"]
});
const assetName = z.string().trim().min(1).max(200);
/** Create inside this folder (Phase 16 Step 6); omitted/null = top level. */
const createIn = { folderId: IdSchema.nullable().optional() };
const CreateMediaAssetRequestSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("video"),
		name: assetName,
		url: HttpUrlSchema,
		sha256: Sha256HexSchema,
		sizeBytes: PositiveIntSchema,
		mimeType: z.enum(["video/mp4", "video/webm"]),
		codec: VideoCodecSchema,
		width: PositiveIntSchema,
		height: PositiveIntSchema,
		durationMs: PositiveIntSchema,
		...createIn
	}),
	z.object({
		kind: z.literal("image"),
		name: assetName,
		url: HttpUrlSchema,
		sha256: Sha256HexSchema,
		sizeBytes: PositiveIntSchema,
		mimeType: z.enum([
			"image/jpeg",
			"image/png",
			"image/webp"
		]),
		width: PositiveIntSchema,
		height: PositiveIntSchema,
		...createIn
	}),
	z.object({
		kind: z.literal("widget"),
		name: assetName,
		config: WidgetConfigSchema,
		...createIn
	}),
	z.object({
		kind: z.literal("app"),
		name: assetName,
		app: AppInstanceConfigSchema,
		...createIn
	}),
	z.object({
		kind: z.literal("composition"),
		name: assetName,
		composition: CompositionDocSchema,
		...createIn
	})
]);
/** Rename any asset; `config` only for widgets, `app` only for custom-app items (files are immutable: pinned by sha256). */
const UpdateMediaAssetRequestSchema = z.object({
	name: assetName.optional(),
	config: WidgetConfigSchema.optional(),
	app: AppInstanceConfigSchema.optional(),
	/** Compositions: the whole design (locked elements: ADMIN+ only). */
	composition: CompositionDocSchema.optional(),
	/** PDFs: seconds each page stays on screen (as ms). */
	pdfPageMs: z.int().min(MIN_ITEM_DURATION_MS).max(36e5).optional(),
	/** PDFs: how pages turn. */
	pdfPageTransition: PdfPageTransitionSchema.optional()
}).refine((b) => b.name !== void 0 || b.config !== void 0 || b.app !== void 0 || b.composition !== void 0 || b.pdfPageMs !== void 0 || b.pdfPageTransition !== void 0, "Nothing to update");
const MediaAssetRecordSchema = z.object({
	/** Phase 17.5: content-wide restriction (null = none); every player it plays on also applies its own. */
	restriction: RestrictionSchema.nullable().default(null),
	id: IdSchema,
	name: z.string(),
	kind: MediaKindSchema,
	/** Phase 6: only READY assets can be placed in layouts (and therefore reach a manifest). Widgets are always READY. */
	status: MediaStatusSchema,
	/** Uploaded file name (display only — never used as a storage path). null for widgets / URL registrations. */
	originalName: z.string().nullable(),
	/** Normalized, content-addressed file (files only; null until READY). Renamed from Phase 5 `url`. */
	fileUrl: z.string().nullable(),
	/** Small WebP preview for the CMS (files only; null until READY). */
	thumbnailUrl: z.string().nullable(),
	/** Mute switch: plays without sound everywhere. */
	muted: z.boolean().default(false),
	/** 0–100 while PROCESSING, else null. */
	progress: z.int().min(0).max(100).nullable(),
	/** Human-readable reason when FAILED. */
	processingError: z.string().nullable(),
	sizeBytes: NonNegativeIntSchema.nullable(),
	mimeType: z.string().nullable(),
	width: PositiveIntSchema.nullable(),
	height: PositiveIntSchema.nullable(),
	durationMs: PositiveIntSchema.nullable(),
	/** Smaller versions made for smaller screens (videos and images; empty when the original is small already). */
	versions: z.array(MediaVersionSchema).default([]),
	/** PDFs only: page images (in order) and how long each stays on screen. */
	pdf: z.object({
		pageMs: z.int().min(MIN_ITEM_DURATION_MS),
		pageTransition: PdfPageTransitionSchema,
		pages: z.array(z.object({
			url: z.string(),
			width: PositiveIntSchema,
			height: PositiveIntSchema
		}))
	}).nullable().default(null),
	/** Widgets only. */
	config: WidgetConfigSchema.nullable(),
	/** Phase 16 Step 15d: custom-app items only. */
	app: AppInstanceConfigSchema.nullable().default(null),
	/** Compositions only. */
	composition: CompositionInfoSchema.nullable().default(null),
	/** Phase 16 Step 6: library folder (null = top level) and organising tags. */
	folderId: IdSchema.nullable(),
	tags: z.array(z.string()),
	/** Phase 16 Step 15b: custom attribute values. */
	attributes: AttributeValuesSchema.default({}),
	createdAt: IsoDateTimeSchema
});
const LayoutZoneItemInputSchema = z.object({
	assetId: IdSchema,
	durationMs: z.int().min(MIN_ITEM_DURATION_MS).nullable(),
	/** How the item enters its section (Phase 17.7 players animate it; video → video and app → app cut). */
	transition: TransitionSchema.default({
		type: "cut",
		durationMs: 0
	}),
	/** How the item fills its section; null = the campaign's fit. */
	fit: FitSchema.nullable().default(null)
});
const LayoutZoneInputSchema = z.object({
	name: z.string().trim().min(1).max(80),
	x: z.int().min(0).max(MAX_CANVAS_PX),
	y: z.int().min(0).max(MAX_CANVAS_PX),
	width: z.int().min(1).max(MAX_CANVAS_PX),
	height: z.int().min(1).max(MAX_CANVAS_PX),
	zIndex: z.int().min(0).max(100),
	/** Linear timeline for this zone. May be empty while drafting; a scheduled layout needs ≥1 per zone. */
	items: z.array(LayoutZoneItemInputSchema).max(500),
	/** Off: when its content ends before the campaign does, the section stays black until the next pass. */
	loop: z.boolean().default(true)
});
/** Phase 16 Step 15f: a campaign's Audio Track — one audio file or one audio playlist, looped. */
const CampaignAudioSchema = z.object({
	source: z.object({
		kind: z.enum(["media", "playlist"]),
		id: IdSchema
	}),
	/** Silence the zones' video sound while the track plays (OnSign / legacy default). */
	muteVideo: z.boolean().default(true)
});
const UpsertLayoutRequestSchema = z.object({
	name: z.string().trim().min(1).max(120),
	canvasWidth: CanvasSchema.shape.width,
	canvasHeight: CanvasSchema.shape.height,
	/** Scene: time on screen when a schedule plays several layouts in sequence. */
	durationMs: LayoutSceneSchema.shape.durationMs.default(DEFAULT_LAYOUT_DURATION_MS),
	/** Scene: how this layout enters the screen (the whole screen moves). */
	transition: TransitionSchema.default({
		type: "cut",
		durationMs: 0
	}),
	zones: z.array(LayoutZoneInputSchema).min(1).max(12),
	/** Create inside this folder; ignored on update (moving is POST content/move). */
	folderId: IdSchema.nullable().optional(),
	/** Phase 16 Step 15f: null = no Audio Track. */
	audio: CampaignAudioSchema.nullable().default(null),
	/** Mute switch: no sound from this campaign on any player (videos, apps and its Audio Track). */
	muted: z.boolean().default(false),
	/** How media fills its sections (each item may override); null = the player's Display Settings. */
	fit: FitSchema.nullable().default(null)
}).superRefine((l, ctx) => {
	const names = /* @__PURE__ */ new Set();
	l.zones.forEach((z, i) => {
		if (z.x + z.width > l.canvasWidth || z.y + z.height > l.canvasHeight) ctx.addIssue({
			code: "custom",
			message: `Zone “${z.name}” extends outside the ${l.canvasWidth}×${l.canvasHeight} canvas`,
			path: ["zones", i]
		});
		if (names.has(z.name.toLowerCase())) ctx.addIssue({
			code: "custom",
			message: `Duplicate zone name “${z.name}”`,
			path: [
				"zones",
				i,
				"name"
			]
		});
		names.add(z.name.toLowerCase());
	});
});
const LayoutSchema = z.object({
	/** Phase 17.5: content-wide restriction (null = none). */
	restriction: RestrictionSchema.nullable().default(null),
	id: IdSchema,
	name: z.string(),
	canvasWidth: PositiveIntSchema,
	canvasHeight: PositiveIntSchema,
	durationMs: PositiveIntSchema,
	transition: TransitionSchema,
	zones: z.array(LayoutZoneInputSchema.extend({
		id: IdSchema,
		items: z.array(LayoutZoneItemInputSchema.extend({ id: IdSchema }))
	})),
	/** Phase 16 Step 15f: the Audio Track (with the source's name for display). */
	audio: CampaignAudioSchema.extend({ name: z.string() }).nullable().default(null),
	/** Mute switch (see UpsertLayoutRequestSchema). */
	muted: z.boolean().default(false),
	/** null = the player's Display Settings. */
	fit: FitSchema.nullable().default(null),
	/** Schedules (screen- or group-level) that play this layout. Non-zero → cannot be deleted. */
	scheduleCount: NonNegativeIntSchema,
	folderId: IdSchema.nullable(),
	tags: z.array(z.string()),
	/** Phase 16 Step 15b: custom attribute values. */
	attributes: AttributeValuesSchema.default({}),
	updatedAt: IsoDateTimeSchema
});
/**
* A reusable screen layout ("Layout" in legacy CODOT): named sections on a canvas, picked when building a
* campaign. Choosing one COPIES its sections into the campaign (zones); editing or deleting the template
* later never changes existing campaigns. Built-in layouts (Full screen, Layout 1–5…) live in the CMS.
*/
const LayoutSectionSchema = z.object({
	name: z.string().trim().min(1).max(80),
	x: z.int().min(0).max(MAX_CANVAS_PX),
	y: z.int().min(0).max(MAX_CANVAS_PX),
	width: z.int().min(1).max(MAX_CANVAS_PX),
	height: z.int().min(1).max(MAX_CANVAS_PX)
});
const UpsertLayoutTemplateRequestSchema = z.object({
	name: z.string().trim().min(1).max(120),
	canvasWidth: CanvasSchema.shape.width,
	canvasHeight: CanvasSchema.shape.height,
	/** Bottom → top (later sections draw over earlier ones). */
	sections: z.array(LayoutSectionSchema).min(1).max(12)
}).superRefine((t, ctx) => {
	t.sections.forEach((s, i) => {
		if (s.x + s.width > t.canvasWidth || s.y + s.height > t.canvasHeight) ctx.addIssue({
			code: "custom",
			message: `Section “${s.name}” extends outside the ${t.canvasWidth}×${t.canvasHeight} canvas`,
			path: ["sections", i]
		});
	});
});
const LayoutTemplateSchema = z.object({
	id: IdSchema,
	name: z.string(),
	canvasWidth: PositiveIntSchema,
	canvasHeight: PositiveIntSchema,
	sections: z.array(LayoutSectionSchema),
	updatedAt: IsoDateTimeSchema
});
/**
* Same decoder-budget check the compiler runs — lets the CMS flag violations before saving.
* `kindOf` resolves an assetId to its media kind (unknown ids count as images).
*/
function checkLayoutDecoderBudget(zones, kindOf) {
	return analyzeDecoderBudget(zones.map((z, i) => ({
		zoneId: `zone-${i}`,
		name: z.name,
		width: z.width,
		height: z.height,
		items: z.items.map((it) => ({ kind: kindOf(it.assetId) ?? "image" }))
	}))).violations;
}
const CompileIssueSchema = z.object({
	screenId: IdSchema.nullable(),
	screenName: z.string().nullable(),
	layoutId: IdSchema.nullable(),
	code: z.string(),
	message: z.string()
});
const CompileResultSchema = z.object({ screens: z.array(z.object({
	screenId: IdSchema,
	screenName: z.string(),
	/** `unchanged` = compiled content identical to the live revision → nothing re-published. */
	status: z.enum(["published", "unchanged"]),
	revision: PositiveIntSchema.nullable(),
	/** Published anyway, but worth knowing (e.g. a campaign plays more videos at once than this player can). */
	warnings: z.array(CompileIssueSchema).optional()
})) });
/** 422 body when a publish is rejected (nothing is published for ANY screen). */
const CompileFailureSchema = z.object({
	statusCode: z.literal(422),
	error: z.string(),
	code: z.literal("MANIFEST_COMPILE_FAILED"),
	message: z.string(),
	issues: z.array(CompileIssueSchema).min(1)
});
/**
* Health Check › fix broken content: `to` takes `from`'s place in every campaign section, Audio Track, playlist,
* player/group publication and tag publication, keeping each place's own settings (duration, transition, fit…).
* `only`: limit to these rows — how Undo puts back exactly what a replace changed.
*/
const ContentReplaceRowsSchema = z.object({
	zoneItemIds: z.array(IdSchema).max(5e3),
	audioLayoutIds: z.array(IdSchema).max(5e3),
	playlistItemIds: z.array(IdSchema).max(5e3),
	placementIds: z.array(IdSchema).max(5e3),
	tagPublicationIds: z.array(IdSchema).max(5e3)
});
const ContentReplaceRequestSchema = z.object({
	fromAssetId: IdSchema,
	toAssetId: IdSchema,
	only: ContentReplaceRowsSchema.optional()
}).refine((r) => r.fromAssetId !== r.toAssetId, {
	message: "Pick a different item",
	path: ["toAssetId"]
});
const ContentReplaceResultSchema = z.object({
	changed: ContentReplaceRowsSchema,
	places: z.number().int().nonnegative(),
	players: z.number().int().nonnegative()
});
const ContentDetachRequestSchema = z.object({ assetId: IdSchema });
const ContentDetachResultSchema = z.object({ players: z.number().int().nonnegative() });
//#endregion
//#region src/observability.ts
/** Player-side sampling cadence for resource metrics. */
const DEVICE_METRICS_INTERVAL_MS = 6e4;
/** Rolling retention windows (daily partitions older than this are dropped). */
const METRICS_RETENTION_DAYS = 14;
const LOGS_RETENTION_DAYS = 30;
const MAX_POP_EVENTS_PER_BATCH = 500;
const MAX_METRIC_SAMPLES_PER_BATCH = 720;
/** 50 × (2 000 + 8 000 chars) stays well under the API's 1 MiB body limit. */
const MAX_LOGS_PER_BATCH = 50;
const MAX_LOG_MESSAGE_CHARS = 2e3;
const MAX_LOG_STACK_CHARS = 8e3;
/** Items stamped further in the future than this (device clock skew) are rejected. */
const MAX_FUTURE_SKEW_MS = 864e5;
const uniqueBy = (items, key) => new Set(items.map(key)).size === items.length;
const ProofOfPlayEventSchema = z.object({
	/** Device-generated UUID — the dedupe key (ON CONFLICT DO NOTHING). */
	eventId: IdSchema,
	assetId: IdSchema,
	scheduleId: IdSchema,
	layoutId: IdSchema,
	zoneId: IdSchema,
	/** Manifest revision that was playing (audits: "which content version ran"). */
	manifestRevision: PositiveIntSchema,
	/** Device clock when the item STARTED on screen. */
	playedAt: IsoDateTimeSchema,
	/** How long it was actually visible (≤ 24 h). */
	durationMs: z.int().min(1).max(864e5)
});
/** `POST /api/v1/player/proof-of-play` */
const ProofOfPlayBatchSchema = z.object({
	screenId: IdSchema,
	events: z.array(ProofOfPlayEventSchema).min(1).max(500)
}).refine((b) => uniqueBy(b.events, (e) => e.eventId), {
	message: "eventId must be unique within a batch",
	path: ["events"]
});
/** Shared ack shape for the three diagnostic batches. The player deletes every item it sent on 2xx. */
const TelemetryIngestAckSchema = z.object({
	/** Newly stored. */
	accepted: NonNegativeIntSchema,
	/** Already stored by an earlier (retried) upload. */
	duplicates: NonNegativeIntSchema,
	/** Unusable (e.g. timestamp too far in the future); dropped, never retry them. */
	rejected: NonNegativeIntSchema,
	serverTime: IsoDateTimeSchema
});
const Percent = z.number().min(0).max(100);
const DeviceMetricSampleSchema = z.object({
	/** Device clock; together with the screen it is the dedupe key. */
	capturedAt: IsoDateTimeSchema,
	cpuPercent: Percent,
	memoryPercent: Percent,
	/** Of the partition holding app-specific storage (the media cache). */
	diskPercent: Percent,
	/** HTTPS round trip to the API. null = no connectivity at sample time (plotted as offline). */
	pingMs: z.int().min(0).max(6e4).nullable(),
	networkType: NetworkTransportSchema.optional()
});
/** `POST /api/v1/player/metrics` */
const DeviceMetricsBatchSchema = z.object({
	screenId: IdSchema,
	samples: z.array(DeviceMetricSampleSchema).min(1).max(720)
}).refine((b) => uniqueBy(b.samples, (s) => String(Date.parse(s.capturedAt))), {
	message: "capturedAt must be unique within a batch",
	path: ["samples"]
});
const DEVICE_LOG_LEVELS = ["WARN", "ERROR"];
const DeviceLogLevelSchema = z.enum(DEVICE_LOG_LEVELS);
const DeviceLogItemSchema = z.object({
	/** Device-generated UUID — the dedupe key. */
	logId: IdSchema,
	timestamp: IsoDateTimeSchema,
	level: DeviceLogLevelSchema,
	/** Android log tag / component, e.g. "ExoPlayer", "AssetDownloader". */
	tag: z.string().min(1).max(64),
	message: z.string().min(1).max(MAX_LOG_MESSAGE_CHARS),
	stackTrace: z.string().max(MAX_LOG_STACK_CHARS).nullable()
});
/** `POST /api/v1/player/logs` */
const DeviceLogsBatchSchema = z.object({
	screenId: IdSchema,
	logs: z.array(DeviceLogItemSchema).min(1).max(50)
}).refine((b) => uniqueBy(b.logs, (l) => l.logId), {
	message: "logId must be unique within a batch",
	path: ["logs"]
});
const PAGE_SIZE_DEFAULT = 25;
const PAGE_SIZE_MAX = 100;
const limitParam = z.coerce.number().int().min(1).max(100).default(25);
/** Opaque keyset cursor (never an offset: tables are large and append-only). */
const cursorParam = z.string().min(1).max(200).optional();
/**
* How the item came to play: through a schedule on this screen, a schedule on its group, or a schedule
* that has since been deleted (the play still happened and still counts).
*/
/** How the played item reached the player (Phase 17.1: from its placement): published to it, through a group, through a tag, or since removed. */
const PlaybackTypeSchema = z.enum([
	"player",
	"group",
	"tag",
	"removed"
]);
const ScreenActivityQuerySchema = z.object({
	cursor: cursorParam,
	limit: limitParam,
	from: IsoDateTimeSchema.optional(),
	to: IsoDateTimeSchema.optional(),
	/**
	* Numbered pages (1-based) instead of the cursor: the response then carries `total` (capped at
	* ACTIVITY_COUNT_CAP). Bounded by from/to, so the offset stays within a few partitions.
	*/
	page: z.coerce.number().int().min(1).max(1e4).optional(),
	/** Search the item and layout names (case-insensitive). */
	q: z.string().trim().max(100).optional(),
	type: PlaybackTypeSchema.optional(),
	kind: MediaKindSchema.optional()
}).refine((q) => !q.from || !q.to || Date.parse(q.from) < Date.parse(q.to), {
	message: "from must be before to",
	path: ["to"]
});
const ScreenActivityItemSchema = z.object({
	eventId: IdSchema,
	playedAt: IsoDateTimeSchema,
	durationMs: PositiveIntSchema,
	assetId: IdSchema,
	/** null when the asset has since been deleted. */
	assetName: z.string().nullable(),
	assetKind: MediaKindSchema.nullable(),
	/** Small picture of the item (videos, images, PDFs, compositions); null for apps and deleted items. */
	assetThumbnailUrl: z.string().nullable().optional(),
	/** Built-in apps: which app (clock, weather…), so the CMS shows its own icon. */
	appType: z.string().nullable().optional(),
	playbackType: PlaybackTypeSchema,
	scheduleId: IdSchema.nullable(),
	layoutId: IdSchema.nullable(),
	layoutName: z.string().nullable(),
	zoneId: IdSchema.nullable(),
	manifestRevision: PositiveIntSchema
});
const ScreenActivityPageSchema = z.object({
	items: z.array(ScreenActivityItemSchema),
	/** Pass back as `cursor` for older items; null = no more. */
	nextCursor: z.string().nullable(),
	/** With `page`: how many plays match, up to ACTIVITY_COUNT_CAP (then `totalCapped`). */
	total: z.number().int().nonnegative().optional(),
	totalCapped: z.boolean().optional()
});
/** Counting stops here: beyond it the CMS shows "10,000+" and narrower filters are the way in. */
const ACTIVITY_COUNT_CAP = 1e4;
const METRICS_RANGES = {
	"24h": {
		durationMs: 864e5,
		bucketSeconds: 300
	},
	"7d": {
		durationMs: 6048e5,
		bucketSeconds: 3600
	}
};
const MetricsRangeSchema = z.enum(["24h", "7d"]);
const ScreenMetricsQuerySchema = z.object({ range: MetricsRangeSchema.default("24h") });
/** One time bucket. Aggregates are null when the bucket has no samples (device off → a gap, not a zero). */
const MetricBucketSchema = z.object({
	/** Bucket start. */
	t: IsoDateTimeSchema,
	samples: NonNegativeIntSchema,
	cpuAvg: z.number().nullable(),
	cpuMax: z.number().nullable(),
	memoryAvg: z.number().nullable(),
	memoryMax: z.number().nullable(),
	diskMax: z.number().nullable(),
	/** Over samples that HAD connectivity. */
	pingAvg: z.number().nullable(),
	pingMax: z.number().nullable(),
	/** Samples with pingMs = null (no connectivity). */
	offlineSamples: NonNegativeIntSchema
});
const ScreenMetricsSchema = z.object({
	range: MetricsRangeSchema,
	bucketSeconds: PositiveIntSchema,
	from: IsoDateTimeSchema,
	to: IsoDateTimeSchema,
	/** Every bucket in [from, to), oldest first — fixed length, so charts can draw gaps honestly. */
	buckets: z.array(MetricBucketSchema),
	/** Most recent sample in the retention window (for the meters). */
	latest: DeviceMetricSampleSchema.nullable()
});
const ScreenLogsQuerySchema = z.object({
	cursor: cursorParam,
	limit: limitParam,
	level: DeviceLogLevelSchema.optional()
});
const ScreenLogItemSchema = DeviceLogItemSchema.extend({ 
/** Server receive time (the device clock may be wrong). */
receivedAt: IsoDateTimeSchema });
const ScreenLogsPageSchema = z.object({
	items: z.array(ScreenLogItemSchema),
	nextCursor: z.string().nullable(),
	/** Badge counts over the last 24 h (independent of the level filter). */
	last24h: z.object({
		WARN: NonNegativeIntSchema,
		ERROR: NonNegativeIntSchema
	})
});
//#endregion
//#region src/realtime.ts
/** Under the API prefix so the same reverse-proxy / Vite rule (`/api`) covers HTTP and WebSocket. */
const SOCKET_PATH = `/${API_GLOBAL_PREFIX}/ws`;
const DEVICE_NAMESPACE = "/device";
const CMS_NAMESPACE = "/cms";
/** Event names (one table so server, CMS and player can never drift). */
const SocketEvents = {
	/** server → device (its screen room) and → CMS (tenant room): a new manifest revision is live. */
	MANIFEST_PUBLISHED: "MANIFEST_PUBLISHED",
	/** CMS → server (ack: SocketAck) · server → device: RemoteCommand */
	REMOTE_COMMAND: "REMOTE_COMMAND",
	/** device → server · server → the issuing CMS socket only */
	COMMAND_RESULT: "COMMAND_RESULT",
	/** CMS → server (ack) → device */
	WEBRTC_OFFER: "webrtc_offer",
	/** device → server (ack) → the CMS socket that sent the offer */
	WEBRTC_ANSWER: "webrtc_answer",
	/** both directions, routed by sessionId */
	ICE_CANDIDATE: "ice_candidate",
	/** both directions: tear the session down */
	WEBRTC_HANGUP: "webrtc_hangup",
	/** Superseded by SCREEN_PRESENCE_BATCH (kept so the name stays reserved). */
	SCREEN_PRESENCE: "SCREEN_PRESENCE",
	/** server → CMS (tenant room): connection changes, batched every couple of seconds (PresenceBatchEvent) */
	SCREEN_PRESENCE_BATCH: "SCREEN_PRESENCE_BATCH",
	/** CMS → server (ack: ScreenPresenceAck): is this screen's player connected right now? */
	PRESENCE_QUERY: "PRESENCE_QUERY"
};
/** Handshake/ack error codes. */
const SocketErrorCode = {
	UNAUTHORIZED: "UNAUTHORIZED",
	FORBIDDEN: "FORBIDDEN",
	NOT_FOUND: "NOT_FOUND",
	SCREEN_OFFLINE: "SCREEN_OFFLINE",
	INVALID_PAYLOAD: "INVALID_PAYLOAD",
	RATE_LIMITED: "RATE_LIMITED",
	UNKNOWN_SESSION: "UNKNOWN_SESSION"
};
/** Every client → server event that expects a reply uses an ack callback with this shape. */
const SocketAckSchema = z.discriminatedUnion("ok", [z.object({
	ok: z.literal(true),
	id: IdSchema.optional()
}), z.object({
	ok: z.literal(false),
	code: z.enum(Object.values(SocketErrorCode)),
	message: z.string()
})]);
const ManifestPublishedEventSchema = z.object({
	type: z.literal("MANIFEST_PUBLISHED"),
	screenId: IdSchema,
	/** Same pointer the heartbeat ack carries: the player verifies sha256 before switching. */
	pointer: ManifestPointerSchema,
	publishedAt: IsoDateTimeSchema
});
const ScreenPresenceEventSchema = z.object({
	screenId: IdSchema,
	connected: z.boolean(),
	at: IsoDateTimeSchema
});
/**
* Live connection status everywhere the CMS shows a player (the heartbeat's 3-minute rule stays the fallback):
* a dropped connection shows "Reconnecting" at once — players in lifts drop for a minute many times a day and
* keep playing — and "Offline" only once nothing has been heard for SCREEN_OFFLINE_AFTER_MS.
*/
const PresenceBatchEventSchema = z.object({ changes: z.array(ScreenPresenceEventSchema) });
const PresenceSnapshotSchema = z.object({
	/** Players with a live connection right now. */
	connected: z.array(IdSchema),
	/** Players whose connection dropped recently (and isn't back): when. */
	droppedAt: z.record(IdSchema, IsoDateTimeSchema),
	at: IsoDateTimeSchema
});
const PresenceQuerySchema = z.object({ screenId: IdSchema });
const ScreenPresenceAckSchema = z.union([z.object({
	ok: z.literal(true),
	connected: z.boolean()
}), SocketAckSchema]);
/** Phase 17.7: RESTART_APP restarts the player app only (works without Device Owner, unlike REBOOT). */
/** PRESS_RECENTS: the recent-apps list (root or Displaro's accessibility switch). PRESS_BACK / PRESS_HOME: the device's Back and Home buttons (anywhere with root; else Home always, Back inside Displaro). */
/** REQUEST_PERMISSION: allow one of the player's device permissions (`permission`): granted directly on rooted / kiosk-mode
*  players, otherwise Android's prompt is shown on the screen for someone there to accept; `all` = every missing one. */
const REMOTE_COMMAND_ACTIONS = [
	"REBOOT",
	"CLEAR_CACHE",
	"FORCE_SYNC",
	"CAPTURE_SCREENSHOT",
	"RESTART_APP",
	"PRESS_BACK",
	"PRESS_HOME",
	"PRESS_RECENTS",
	"REQUEST_PERMISSION"
];
const DEVICE_PERMISSION_KEYS = [
	"autoStart",
	"batteryUnrestricted",
	"installUpdates",
	"fullScreenCapture",
	"remoteTouch",
	"location",
	"notifications",
	"all"
];
const DevicePermissionKeySchema = z.enum(DEVICE_PERMISSION_KEYS);
const RemoteCommandActionSchema = z.enum(REMOTE_COMMAND_ACTIONS);
/** Disruptive actions need ADMIN; diagnostic ones EDITOR (enforced server-side on every command). */
const COMMAND_MIN_ROLE = {
	REBOOT: "ADMIN",
	CLEAR_CACHE: "ADMIN",
	FORCE_SYNC: "EDITOR",
	CAPTURE_SCREENSHOT: "EDITOR",
	RESTART_APP: "ADMIN",
	PRESS_BACK: "EDITOR",
	PRESS_HOME: "EDITOR",
	PRESS_RECENTS: "EDITOR",
	REQUEST_PERMISSION: "ADMIN"
};
/** Opening a live view of a screen. */
const LIVE_VIEW_MIN_ROLE = "EDITOR";
/**
* PRESS_HOME / PRESS_RECENTS: Displaro leaves the screen — come back by itself after `comeBackAfterMs`, or stay in the
* background (nothing pulls it back) until someone opens it. Absent: the player's normal behaviour.
*/
const RemoteLeaveSchema = z.object({
	stayInBackground: z.boolean(),
	comeBackAfterMs: z.number().int().min(1e3).max(36e5).optional()
});
/** CMS → server. The server never trusts anything else from the client: tenant and role come from the session. */
const RemoteCommandRequestSchema = z.object({
	screenId: IdSchema,
	action: RemoteCommandActionSchema,
	permission: DevicePermissionKeySchema.optional(),
	leave: RemoteLeaveSchema.optional()
}).refine((r) => r.action !== "REQUEST_PERMISSION" || r.permission !== void 0, {
	message: "Which permission?",
	path: ["permission"]
});
/** server → device. The player re-checks `screenId` against its own before acting. */
const RemoteCommandSchema = z.object({
	commandId: IdSchema,
	screenId: IdSchema,
	action: RemoteCommandActionSchema,
	issuedAt: IsoDateTimeSchema,
	/** For the player's local audit log. */
	issuedBy: z.object({ userId: IdSchema }),
	/** REQUEST_PERMISSION only. */
	permission: DevicePermissionKeySchema.optional(),
	/** PRESS_HOME / PRESS_RECENTS only. */
	leave: RemoteLeaveSchema.optional()
});
/** ~ 700 KB of base64 ≈ a 520 KB JPEG/WebP — well under the 1 MB socket frame limit. */
const MAX_SCREENSHOT_BASE64_CHARS = 7e5;
const CommandResultSchema = z.object({
	commandId: IdSchema,
	/** `accepted` = will act now (e.g. before a reboot); `completed`/`failed` are final. */
	status: z.enum([
		"accepted",
		"completed",
		"failed",
		"unsupported"
	]),
	message: z.string().max(500).optional(),
	/** CAPTURE_SCREENSHOT only; relayed to the requesting admin, never stored. */
	screenshot: z.object({
		mimeType: z.enum([
			"image/jpeg",
			"image/webp",
			"image/png"
		]),
		dataBase64: z.string().max(MAX_SCREENSHOT_BASE64_CHARS).regex(/^[A-Za-z0-9+/]+=*$/),
		width: PositiveIntSchema,
		height: PositiveIntSchema
	}).optional()
}).strict();
/** server → CMS: the result, enriched with what it refers to. */
const CommandResultEventSchema = CommandResultSchema.extend({
	screenId: IdSchema,
	action: RemoteCommandActionSchema
});
/** Fallback when no TURN server is configured: public STUN only (enough for most NATs, not symmetric NAT). */
const DEFAULT_ICE_SERVERS = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];
/** One ICE server; TURN entries carry short-lived credentials minted by the API (coturn use-auth-secret). */
const IceServerSchema = z.object({
	urls: z.array(z.string().min(1).max(300)).min(1).max(10),
	username: z.string().max(300).optional(),
	credential: z.string().max(300).optional()
});
const IceServersSchema = z.array(IceServerSchema).max(10);
/** A live view is torn down after this long even if nobody hangs up (bandwidth on cellular sites). */
const LIVE_VIEW_MAX_MS = 6e5;
const Sdp = (type) => z.object({
	type: z.literal(type),
	sdp: z.string().min(1).max(2e4)
});
/** CMS → server → device. `sessionId` is minted by the CMS; the server binds it to the sender. */
const WebRtcOfferSchema = z.object({
	sessionId: IdSchema,
	screenId: IdSchema,
	sdp: Sdp("offer")
});
/** device → server → the CMS socket that owns the session. */
const WebRtcAnswerSchema = z.object({
	sessionId: IdSchema,
	sdp: Sdp("answer")
});
/** Trickle ICE, both directions. `candidate: null` = end of candidates. */
const IceCandidateSchema = z.object({
	sessionId: IdSchema,
	candidate: z.object({
		candidate: z.string().max(1e3),
		sdpMid: z.string().max(64).nullable().optional(),
		sdpMLineIndex: z.int().min(0).max(64).nullable().optional(),
		usernameFragment: z.string().max(256).nullable().optional()
	}).nullable()
});
const WebRtcHangupSchema = z.object({
	sessionId: IdSchema,
	reason: z.string().max(200).optional()
});
/**
* The WebRTC data channel the CMS opens inside a live-view session (same peer connection, so only the person
* who opened the live view — EDITOR+ — can send input, and it never touches the server).
*/
const LIVE_INPUT_CHANNEL = "codot-input";
/** Positions are fractions of the player's window (0,0 = top-left; 1,1 = bottom-right): resolution-independent. */
const LivePointSchema = z.object({
	x: z.number().min(0).max(1),
	y: z.number().min(0).max(1)
});
/** A swipe longer than this is cut short by the player (a long hold is not a swipe). */
const LIVE_SWIPE_MAX_MS = 3e3;
const LiveInputSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("tap"),
	x: LivePointSchema.shape.x,
	y: LivePointSchema.shape.y
}), z.object({
	type: z.literal("swipe"),
	points: z.array(LivePointSchema.extend({ t: z.int().min(0).max(LIVE_SWIPE_MAX_MS) })).min(2).max(60)
})]);
//#endregion
//#region src/playlists.ts
/** What a playlist can hold: playable media (image, video, app) or a campaign. */
const PlaylistContentRefSchema = z.object({
	kind: z.enum(["media", "campaign"]),
	id: IdSchema
});
const PlaylistItemInputSchema = z.object({
	content: PlaylistContentRefSchema,
	/** null = default (playlist default for images/apps; own length for videos and campaigns). */
	durationMs: z.int().min(1e3).max(864e5).nullable().default(null),
	/** null = the playlist's default transition. */
	transition: TransitionSchema.nullable().default(null),
	/** null = the playlist's default fit. */
	fit: FitSchema.nullable().default(null),
	paused: z.boolean().default(false)
});
/** Saved as a whole from the editor (name, defaults and the full ordered item list). */
/** Phase 16 Step 15f (legacy "Playlist Type", C4): `audio` holds only audio files and plays on campaign Audio Tracks. */
const PlaylistTypeSchema = z.enum(["visual", "audio"]);
/**
* Phase 17.4 (legacy Playback Mode): Play All = every item each time the loop reaches the playlist; Cycle = the
* next `itemsPerPass` items each time, carrying on from there next time round.
*/
const PlaylistPlaybackModeSchema = z.enum(["all", "cycle"]);
/** Phase 17.4 (legacy Playback Order): the editor's order; grouped by type; a new random order every pass. */
const PlaylistPlaybackOrderSchema = z.enum([
	"manual",
	"byType",
	"shuffle"
]);
const MAX_ITEMS_PER_PASS = 100;
const UpsertPlaylistRequestSchema = z.object({
	name: z.string().trim().min(1).max(120),
	/** Fixed at creation (ignored on update). */
	type: PlaylistTypeSchema.default("visual"),
	defaultDurationMs: z.int().min(1e3).max(864e5).default(1e4),
	transition: TransitionSchema.default({
		type: "cut",
		durationMs: 0
	}),
	fit: FitSchema.default("fit"),
	playbackMode: PlaylistPlaybackModeSchema.default("all"),
	itemsPerPass: z.int().min(1).max(100).default(1),
	/** Audio playlists: manual or shuffle only. */
	playbackOrder: PlaylistPlaybackOrderSchema.default("manual"),
	items: z.array(PlaylistItemInputSchema).max(500),
	/** Background audio for a visual playlist (one audio file or audio playlist), playing through its items. */
	audio: CampaignAudioSchema.nullable().default(null),
	/** Mute switch: no sound from this playlist's items on any player (nor its background audio). */
	muted: z.boolean().default(false),
	/** Create inside this folder; ignored on update (moving is POST content/move). */
	folderId: IdSchema.nullable().optional()
});
const PlaylistItemSchema = z.object({
	id: IdSchema,
	content: PlaylistContentRefSchema,
	contentType: z.enum([
		"image",
		"video",
		"app",
		"campaign",
		"audio",
		"pdf"
	]),
	name: z.string(),
	thumbnailUrl: z.string().nullable(),
	/** Own length: a video's, a campaign's, a PDF's (pages × time per page); null for images and apps (they use the playlist default). */
	naturalMs: PositiveIntSchema.nullable(),
	durationMs: PositiveIntSchema.nullable(),
	/** What actually plays: override → own length → playlist default. */
	effectiveDurationMs: PositiveIntSchema,
	transition: TransitionSchema.nullable(),
	fit: FitSchema.nullable(),
	paused: z.boolean()
});
const PlaylistSchema = z.object({
	/** Phase 17.5: content-wide restriction (null = none). */
	restriction: RestrictionSchema.nullable().default(null),
	id: IdSchema,
	name: z.string(),
	type: PlaylistTypeSchema.default("visual"),
	defaultDurationMs: PositiveIntSchema,
	transition: TransitionSchema,
	fit: FitSchema,
	playbackMode: PlaylistPlaybackModeSchema.default("all"),
	itemsPerPass: z.int().min(1).default(1),
	playbackOrder: PlaylistPlaybackOrderSchema.default("manual"),
	items: z.array(PlaylistItemSchema),
	audio: CampaignAudioSchema.extend({ name: z.string() }).nullable().default(null),
	muted: z.boolean().default(false),
	/** One pass through the unpaused items. */
	totalDurationMs: NonNegativeIntSchema,
	/** Players this playlist is published on. */
	playerCount: NonNegativeIntSchema,
	/** Phase 16 Step 6: library folder (null = top level) and organising tags. */
	folderId: IdSchema.nullable(),
	tags: z.array(z.string()),
	/** Phase 16 Step 15b: custom attribute values. */
	attributes: AttributeValuesSchema.default({}),
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema
});
//#endregion
//#region src/folders.ts
/** Nesting cap: deep enough for real libraries, shallow enough for a readable breadcrumb. */
const FOLDER_MAX_DEPTH = 8;
const folderName = z.string().trim().min(1).max(120);
const ContentFolderSchema = z.object({
	id: IdSchema,
	/** null = top level ("Content"). */
	parentId: IdSchema.nullable(),
	name: z.string(),
	createdAt: IsoDateTimeSchema,
	/** Shared with these player groups: members limited to them see this folder and everything inside. */
	sharedGroupIds: z.array(IdSchema).default([])
});
/** Names are unique per parent, case-insensitive (409 FOLDER_NAME_TAKEN). */
const CreateFolderRequestSchema = z.object({
	name: folderName,
	parentId: IdSchema.nullable().default(null)
});
/** Rename and/or move. Moving into its own subtree or past FOLDER_MAX_DEPTH is refused (422 FOLDER_INVALID_MOVE). */
const UpdateFolderRequestSchema = z.object({
	name: folderName.optional(),
	parentId: IdSchema.nullable().optional(),
	sharedGroupIds: z.array(IdSchema).max(200).optional()
}).refine((b) => b.name !== void 0 || b.parentId !== void 0 || b.sharedGroupIds !== void 0, "Nothing to update");
/** Bulk move of content and folders into `folderId` (null = top level). */
const MoveContentRequestSchema = z.object({
	items: z.array(ContentRefSchema).max(500).default([]),
	folderIds: z.array(IdSchema).max(100).default([]),
	folderId: IdSchema.nullable()
}).refine((b) => b.items.length + b.folderIds.length > 0, "Nothing to move");
/**
* Tags on content (organising and filtering; publishing by content tag is not part of Step 6).
* `set` replaces, `add` / `remove` edit each item's own list. Tags are trimmed and lowercased.
*/
const UpdateContentTagsRequestSchema = z.object({
	items: z.array(ContentRefSchema).min(1).max(500),
	tags: TagsSchema,
	mode: z.enum([
		"set",
		"add",
		"remove"
	]).default("set")
});
/** Fields every library item carries (media, campaigns, playlists). */
const organizedFields = {
	/** null = top level. */
	folderId: IdSchema.nullable(),
	tags: z.array(z.string())
};
//#endregion
//#region src/library.ts
/** What the Content page shows per card; media kind `widget` is shown as `app`. */
const LibraryTypeSchema = z.enum([
	"playlist",
	"campaign",
	"composition",
	"app",
	"video",
	"image",
	"pdf",
	"audio"
]);
const LIBRARY_PAGE_SIZE = 50;
/** A refresh reloads every row already on screen in one request, so the cap is larger than a page. */
const LIBRARY_PAGE_MAX = 500;
const LibraryQuerySchema = z.object({
	/** Name or original file name contains this (case-insensitive). A search looks in every folder. */
	q: z.string().trim().max(200).default(""),
	type: z.union([LibraryTypeSchema, z.literal("all")]).default("all"),
	/** Several types at once (`types=app,composition`); used instead of `type` when given. */
	types: z.string().transform((v) => v.split(",").filter(Boolean)).pipe(z.array(LibraryTypeSchema).max(8)).optional(),
	/** Only what can be published to a player now: ready media, no audio (audio plays on campaign Audio Tracks). */
	playable: z.enum(["true", "false"]).transform((v) => v === "true").default(false),
	/** Only ready media (audio included; campaigns and playlists always count as ready). */
	ready: z.enum(["true", "false"]).transform((v) => v === "true").default(false),
	tag: z.string().trim().toLowerCase().max(60).default(""),
	/** Folder shown when not searching; absent = top level. */
	folderId: IdSchema.optional(),
	/** modified: newest first · name: A→Z (numeric-aware) · type: by type, then name. */
	sort: z.enum([
		"modified",
		"name",
		"type"
	]).default("modified"),
	offset: z.coerce.number().int().min(0).default(0),
	limit: z.coerce.number().int().min(1).max(500).default(50)
});
const LibraryRefSchema = z.object({
	kind: z.enum([
		"media",
		"campaign",
		"playlist"
	]),
	id: IdSchema,
	/** For lists that show rows without loading every campaign and playlist (pickers). */
	name: z.string(),
	type: LibraryTypeSchema,
	folderId: IdSchema.nullable()
});
/** Organization-wide figures for the toolbar and the folder tree (first page only). */
const LibrarySummarySchema = z.object({
	counts: z.record(LibraryTypeSchema, z.number().int()),
	tags: z.array(z.string()),
	/** Items (not sub-folders) per folder id. */
	folderItems: z.record(z.string(), z.number().int())
});
const LibraryPageSchema = z.object({
	/** This page, in order. Campaigns and playlists are referenced by id (the CMS holds those lists whole). */
	items: z.array(LibraryRefSchema),
	/** The page's media, plus media its campaigns, playlists and compositions show (for thumbnails and previews). */
	media: z.array(MediaAssetRecordSchema),
	/** Rows matching the query. */
	total: z.number().int(),
	/** Offset of the next page; null on the last one. */
	nextOffset: z.number().int().nullable(),
	summary: LibrarySummarySchema.nullable()
});
/** At most this many ids per lookup (`?ids=` on GET media-assets and GET screens). */
const LOOKUP_IDS_MAX = 200;
/** `?ids=a,b,c`: only these items (unknown or unseen ids are left out). Absent = the whole list, as before. */
const IdsQuerySchema = z.string().transform((s) => [...new Set(s.split(",").map((x) => x.trim()).filter(Boolean))]).pipe(z.array(IdSchema).max(200)).optional();
const MediaListQuerySchema = z.object({ ids: IdsQuerySchema });
const ScreenListQuerySchema = z.object({
	ids: IdsQuerySchema,
	/** Only players with a location (map pins, app previews that need a place). */
	located: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
	limit: z.coerce.number().int().min(1).max(200).optional()
});
//#endregion
//#region src/players-page.ts
const PLAYERS_PAGE_SIZE = 50;
/** A refresh reloads every row already on screen in one request, so the cap is larger than a page. */
const PLAYERS_PAGE_MAX = 500;
/**
* online: seen within SCREEN_OFFLINE_AFTER_MS · offline: paired, not seen lately (or never) · unpaired: no device ·
* attention: content that won't download or play · outOfSync: not playing its latest content.
*/
const PlayersStatusFilterSchema = z.enum([
	"all",
	"online",
	"offline",
	"attention",
	"unpaired",
	"outOfSync"
]);
const PlayersQuerySchema = z.object({
	/** Name, time zone, device, tag, group name or any attribute value contains this (case-insensitive). */
	q: z.string().trim().max(200).default(""),
	status: PlayersStatusFilterSchema.default("all"),
	/** A group id, or `none` for players in no group. */
	group: z.union([IdSchema, z.literal("none")]).optional(),
	tag: z.string().trim().toLowerCase().max(60).default(""),
	/** name: A→Z (numeric-aware) · status: online first · lastSeen: most recent first · created: oldest first. */
	sort: z.enum([
		"name",
		"status",
		"lastSeen",
		"created"
	]).default("name"),
	dir: z.enum(["asc", "desc"]).default("asc"),
	offset: z.coerce.number().int().min(0).default(0),
	limit: z.coerce.number().int().min(1).max(500).default(50)
});
/** Every player the member may see, whatever the filters (the summary strip; first page only). */
const PlayersSummarySchema = z.object({
	all: z.number().int(),
	online: z.number().int(),
	offline: z.number().int(),
	attention: z.number().int(),
	unpaired: z.number().int(),
	outOfSync: z.number().int(),
	tags: z.array(z.string())
});
const PlayersPageSchema = z.object({
	items: z.array(ScreenSummarySchema),
	/** Players matching the query. */
	total: z.number().int(),
	/** Offset of the next page; null on the last one. */
	nextOffset: z.number().int().nullable(),
	summary: PlayersSummarySchema.nullable()
});
/** How many players a publish to these targets would reach (the Publish drawer's live count). */
const PlayersReachRequestSchema = z.object({
	players: z.array(IdSchema).max(1e3).default([]),
	groups: z.array(IdSchema).max(200).default([]),
	/** Players with the tag, or in a group with the tag (case-insensitive). */
	tags: z.array(z.string().trim().max(60)).max(100).default([]),
	allPlayers: z.boolean().default(false)
});
const PlayersReachSchema = z.object({ players: z.number().int() });
/** Per group id: its players online, offline, and not yet connected (never seen or not paired); `behind`: not playing
*  their latest content (the Players page's "out of sync"). */
const GroupsHealthSchema = z.record(z.string(), z.object({
	online: z.number().int(),
	offline: z.number().int(),
	other: z.number().int(),
	behind: z.number().int()
}));
//#endregion
//#region src/regions.ts
const REGION_MIN_RADIUS_M = 50;
const REGION_MAX_RADIUS_M = 5e5;
const REGION_MAX_POINTS = 200;
/** One corner of a polygon: [latitude, longitude]. */
const RegionPointSchema = z.tuple([LatitudeSchema, LongitudeSchema]);
const RegionShapeSchema = z.enum(["circle", "polygon"]);
const RegionFieldsSchema = z.object({
	name: z.string().trim().min(1, "Enter a name").max(120),
	/** Phase 17.7 (absent = circle, as before). */
	shape: RegionShapeSchema.default("circle"),
	latitude: LatitudeSchema,
	longitude: LongitudeSchema,
	radiusMeters: z.int().min(50, "The radius must be at least 50 m").max(REGION_MAX_RADIUS_M, "The radius can be at most 500 km"),
	/** Polygon corners in order (not closed: the last joins the first). Null for a circle. */
	polygon: z.array(RegionPointSchema).max(200).nullable().default(null)
});
const UpsertRegionRequestSchema = RegionFieldsSchema.refine((r) => r.shape === "circle" || (r.polygon?.length ?? 0) >= 3, {
	message: "Draw at least 3 points",
	path: ["polygon"]
});
const RegionSchema = RegionFieldsSchema.extend({
	id: IdSchema,
	/** Players (with a location) inside the circle (RG6). */
	playersInside: NonNegativeIntSchema,
	/** Restrictions that use this region; delete is refused while > 0. */
	usedBy: NonNegativeIntSchema,
	/** Phase 17.8: players added by hand (inside whatever their location) and taken out by hand. */
	includeScreenIds: z.array(IdSchema).default([]),
	excludeScreenIds: z.array(IdSchema).default([]),
	updatedAt: IsoDateTimeSchema
});
/** Great-circle distance in metres (shared by the compiler, the API and the CMS so "inside" never disagrees). */
function distanceMeters(aLat, aLng, bLat, bLng) {
	const r = (d) => d * Math.PI / 180;
	const dLat = r(bLat - aLat);
	const dLng = r(bLng - aLng);
	const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(aLat)) * Math.cos(r(bLat)) * Math.sin(dLng / 2) ** 2;
	return 12742017.6 * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** Ray casting on latitude/longitude (accurate at city and country scale; regions don't cross the 180° line). */
function insidePolygon(lat, lng, points) {
	let inside = false;
	for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
		const [yi, xi] = points[i];
		const [yj, xj] = points[j];
		if (yi > lat !== yj > lat && lng < (xj - xi) * (lat - yi) / (yj - yi) + xi) inside = !inside;
	}
	return inside;
}
const isInsideRegion = (lat, lng, region) => {
	if (lat === null || lng === null) return false;
	const pts = region.shape === "polygon" && Array.isArray(region.polygon) ? region.polygon : null;
	return pts && pts.length >= 3 ? insidePolygon(lat, lng, pts) : distanceMeters(lat, lng, region.latitude, region.longitude) <= region.radiusMeters;
};
/** Centre and covering radius of a polygon (stored alongside it; used to list and zoom to it). */
function polygonCircle(points) {
	const lat = points.reduce((s, p) => s + p[0], 0) / points.length;
	const lng = points.reduce((s, p) => s + p[1], 0) / points.length;
	const far = Math.max(...points.map((p) => distanceMeters(lat, lng, p[0], p[1])));
	return {
		latitude: +lat.toFixed(6),
		longitude: +lng.toFixed(6),
		radiusMeters: Math.min(REGION_MAX_RADIUS_M, Math.max(50, Math.ceil(far)))
	};
}
/**
* Phase 17.8: is this player in the region? A player added by hand always is, one taken out never is; everyone
* else is decided by location. The compiler, the API and the CMS all use this so they never disagree.
*/
function inRegion(screen, region) {
	if (region.includeScreenIds?.includes(screen.id)) return true;
	if (region.excludeScreenIds?.includes(screen.id)) return false;
	return isInsideRegion(screen.latitude, screen.longitude, region);
}
/** Why a player is listed: inside by location, added / taken out by hand, or just outside the edge. */
const RegionMembershipSchema = z.enum([
	"location",
	"included",
	"excluded",
	"nearby"
]);
const RegionPlayerSchema = z.object({
	id: IdSchema,
	name: z.string(),
	latitude: LatitudeSchema.nullable(),
	longitude: LongitudeSchema.nullable(),
	locationSource: z.enum([
		"manual",
		"device",
		"ip"
	]).nullable(),
	lastSeenAt: IsoDateTimeSchema.nullable(),
	/** Is its location inside the shape (regardless of hand overrides)? */
	locatedInside: z.boolean(),
	membership: RegionMembershipSchema,
	/** Metres from the region's centre; null without a location. */
	distanceMeters: z.number().nullable()
});
/** Content whose restriction mentions the region — its own (all players) or one placement's. */
const RegionContentSchema = z.object({
	/** 'content' = the item's own restriction; 'placement' = only where it is published to `target`. */
	scope: z.enum(["content", "placement"]),
	placementId: IdSchema.nullable(),
	content: ContentRefSchema,
	contentType: z.enum([
		"image",
		"video",
		"app",
		"campaign",
		"playlist"
	]),
	contentName: z.string(),
	thumbnailUrl: z.string().nullable(),
	/** Where the placement is published (null for scope 'content'). */
	target: z.object({
		kind: z.enum(["player", "group"]),
		id: IdSchema,
		name: z.string()
	}).nullable(),
	/** 'inside' = plays only inside, 'outside' = doesn't play inside, 'rule' = part of a logic rule. */
	rule: z.enum([
		"inside",
		"outside",
		"rule"
	]),
	/** Can be taken off here (not inside a logic rule). */
	removable: z.boolean()
});
const RegionDetailSchema = z.object({
	region: RegionSchema,
	players: z.array(RegionPlayerSchema),
	content: z.array(RegionContentSchema)
});
/** Add a player by hand, take it out by hand, or go back to its location ('auto'). */
const SetRegionPlayerRequestSchema = z.object({
	screenId: IdSchema,
	membership: z.enum([
		"auto",
		"include",
		"exclude"
	])
});
/** Make content play only inside the region (its own restriction), or take the region off a restriction. */
const RegionContentRequestSchema = z.discriminatedUnion("action", [z.object({
	action: z.literal("add"),
	content: ContentRefSchema
}), z.object({
	action: z.literal("remove"),
	content: ContentRefSchema,
	placementId: IdSchema.nullable().default(null)
})]);
//#endregion
//#region src/tags.ts
/** "Lobby " / "LOBBY" → "lobby": how two spellings are compared. */
const tagKey = (t) => t.trim().toLowerCase();
/** Collapses spaces, dashes and underscores so "east-region" and "East Region" look alike. */
const looseKey = (t) => tagKey(t).replace(/[\s_-]+/g, "");
/** Pairs of tags that are probably the same thing (different only in spacing / dashes, or one letter apart). */
function similarTags(tags) {
	const out = [];
	for (let i = 0; i < tags.length; i++) for (let j = i + 1; j < tags.length; j++) {
		const a = looseKey(tags[i]);
		const b = looseKey(tags[j]);
		if (a === b || a.length >= 5 && b.length >= 5 && editDistance(a, b) === 1) out.push([tags[i], tags[j]]);
	}
	return out;
}
function editDistance(a, b) {
	if (Math.abs(a.length - b.length) > 1) return 2;
	const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
	for (let j = 1; j <= b.length; j++) d[0][j] = j;
	for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
	return d[a.length][b.length];
}
const TagSummarySchema = z.object({
	tag: TagSchema,
	/** Players that have it (their own or through a group). */
	players: NonNegativeIntSchema,
	groups: NonNegativeIntSchema,
	/** Content published to the tag. */
	publications: NonNegativeIntSchema,
	/** Restrictions (content's own or placements') that ask for it. */
	restrictions: NonNegativeIntSchema,
	/** Other tags that look like the same one (merge candidates). */
	similar: z.array(TagSchema)
});
const TagPlayerSchema = z.object({
	id: IdSchema,
	name: z.string(),
	lastSeenAt: IsoDateTimeSchema.nullable(),
	/** The player has the tag itself. */
	direct: z.boolean(),
	/** Groups it gets the tag through. */
	viaGroups: z.array(z.object({
		id: IdSchema,
		name: z.string()
	}))
});
const TagGroupSchema = z.object({
	id: IdSchema,
	name: z.string(),
	members: NonNegativeIntSchema
});
const ContentTypeSchema = z.enum([
	"image",
	"video",
	"app",
	"campaign",
	"playlist"
]);
const TagPublicationRowSchema = z.object({
	id: IdSchema,
	content: ContentRefSchema,
	contentType: ContentTypeSchema,
	contentName: z.string(),
	thumbnailUrl: z.string().nullable(),
	playersReached: NonNegativeIntSchema,
	createdAt: IsoDateTimeSchema
});
const TagRestrictionRowSchema = z.object({
	scope: z.enum(["content", "placement"]),
	placementId: IdSchema.nullable(),
	content: ContentRefSchema,
	contentType: ContentTypeSchema,
	contentName: z.string(),
	target: z.object({
		kind: z.enum(["player", "group"]),
		id: IdSchema,
		name: z.string()
	}).nullable(),
	/** 'playIf' = plays only on players with it, 'dontPlayIf' = not on them, 'rule' = in a logic rule. */
	rule: z.enum([
		"playIf",
		"dontPlayIf",
		"rule"
	])
});
const TagDetailSchema = z.object({
	summary: TagSummarySchema,
	players: z.array(TagPlayerSchema),
	groups: z.array(TagGroupSchema),
	publications: z.array(TagPublicationRowSchema),
	restrictions: z.array(TagRestrictionRowSchema)
});
/** Give the tag to (or take it from) players or groups, in bulk. */
const TagMembersRequestSchema = z.object({
	tag: TagSchema,
	action: z.enum(["add", "remove"]),
	screenIds: z.array(IdSchema).max(500).default([]),
	groupIds: z.array(IdSchema).max(200).default([])
});
/** Rename a tag everywhere; renaming onto an existing tag merges the two. */
const RenameTagRequestSchema = z.object({
	from: TagSchema,
	to: TagSchema
});
/** What deleting a tag would change (shown before confirming). */
const TagDeleteImpactSchema = z.object({
	players: NonNegativeIntSchema,
	groups: NonNegativeIntSchema,
	publications: NonNegativeIntSchema,
	/** Items players would lose (copies of tag publications). */
	itemsRemoved: NonNegativeIntSchema,
	restrictions: NonNegativeIntSchema
});
//#endregion
//#region src/billing.ts
const PaidPlanSchema = z.enum(["STANDARD", "ADVANCED"]);
const PLAN_RANK = {
	TRIAL: 0,
	STANDARD: 1,
	ADVANCED: 2
};
const BILLING_MAX_SEATS = 1e4;
/** Smallest amount sent to the gateway; smaller pro-rata amounts wait for the renewal. */
const BILLING_MIN_CHARGE_CENTS = 50;
/** Days a failed renewal keeps the service running while retries happen. */
const BILLING_GRACE_DAYS = 7;
/** Renewal retries after a failure (days after the first attempt). */
const BILLING_RETRY_DAYS = [
	1,
	3,
	5
];
const monthlyCents = (plan, seats) => PLAN_CATALOG[plan].pricePerPlayerCents * seats;
const BillingIntervalSchema = z.enum(["month", "year"]);
const ANNUAL_DISCOUNT = .15;
/** Price of one player for one period (yearly rounded to the cent per player). */
const playerPeriodCents = (plan, interval) => interval === "year" ? Math.round(PLAN_CATALOG[plan].pricePerPlayerCents * 12 * .85) : PLAN_CATALOG[plan].pricePerPlayerCents;
const periodCents = (plan, seats, interval) => playerPeriodCents(plan, interval) * seats;
/** End of a period that starts at `d` (a year keeps the day, 29 Feb → 28 Feb). */
function addPeriod(d, interval) {
	if (interval === "month") return addMonth(d);
	const y = new Date(d);
	y.setUTCDate(1);
	y.setUTCFullYear(y.getUTCFullYear() + 1);
	const last = new Date(Date.UTC(y.getUTCFullYear(), y.getUTCMonth() + 1, 0)).getUTCDate();
	y.setUTCDate(Math.min(d.getUTCDate(), last));
	return y;
}
/** Same day next month (the 31st → the last day of a shorter month). */
function addMonth(d) {
	const out = new Date(d);
	const day = out.getUTCDate();
	out.setUTCDate(1);
	out.setUTCMonth(out.getUTCMonth() + 1);
	const last = new Date(Date.UTC(out.getUTCFullYear(), out.getUTCMonth() + 1, 0)).getUTCDate();
	out.setUTCDate(Math.min(day, last));
	return out;
}
/** The share of the period still to come, 0…1. */
function remainingFraction(periodStart, periodEnd, now) {
	const total = periodEnd.getTime() - periodStart.getTime();
	return total > 0 ? Math.min(1, Math.max(0, (periodEnd.getTime() - now.getTime()) / total)) : 0;
}
/**
* Split a requested plan / player count into what happens now (increases) and at the period end (decreases).
* A higher plan with fewer players → the plan now (keeping today's players), the lower count at the period end.
*/
function planChange(current, want, now) {
	const wantFull = {
		planTier: want.planTier,
		seats: want.seats,
		interval: want.interval ?? "month"
	};
	if (!current || current.planTier === "TRIAL") return {
		now: wantFull,
		later: null,
		proratedCents: periodCents(wantFull.planTier, wantFull.seats, wantFull.interval),
		restart: true,
		creditCents: 0
	};
	const curInterval = current.interval ?? "month";
	const left = remainingFraction(current.periodStart, current.periodEnd, now);
	if (curInterval === "month" && wantFull.interval === "year") {
		const credit = Math.round(periodCents(current.planTier, current.seats, "month") * left);
		const price = periodCents(wantFull.planTier, wantFull.seats, "year");
		return {
			now: wantFull,
			later: null,
			proratedCents: Math.max(0, price - credit),
			restart: true,
			creditCents: credit
		};
	}
	const nowPlan = PLAN_RANK[wantFull.planTier] > PLAN_RANK[current.planTier] ? wantFull.planTier : current.planTier;
	const nowSeats = Math.max(wantFull.seats, current.seats);
	const changesNow = nowPlan !== current.planTier || nowSeats !== current.seats;
	const nowTarget = {
		planTier: nowPlan,
		seats: nowSeats,
		interval: curInterval
	};
	const later = wantFull.planTier !== nowPlan || wantFull.seats !== nowSeats || wantFull.interval !== curInterval ? wantFull : null;
	const diff = periodCents(nowPlan, nowSeats, curInterval) - periodCents(current.planTier, current.seats, curInterval);
	const proratedCents = changesNow ? Math.max(0, Math.round(diff * left)) : 0;
	return {
		now: changesNow ? nowTarget : null,
		later,
		proratedCents,
		restart: false,
		creditCents: 0
	};
}
const InvoiceLineSchema = z.object({
	description: z.string(),
	quantity: PositiveIntSchema,
	unitCents: z.int(),
	amountCents: z.int()
});
const InvoiceSchema = z.object({
	id: IdSchema,
	number: z.string(),
	kind: z.enum([
		"subscribe",
		"change",
		"renewal"
	]),
	status: z.enum([
		"open",
		"paid",
		"failed",
		"void"
	]),
	currency: z.string().length(3),
	amountCents: NonNegativeIntSchema,
	lines: z.array(InvoiceLineSchema),
	periodStart: IsoDateTimeSchema,
	periodEnd: IsoDateTimeSchema,
	failureReason: z.string().nullable(),
	paidAt: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema
});
const BillingContactSchema = z.object({
	name: z.string().trim().min(1, "Enter the billing name").max(120),
	email: z.email("Enter a valid email"),
	/** Country code and number, e.g. "+973 3612 3456" (the gateway needs it to save a card). */
	phone: z.string().trim().regex(/^\+\d{1,4}[\s-]?\d[\d\s-]{5,15}$/, "Enter the phone with its country code, e.g. +973 3612 3456")
});
const BillingOverviewSchema = z.object({
	/** Gateway in use: 'tap' or 'test' (no real money). */
	gateway: z.enum(["tap", "test"]),
	currency: z.string().length(3),
	subscription: z.object({
		planTier: PaidPlanSchema,
		seats: PositiveIntSchema,
		interval: BillingIntervalSchema,
		pendingInterval: BillingIntervalSchema.nullable(),
		status: BillingStatusSchema,
		periodStart: IsoDateTimeSchema,
		periodEnd: IsoDateTimeSchema,
		cancelAtPeriodEnd: z.boolean(),
		pendingPlanTier: PaidPlanSchema.nullable(),
		pendingSeats: PositiveIntSchema.nullable(),
		carryOverCents: NonNegativeIntSchema,
		graceEndsAt: IsoDateTimeSchema.nullable(),
		nextAttemptAt: IsoDateTimeSchema.nullable(),
		card: z.object({
			brand: z.string().nullable(),
			last4: z.string()
		}).nullable(),
		contact: BillingContactSchema,
		/** What the next renewal will charge (pending changes and carry-over included). */
		nextChargeCents: NonNegativeIntSchema
	}).nullable(),
	/** Players in use: the floor for a lower player count. */
	playersInUse: NonNegativeIntSchema,
	invoices: z.array(InvoiceSchema)
});
const PlanRequestSchema = z.object({
	planTier: PaidPlanSchema,
	interval: BillingIntervalSchema.default("month"),
	seats: z.int().min(1, "At least 1 player").max(BILLING_MAX_SEATS)
});
/** What a plan change would cost, before paying. */
const PlanTargetSchema = z.object({
	planTier: PaidPlanSchema,
	seats: PositiveIntSchema,
	interval: BillingIntervalSchema
});
const BillingQuoteSchema = z.object({
	now: PlanTargetSchema.nullable(),
	later: PlanTargetSchema.nullable(),
	/** The paid part starts a new period today. */
	restart: z.boolean(),
	/** Charged today (0 when nothing increases or the amount waits for the renewal). */
	dueNowCents: NonNegativeIntSchema,
	/** A pro-rata amount too small to charge on its own, added to the next renewal. */
	deferredCents: NonNegativeIntSchema,
	/** Price per period from the next renewal (`nextInterval`). */
	nextMonthlyCents: NonNegativeIntSchema,
	nextInterval: BillingIntervalSchema,
	/** When the period ends / renews (now + 1 month for a new subscription). */
	renewsAt: IsoDateTimeSchema,
	lines: z.array(InvoiceLineSchema)
});
const CheckoutRequestSchema = PlanRequestSchema.extend({ contact: BillingContactSchema.optional() });
/** Pay on the gateway's page (`redirectUrl`), or nothing to pay (`applied`). */
const CheckoutResultSchema = z.object({
	invoiceId: IdSchema.nullable(),
	redirectUrl: z.string().nullable(),
	applied: z.boolean()
});
//#endregion
//#region src/analytics.ts
const ANALYTICS_MAX_DAYS = 92;
const AnalyticsQuerySchema = z.object({
	/** Inclusive local dates in `tz`. */
	from: LocalDateSchema,
	to: LocalDateSchema,
	/** The viewer's time zone (days are bucketed in it). */
	tz: IanaTimeZoneSchema
}).refine((q) => q.from <= q.to, {
	message: "The start date is after the end date",
	path: ["from"]
}).refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 864e5 < 92, {
	message: `Choose at most 92 days`,
	path: ["to"]
});
/** The same rule as the Players list: never contacted / not paired are not "offline" (A2). */
const PlayerStatusSchema = z.enum([
	"online",
	"offline",
	"never",
	"unpaired"
]);
/** From the latest heartbeat: playing the current revision, staging a newer one, or behind. */
const PlayerSyncSchema = z.enum([
	"inSync",
	"downloading",
	"outOfSync",
	"unknown"
]);
const AnalyticsPlayerSchema = z.object({
	id: IdSchema,
	name: z.string(),
	status: PlayerStatusSchema,
	sync: PlayerSyncSchema,
	appVersion: z.string().nullable(),
	latitude: z.number().nullable(),
	longitude: z.number().nullable(),
	/** In the range. */
	errors: NonNegativeIntSchema,
	warnings: NonNegativeIntSchema
});
const AnalyticsOverviewSchema = z.object({
	players: z.array(AnalyticsPlayerSchema),
	/** Highest app version reported by any player (A1 "latest version"); null when none reported. */
	latestAppVersion: z.string().nullable(),
	/** One entry per day of the range, zeros included. */
	series: z.array(z.object({
		day: LocalDateSchema,
		errors: NonNegativeIntSchema,
		warnings: NonNegativeIntSchema
	})),
	/** Proof of play in the range. */
	plays: z.object({
		total: NonNegativeIntSchema,
		screenTimeMs: NonNegativeIntSchema,
		top: z.array(z.object({
			kind: z.enum(["media", "campaign"]),
			id: IdSchema,
			name: z.string(),
			plays: NonNegativeIntSchema,
			screenTimeMs: NonNegativeIntSchema,
			players: NonNegativeIntSchema
		}))
	})
});
/** "2.10.1" > "2.9.0" (numeric parts; non-numeric suffixes compare as text). */
function compareVersions(a, b) {
	const pa = a.split(/[.\-+]/);
	const pb = b.split(/[.\-+]/);
	for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
		const x = pa[i] ?? "0";
		const y = pb[i] ?? "0";
		const nx = Number(x);
		const ny = Number(y);
		const c = Number.isFinite(nx) && Number.isFinite(ny) ? nx - ny : x.localeCompare(y);
		if (c) return Math.sign(c);
	}
	return 0;
}
//#endregion
//#region src/health.ts
const SEVERITIES = [
	"critical",
	"warning",
	"info"
];
const HealthSeveritySchema = z.enum(SEVERITIES);
const SEVERITY_RANK = {
	critical: 3,
	warning: 2,
	info: 1
};
const SUBJECT_KINDS = [
	"player",
	"campaign",
	"playlist",
	"media",
	"publication",
	"feed"
];
const HealthSubjectKindSchema = z.enum(SUBJECT_KINDS);
const HEALTH_CODES = [
	"PLAYER_OFFLINE",
	"PLAYER_NEVER_CONNECTED",
	"PLAYER_OUT_OF_SYNC",
	"PLAYER_FALLBACK",
	"PLAYER_NOT_ON_SCREEN",
	"PLAYER_FILES_FAILED",
	"PLAYER_STORAGE_LOW",
	"PLAYER_ERROR_SPIKE",
	"PLAYER_APP_OUTDATED",
	"PLAYER_NO_CONTENT",
	"PLAYER_LOCATION_MISSING",
	"CAMPAIGN_EMPTY_ZONE",
	"PLAYLIST_EMPTY",
	"MEDIA_FAILED",
	"PUBLICATION_EXPIRED",
	"FEED_FAILING",
	"CONTENT_BROKEN",
	"MEDIA_FILE_MISSING",
	"PLAYER_CANT_DOWNLOAD",
	"PLAYER_CONTENT_REJECTED"
];
const HealthCodeSchema = z.enum(HEALTH_CODES);
const HealthSettingsSchema = z.object({
	/** A day: players switched off overnight or over a weekend aren't an emergency by default. */
	offlineAfterMinutes: z.int().min(1).max(1440).default(1440),
	neverConnectedAfterHours: z.int().min(1).max(720).default(24),
	outOfSyncAfterMinutes: z.int().min(1).max(1440).default(15),
	storageLowPercent: z.int().min(1).max(50).default(10),
	errorSpikePerHour: z.int().min(1).max(1e4).default(20),
	/** Checks turned off for the organization: they open no issues (open ones resolve) and alerts on them don't fire. */
	disabled: z.array(HealthCodeSchema).max(HEALTH_CODES.length).default([])
});
const DEFAULT_HEALTH_SETTINGS = HealthSettingsSchema.parse({});
const HealthIssueSchema = z.object({
	id: IdSchema,
	code: HealthCodeSchema,
	severity: HealthSeveritySchema,
	kind: HealthSubjectKindSchema,
	subjectId: IdSchema,
	subjectName: z.string(),
	/** The player concerned (player issues, and publications placed on a player). */
	screenId: IdSchema.nullable(),
	/** Plain-language, rendered by the server ("Offline for 2 h 10 min"). */
	message: z.string(),
	details: z.record(z.string(), z.union([
		z.string(),
		z.number(),
		z.boolean(),
		z.null()
	])),
	openedAt: IsoDateTimeSchema,
	resolvedAt: IsoDateTimeSchema.nullable(),
	acknowledgedAt: IsoDateTimeSchema.nullable(),
	acknowledgedBy: z.string().nullable()
});
/** "player,campaign" → ['player', 'campaign'] (query strings carry lists comma-separated). */
const csv = (values) => z.string().optional().transform((s) => s ? s.split(",").filter(Boolean) : []).pipe(z.array(z.enum(values)));
const HealthIssuesQuerySchema = z.object({
	state: z.enum(["open", "resolved"]).default("open"),
	kinds: csv(SUBJECT_KINDS),
	severities: csv(SEVERITIES),
	screenId: IdSchema.optional(),
	q: z.string().trim().max(100).optional(),
	/** Opaque keyset cursor from the previous page. */
	cursor: z.string().max(200).optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50)
});
const HealthIssuesPageSchema = z.object({
	items: z.array(HealthIssueSchema),
	nextCursor: z.string().nullable()
});
/** Open issue counts (sidebar badge, page header). Acknowledged issues are counted separately. */
const HealthSummarySchema = z.object({
	critical: NonNegativeIntSchema,
	warning: NonNegativeIntSchema,
	info: NonNegativeIntSchema,
	acknowledged: NonNegativeIntSchema,
	lastSweepAt: IsoDateTimeSchema.nullable()
});
/**
* Phase 17.12: the notification bell — recent critical and warning problems (opened in the last 14 days, open or since
* fixed), newest first, and how many open ones this member hasn't seen yet.
*/
const HealthNotificationsSchema = z.object({
	items: z.array(HealthIssueSchema),
	unread: NonNegativeIntSchema,
	seenAt: IsoDateTimeSchema.nullable()
});
const AcknowledgeIssuesRequestSchema = z.object({
	ids: z.array(IdSchema).min(1).max(500),
	acknowledged: z.boolean()
});
/**
* "Is everything playing?" at a glance (top of the Players page): open problems that stop content from playing,
* grouped the way a person thinks about them: one per broken item, one per kind of player problem.
* Offline players are left out (an offline player isn't a playback problem; the Offline card counts them).
*/
const PLAYBACK_CODES = [
	"CONTENT_BROKEN",
	"MEDIA_FILE_MISSING",
	"PLAYER_CANT_DOWNLOAD",
	"PLAYER_CONTENT_REJECTED",
	"PLAYER_FILES_FAILED",
	"PLAYER_FALLBACK"
];
const PlaybackProblemSchema = z.object({
	/** Stable while the problem lasts (code + item, or code + cause). */
	key: z.string(),
	code: HealthCodeSchema,
	severity: HealthSeveritySchema,
	/** The broken item (content problems), else null. */
	item: z.object({
		id: IdSchema,
		name: z.string()
	}).nullable(),
	/** Why, in plain words ("its file arrived damaged", "can't reach the server"). */
	cause: z.string(),
	/** Players affected; null when not known (a file missing on the server: every player using it). */
	players: NonNegativeIntSchema.nullable(),
	/** The issues behind it (one per player for player problems), worst first: each opens in the fix panel. */
	issues: z.array(HealthIssueSchema).max(20)
});
const PlaybackHealthSchema = z.object({
	/** Players this member can see. */
	players: NonNegativeIntSchema,
	/** Of those, how many have at least one playback problem. */
	affected: NonNegativeIntSchema,
	/** Worst first. */
	problems: z.array(PlaybackProblemSchema)
});
//#endregion
//#region src/activity.ts
const ACTIVITY_RETENTION_MONTHS = 12;
const ActivityCategorySchema = z.enum(["audit", "system"]);
const ActivitySeveritySchema = z.enum([
	"info",
	"warning",
	"critical"
]);
/** Action groups for filtering: the part before the dot of `action` ("content.published" → "content"). */
const ACTIVITY_GROUPS = [
	"auth",
	"content",
	"publish",
	"player",
	"group",
	"region",
	"interaction",
	"settings",
	"member",
	"health",
	"alert",
	"feed",
	"device"
];
const ActivityGroupSchema = z.enum(ACTIVITY_GROUPS);
const ActivityEventSchema = z.object({
	id: IdSchema,
	at: IsoDateTimeSchema,
	category: ActivityCategorySchema,
	severity: ActivitySeveritySchema,
	/** e.g. "content.published", "player.offline". */
	action: z.string(),
	actor: z.object({
		type: z.enum([
			"user",
			"device",
			"system"
		]),
		id: IdSchema.nullable(),
		name: z.string(),
		/** Phase 17.6: a person's profile picture (null = initials). */
		avatarUrl: z.string().nullable().default(null)
	}),
	subject: z.object({
		kind: z.string(),
		id: IdSchema.nullable(),
		name: z.string()
	}).nullable(),
	screenId: IdSchema.nullable(),
	message: z.string(),
	details: z.record(z.string(), z.unknown()),
	/** Audit only: where the person acted from. */
	ip: z.string().nullable()
});
const ActivityQuerySchema = z.object({
	category: ActivityCategorySchema.default("system"),
	/** Inclusive local dates in `tz`. */
	from: LocalDateSchema,
	to: LocalDateSchema,
	tz: IanaTimeZoneSchema.default("UTC"),
	group: ActivityGroupSchema.optional(),
	severity: ActivitySeveritySchema.optional(),
	screenId: IdSchema.optional(),
	/** Every player in this player group or building. */
	groupId: IdSchema.optional(),
	/** One exact event type, e.g. "player.offline" (narrower than `group`). */
	action: z.string().trim().max(64).regex(/^[a-z_]+\.[a-z_.]+$/).optional(),
	/** Who did it: a person or a player (by id), or every event of one kind of actor. */
	actorId: IdSchema.optional(),
	actorType: z.enum([
		"user",
		"device",
		"system"
	]).optional(),
	/** Everything about one item (as its subject, or one of the items a publish/move named). */
	subjectId: IdSchema.optional(),
	q: z.string().trim().max(100).optional(),
	cursor: z.string().max(200).optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50)
}).refine((q) => q.from <= q.to, {
	message: "The start date is after the end date",
	path: ["from"]
}).refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 864e5 <= 366, {
	message: "Choose at most 12 months",
	path: ["to"]
});
const ActivityPageSchema = z.object({
	items: z.array(ActivityEventSchema),
	nextCursor: z.string().nullable()
});
/**
* The choices for the event type, "who" and "item" filters: only what actually appears in the period (most frequent
* first, at most ACTIVITY_FACET_LIMIT each), so the lists stay short whatever the log's size.
*/
const ACTIVITY_FACET_LIMIT = 100;
const ActivityFacetsQuerySchema = z.object({
	category: ActivityCategorySchema.default("system"),
	from: LocalDateSchema,
	to: LocalDateSchema,
	tz: IanaTimeZoneSchema.default("UTC")
}).refine((q) => q.from <= q.to, {
	message: "The start date is after the end date",
	path: ["from"]
}).refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 864e5 <= 366, {
	message: "Choose at most 12 months",
	path: ["to"]
});
const ActivityFacetsSchema = z.object({
	actions: z.array(z.object({
		action: z.string(),
		count: z.number().int()
	})),
	actors: z.array(z.object({
		type: z.enum([
			"user",
			"device",
			"system"
		]),
		id: IdSchema.nullable(),
		name: z.string(),
		count: z.number().int()
	})),
	/** Content items (media, campaigns, playlists) the events are about. */
	items: z.array(z.object({
		kind: z.string(),
		id: IdSchema,
		name: z.string(),
		count: z.number().int()
	}))
});
/** One item's history for the content Information Panel's Activity tab (any member; no IP addresses). */
const ContentActivityQuerySchema = z.object({
	kind: z.enum([
		"media",
		"campaign",
		"playlist"
	]),
	id: IdSchema,
	limit: z.coerce.number().int().min(1).max(100).default(50)
});
/** A person shown in the Information Panel (id lets the CMS say “You”). */
const ContentPersonSchema = z.object({
	id: z.string().nullable(),
	name: z.string(),
	avatarUrl: z.string().nullable()
});
const ContentActivitySchema = z.object({
	/** Who created it; null when unknown (made before owners were recorded, or that person was removed). */
	owner: ContentPersonSchema.nullable(),
	createdAt: IsoDateTimeSchema,
	modifiedAt: IsoDateTimeSchema,
	/** Who made the latest change in the Activity Log, when it is still there. */
	modifiedBy: ContentPersonSchema.nullable(),
	items: z.array(ActivityEventSchema)
});
//#endregion
//#region src/reports.ts
const ReportFormatSchema = z.enum([
	"csv",
	"xlsx",
	"pdf",
	"html"
]);
const FORMAT_LABEL = {
	csv: "CSV",
	xlsx: "Excel",
	pdf: "PDF",
	html: "HTML"
};
/** PDF/HTML are documents to read: longer reports should be CSV/Excel. */
const DOCUMENT_ROW_LIMIT = 5e3;
const SPREADSHEET_ROW_LIMIT = 1e6;
const REPORT_FILE_RETENTION_DAYS = 90;
const REPORT_TYPE_IDS = [
	"playback_summary",
	"playback_by_day",
	"playback_by_player",
	"playback_daily_by_player",
	"playback_individual",
	"playback_by_building",
	"proof_of_play",
	"health_issues",
	"players",
	"published_content",
	"audit_log"
];
const ReportTypeSchema = z.enum(REPORT_TYPE_IDS);
const REPORT_TYPES = {
	playback_summary: {
		category: "Playback",
		label: "Playback summary",
		description: "Plays and time on screen per content item.",
		usesPeriod: true,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	playback_by_day: {
		category: "Playback",
		label: "Playback by day",
		description: "Plays and time on screen per content item, day by day.",
		usesPeriod: true,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	playback_by_player: {
		category: "Playback",
		label: "Playback by player",
		description: "What each player played, and for how long.",
		usesPeriod: true,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	playback_daily_by_player: {
		category: "Playback",
		label: "Daily playback by player",
		description: "What each player played, day by day.",
		usesPeriod: true,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	playback_individual: {
		category: "Playback",
		label: "Individual playbacks",
		description: "Every single play with its time, player and duration. Can be very large: use CSV or Excel.",
		usesPeriod: true,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	playback_by_building: {
		category: "Playback",
		label: "Playback by building",
		description: "Plays and time on screen per content item in each building.",
		usesPeriod: true,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	proof_of_play: {
		category: "Playback",
		label: "Proof of play (for advertisers)",
		description: "One section per content item with its picture, totals, and where and when it ran: by building, day and player. Made to send to the advertiser as PDF.",
		usesPeriod: true,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	health_issues: {
		category: "Health Check",
		label: "Health Check issues",
		description: "Open issues at the time the report runs, plus those resolved in the period.",
		usesPeriod: true,
		usesContent: false,
		usesPlayers: true,
		adminOnly: false
	},
	players: {
		category: "Players",
		label: "Players",
		description: "Every player with its status, device, app version, location, groups and tags.",
		usesPeriod: false,
		usesContent: false,
		usesPlayers: true,
		adminOnly: false
	},
	published_content: {
		category: "Content",
		label: "Published content",
		description: "What is published where: every player and group list with its restrictions.",
		usesPeriod: false,
		usesContent: true,
		usesPlayers: true,
		adminOnly: false
	},
	audit_log: {
		category: "Audit",
		label: "Audit log",
		description: "What people did in the period (admins only).",
		usesPeriod: true,
		usesContent: false,
		usesPlayers: false,
		adminOnly: true
	}
};
const PERIOD_PRESETS = [
	"yesterday",
	"last7",
	"last30",
	"thisMonth",
	"lastMonth"
];
const PRESET_LABEL = {
	yesterday: "Yesterday",
	last7: "Last 7 days",
	last30: "Last 30 days",
	thisMonth: "This month",
	lastMonth: "Last month"
};
const ReportScheduleSchema = z.object({
	every: z.enum([
		"day",
		"week",
		"month"
	]),
	/** Every N days / weeks / months. */
	interval: z.int().min(1).max(12),
	/** ISO weekdays (1 = Mon … 7 = Sun); weekly only. */
	weekdays: z.array(z.int().min(1).max(7)).max(7).default([]),
	/** Day of the month (1–28) or the last day; monthly only. */
	monthDay: z.union([z.int().min(1).max(28), z.literal("last")]).default(1),
	/** Local time of day the report is generated. */
	at: LocalTimeSchema,
	/** First local date runs may happen (anchors "every N"). */
	startsOn: LocalDateSchema,
	/** Last local date runs may happen; null = never ends. */
	endsOn: LocalDateSchema.nullable()
}).refine((s) => s.every !== "week" || s.weekdays.length > 0, {
	message: "Choose at least one day",
	path: ["weekdays"]
}).refine((s) => !s.endsOn || s.endsOn >= s.startsOn, {
	message: "The end date is before the start date",
	path: ["endsOn"]
});
const ReportPeriodSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal("once"),
	from: LocalDateSchema,
	to: LocalDateSchema
}).refine((p) => p.from <= p.to, {
	message: "The start date is after the end date",
	path: ["from"]
}).refine((p) => days(p.from, p.to) <= 366, {
	message: "Choose at most a year",
	path: ["to"]
}), z.object({
	kind: z.literal("recurring"),
	schedule: ReportScheduleSchema
})]);
const TagMatchSchema = z.object({
	match: z.enum(["all", "any"]).default("any"),
	tags: TagsSchema.default([])
});
const ReportScopeSchema = z.object({
	players: z.discriminatedUnion("mode", [
		z.object({ mode: z.literal("all") }),
		z.object({
			mode: z.literal("players"),
			ids: z.array(IdSchema).min(1, "Choose at least one player").max(1e3)
		}),
		z.object({
			mode: z.literal("groups"),
			ids: z.array(IdSchema).min(1, "Choose at least one group").max(200)
		})
	]).default({ mode: "all" }),
	playerTags: TagMatchSchema.default({
		match: "any",
		tags: []
	}),
	/** Empty = all content. Playback is recorded per media item or campaign. */
	content: z.array(z.object({
		kind: z.enum(["media", "campaign"]),
		id: IdSchema
	})).max(200).default([]),
	contentTags: TagMatchSchema.default({
		match: "any",
		tags: []
	})
});
const UpsertReportRequestSchema = z.object({
	name: z.string().trim().min(1, "Enter a name").max(120),
	type: ReportTypeSchema,
	format: ReportFormatSchema,
	timeZone: IanaTimeZoneSchema,
	period: ReportPeriodSchema,
	scope: ReportScopeSchema.default({
		players: { mode: "all" },
		playerTags: {
			match: "any",
			tags: []
		},
		content: [],
		contentTags: {
			match: "any",
			tags: []
		}
	}),
	/** Each run's file is emailed here (when a mail server is configured). */
	emails: z.array(EmailSchema).max(20).default([])
});
const ReportRunStatusSchema = z.enum([
	"queued",
	"running",
	"done",
	"failed"
]);
const ReportRunSchema = z.object({
	id: IdSchema,
	reportId: IdSchema,
	status: ReportRunStatusSchema,
	/** The covered dates (null for snapshot reports). */
	periodFrom: LocalDateSchema.nullable(),
	periodTo: LocalDateSchema.nullable(),
	rows: NonNegativeIntSchema.nullable(),
	/** The row limit for the format was reached; the file says so on its last line. */
	truncated: z.boolean(),
	sizeBytes: NonNegativeIntSchema.nullable(),
	error: z.string().nullable(),
	email: z.enum([
		"none",
		"sent",
		"skipped",
		"failed"
	]),
	emailNote: z.string().nullable(),
	createdAt: IsoDateTimeSchema,
	finishedAt: IsoDateTimeSchema.nullable(),
	/** False once the file is past retention. */
	downloadable: z.boolean()
});
const ReportSchema = z.object({
	id: IdSchema,
	name: z.string(),
	type: ReportTypeSchema,
	format: ReportFormatSchema,
	timeZone: z.string(),
	period: ReportPeriodSchema,
	scope: ReportScopeSchema,
	emails: z.array(z.string()),
	createdBy: z.string(),
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema,
	nextRunAt: IsoDateTimeSchema.nullable(),
	lastRun: ReportRunSchema.nullable()
});
const ReportPreviewSchema = z.object({
	columns: z.array(z.string()),
	rows: z.array(z.array(z.union([
		z.string(),
		z.number(),
		z.null()
	]))),
	periodFrom: LocalDateSchema.nullable(),
	periodTo: LocalDateSchema.nullable(),
	/** More rows exist than shown. */
	more: z.boolean()
});
const DAY_MS = 864e5;
const toUtcDay = (d) => Date.parse(`${d}T00:00:00Z`);
const fromUtcDay = (ms) => new Date(ms).toISOString().slice(0, 10);
const addDays = (d, n) => fromUtcDay(toUtcDay(d) + n * DAY_MS);
function days(a, b) {
	return Math.round((toUtcDay(b) - toUtcDay(a)) / DAY_MS);
}
const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
/** ISO weekday (1 = Mon … 7 = Sun) of a local date. */
const isoWeekday = (d) => (new Date(toUtcDay(d)).getUTCDay() + 6) % 7 + 1;
/** Concrete dates of a preset, relative to the viewer's/report's local "today". */
function presetRange(preset, today) {
	const [y, m] = today.split("-").map(Number);
	switch (preset) {
		case "yesterday": return {
			from: addDays(today, -1),
			to: addDays(today, -1)
		};
		case "last7": return {
			from: addDays(today, -6),
			to: today
		};
		case "last30": return {
			from: addDays(today, -29),
			to: today
		};
		case "thisMonth": return {
			from: `${today.slice(0, 8)}01`,
			to: today
		};
		case "lastMonth": {
			const py = m === 1 ? y - 1 : y;
			const pm = m === 1 ? 12 : m - 1;
			const mm = String(pm).padStart(2, "0");
			return {
				from: `${py}-${mm}-01`,
				to: `${py}-${mm}-${String(lastDayOfMonth(py, pm)).padStart(2, "0")}`
			};
		}
	}
}
/** Wall-clock parts of an instant in a time zone. */
function localParts(utcMs, tz) {
	const f = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23"
	});
	const p = Object.fromEntries(f.formatToParts(new Date(utcMs)).map((x) => [x.type, x.value]));
	return {
		date: `${p.year}-${p.month}-${p.day}`,
		hour: Number(p.hour),
		minute: Number(p.minute)
	};
}
const offsetMs = (utcMs, tz) => {
	const l = localParts(utcMs, tz);
	return Date.UTC(Number(l.date.slice(0, 4)), Number(l.date.slice(5, 7)) - 1, Number(l.date.slice(8, 10)), l.hour, l.minute) - Math.floor(utcMs / 6e4) * 6e4;
};
/** The instant a local date + time happens in `tz` (a time skipped by DST moves forward by the gap). */
function localToUtc(date, time, tz) {
	const [hh, mm] = time.split(":").map(Number);
	const guess = toUtcDay(date) + (hh * 60 + mm) * 6e4;
	const first = guess - offsetMs(guess, tz);
	const second = guess - offsetMs(first, tz);
	return Math.max(first, second);
}
/** Does a run happen on this local date? ("every N" counts from `startsOn`.) */
function runsOn(s, date) {
	if (date < s.startsOn || s.endsOn && date > s.endsOn) return false;
	if (s.every === "day") return days(s.startsOn, date) % s.interval === 0;
	if (s.every === "week") {
		if (!s.weekdays.includes(isoWeekday(date))) return false;
		const weekStart = (d) => addDays(d, 1 - isoWeekday(d));
		return Math.round(days(weekStart(s.startsOn), weekStart(date)) / 7) % s.interval === 0;
	}
	const [y, m, d] = date.split("-").map(Number);
	const [sy, sm] = s.startsOn.split("-").map(Number);
	if (((y - sy) * 12 + (m - sm)) % s.interval !== 0) return false;
	return s.monthDay === "last" ? d === lastDayOfMonth(y, m) : d === s.monthDay;
}
/** The first run strictly after `afterMs`, or null when the schedule has ended. */
function nextRunAt(s, afterMs, tz) {
	let date = localParts(afterMs, tz).date;
	if (date < s.startsOn) date = s.startsOn;
	for (let i = 0; i < 800; i++, date = addDays(date, 1)) {
		if (s.endsOn && date > s.endsOn) return null;
		if (!runsOn(s, date)) continue;
		const at = localToUtc(date, s.at, tz);
		if (at > afterMs) return at;
	}
	return null;
}
/** What a recurring run covers: the previous day, the previous 7 days, or the previous calendar month (RP4). */
function coveredPeriod(every, runAtMs, tz) {
	const today = localParts(runAtMs, tz).date;
	if (every === "day") return {
		from: addDays(today, -1),
		to: addDays(today, -1)
	};
	if (every === "week") return {
		from: addDays(today, -7),
		to: addDays(today, -1)
	};
	return presetRange("lastMonth", today);
}
const COVERS_TEXT = {
	day: "the previous day",
	week: "the previous 7 days",
	month: "the previous calendar month"
};
const ContentPlaybackQuerySchema = z.object({
	kind: z.enum(["media", "campaign"]),
	id: IdSchema,
	from: LocalDateSchema,
	to: LocalDateSchema,
	tz: IanaTimeZoneSchema
}).refine((q) => q.from <= q.to && days(q.from, q.to) <= 366, {
	message: "Choose up to a year",
	path: ["to"]
});
const ContentPlaybackSchema = z.object({
	plays: NonNegativeIntSchema,
	screenTimeMs: NonNegativeIntSchema,
	players: NonNegativeIntSchema,
	/** Buildings it played in (players in no building are not counted). */
	buildings: NonNegativeIntSchema,
	/** Every day of the range, zeros included. */
	byDay: z.array(z.object({
		day: LocalDateSchema,
		plays: NonNegativeIntSchema,
		screenTimeMs: NonNegativeIntSchema
	}))
});
//#endregion
//#region src/alerts.ts
const ALERT_METRICS = [
	"offline_minutes",
	"out_of_sync_minutes",
	"fallback_minutes",
	"files_failed",
	"errors_last_hour",
	"storage_free_percent",
	"memory_free_mb",
	"battery_percent",
	"health_issue"
];
const AlertMetricSchema = z.enum(ALERT_METRICS);
/** Label, unit and the operator/threshold a new rule starts with. `health_issue` is 1 when a matching issue is open. */
const ALERT_METRIC_META = {
	offline_minutes: {
		label: "Offline time",
		unit: "min",
		description: "Minutes since the player last checked in.",
		defaultOp: "gte",
		defaultThreshold: 10
	},
	out_of_sync_minutes: {
		label: "Out of sync time",
		unit: "min",
		description: "Minutes a newer published revision has been waiting to play.",
		defaultOp: "gte",
		defaultThreshold: 30
	},
	fallback_minutes: {
		label: "Fallback (black screen) time",
		unit: "min",
		description: "Minutes the player has been showing its fallback instead of your content.",
		defaultOp: "gte",
		defaultThreshold: 5
	},
	files_failed: {
		label: "Failed files",
		unit: "files",
		description: "Files the player could not download or verify.",
		defaultOp: "gt",
		defaultThreshold: 0
	},
	errors_last_hour: {
		label: "Errors in the last hour",
		unit: "errors",
		description: "Error lines in the player log during the last hour.",
		defaultOp: "gte",
		defaultThreshold: 20
	},
	storage_free_percent: {
		label: "Free storage",
		unit: "%",
		description: "Free space on the player.",
		defaultOp: "lt",
		defaultThreshold: 10
	},
	memory_free_mb: {
		label: "Free memory",
		unit: "MB",
		description: "Memory the system has available.",
		defaultOp: "lt",
		defaultThreshold: 200
	},
	battery_percent: {
		label: "Battery level",
		unit: "%",
		description: "Players with a battery only.",
		defaultOp: "lt",
		defaultThreshold: 20
	},
	health_issue: {
		label: "Health Check issue open",
		unit: "",
		description: "A Health Check issue of the chosen kind and severity is open on the player.",
		defaultOp: "eq",
		defaultThreshold: 1
	}
};
const ALERT_OPERATORS = [
	"gte",
	"gt",
	"eq",
	"neq",
	"lt",
	"lte"
];
const AlertOperatorSchema = z.enum(ALERT_OPERATORS);
const ALERT_OPERATOR_TEXT = {
	gte: "at least",
	gt: "more than",
	eq: "equal to",
	neq: "not equal to",
	lt: "less than",
	lte: "at most"
};
const compareAlert = (op, v, t) => op === "gte" ? v >= t : op === "gt" ? v > t : op === "eq" ? v === t : op === "neq" ? v !== t : op === "lt" ? v < t : v <= t;
const AlertConditionKindSchema = z.enum([
	"always",
	"once",
	"average"
]);
const ALERT_MAX_WINDOW_MINUTES = 120;
const AlertConditionSchema = z.object({
	metric: AlertMetricSchema,
	operator: AlertOperatorSchema,
	threshold: z.number().min(-1e6).max(1e6),
	kind: AlertConditionKindSchema.default("always"),
	windowMinutes: z.int().min(1).max(120).default(5),
	/** Don't trigger when the window holds no samples (e.g. a player that never sent telemetry). */
	ignoreEmpty: z.boolean().default(true),
	/** health_issue only: null = any kind. */
	healthCode: HealthCodeSchema.nullable().default(null),
	/** health_issue only: this severity or worse. */
	minSeverity: HealthSeveritySchema.default("warning")
}).refine((c) => c.metric !== "health_issue" || c.kind !== "average", {
	message: "Average does not apply to an open issue",
	path: ["kind"]
});
/** onsign: "Filter which devices will be alerted by following All / Any / None of these filters". Empty = every player. */
const AlertFilterSchema = z.object({
	match: z.enum([
		"all",
		"any",
		"none"
	]).default("any"),
	players: z.array(IdSchema).max(1e3).default([]),
	groups: z.array(IdSchema).max(200).default([]),
	tags: TagsSchema.default([])
});
const AlertNotifySchema = z.object({
	emails: z.array(EmailSchema).max(20).default([]),
	/** Also every ADMIN/OWNER of the organization. */
	admins: z.boolean().default(false),
	/** individual = one message per player; aggregate = one per rule per sweep, listing the players. */
	mode: z.enum(["individual", "aggregate"]).default("aggregate"),
	/** Remind while still firing; 0 = never. */
	renotifyMinutes: z.int().min(0).max(10080).default(0),
	onResolve: z.boolean().default(true),
	/** https only; a JSON POST signed with `X-Codot-Signature: sha256=<hex HMAC of the body>`. */
	webhookUrl: z.string().trim().url("Enter a full https:// address").refine((u) => u.startsWith("https://"), "Webhooks must use https").max(2e3).nullable().default(null)
});
const UpsertAlertRequestSchema = z.object({
	/** Blank = generated from the condition. */
	name: z.string().trim().max(120).default(""),
	enabled: z.boolean().default(true),
	condition: AlertConditionSchema,
	filter: AlertFilterSchema.default({
		match: "any",
		players: [],
		groups: [],
		tags: []
	}),
	notify: AlertNotifySchema.default({
		emails: [],
		admins: false,
		mode: "aggregate",
		renotifyMinutes: 0,
		onResolve: true,
		webhookUrl: null
	})
});
const AlertSchema = UpsertAlertRequestSchema.extend({
	id: IdSchema,
	/** Always set: the stored name, or the generated one. */
	name: z.string(),
	/** The webhook signing secret (shown to admins so the receiver can verify). */
	webhookSecret: z.string(),
	firing: NonNegativeIntSchema,
	lastTriggeredAt: IsoDateTimeSchema.nullable(),
	createdBy: z.string(),
	updatedAt: IsoDateTimeSchema
});
const AlertDeliverySchema = z.object({
	status: z.enum([
		"sent",
		"skipped",
		"failed",
		"none"
	]),
	note: z.string().nullable()
});
const AlertIncidentSchema = z.object({
	id: IdSchema,
	alertId: IdSchema,
	screenId: IdSchema,
	playerName: z.string(),
	/** The value that triggered it (the window's latest, or its average). */
	value: z.number().nullable(),
	openedAt: IsoDateTimeSchema,
	resolvedAt: IsoDateTimeSchema.nullable(),
	lastNotifiedAt: IsoDateTimeSchema.nullable(),
	email: AlertDeliverySchema,
	webhook: AlertDeliverySchema
});
/** The editor's live check: who this (unsaved) rule would fire for with the latest samples. */
const AlertCheckResultSchema = z.object({
	inScope: NonNegativeIntSchema,
	firing: z.array(z.object({
		screenId: IdSchema,
		name: z.string(),
		value: z.number().nullable()
	})),
	/** Players in scope without any sample in the window. */
	noData: NonNegativeIntSchema
});
const AlertTestResultSchema = z.object({
	email: AlertDeliverySchema,
	webhook: AlertDeliverySchema
});
/** Per member: the weekly Health Check email (Step 13 deferred item). */
const NotificationPrefsSchema = z.object({ weeklyHealthEmail: z.boolean() });
const fmt = (n) => Number.isInteger(n) ? String(n) : n.toFixed(1);
/** "Offline time is at least 10 min, for 5 min" — the generated name and the list's condition text. */
function describeAlertCondition(c, codeLabel) {
	const m = ALERT_METRIC_META[c.metric];
	if (c.metric === "health_issue") return `${c.healthCode ? `“${codeLabel ? codeLabel(c.healthCode) : c.healthCode}”` : "Any Health Check issue"}${c.minSeverity === "info" ? "" : c.minSeverity === "critical" ? " (critical)" : " (warning or worse)"} is open${c.kind === "always" && c.windowMinutes > 1 ? ` for ${c.windowMinutes} min` : ""}`;
	const unit = m.unit === "%" ? "%" : m.unit ? ` ${m.unit}` : "";
	const value = `${ALERT_OPERATOR_TEXT[c.operator]} ${fmt(c.threshold)}${unit}`;
	if (c.windowMinutes === 1) return `${m.label} is ${value}`;
	const when = c.kind === "always" ? `for ${c.windowMinutes} min` : c.kind === "once" ? `at any time in the last ${c.windowMinutes} min` : `over ${c.windowMinutes} min`;
	return `${m.label} ${c.kind === "average" ? "averages" : "is"} ${value} ${when}`;
}
//#endregion
//#region src/data-feeds.ts
const DATA_FEED_LIMITS = {
	columns: 30,
	rows: 1e3,
	cellChars: 2e3,
	/** Largest source document read. */
	sourceBytes: 2e6,
	minRefreshMinutes: 5,
	maxRefreshMinutes: 10080
};
const DATA_FEED_KINDS = [
	"manual",
	"csv",
	"json",
	"rss"
];
const DataFeedKindSchema = z.enum(DATA_FEED_KINDS);
const DATA_FEED_KIND_LABEL = {
	manual: "Manual",
	csv: "CSV",
	json: "JSON",
	rss: "RSS / Atom"
};
const DATA_COLUMN_TYPES = [
	"text",
	"number",
	"image",
	"link",
	"date"
];
const DataColumnTypeSchema = z.enum(DATA_COLUMN_TYPES);
const DataColumnSchema = z.object({
	/** Stable key apps use ("price"); letters, digits, _ and -. */
	key: z.string().trim().min(1).max(40).regex(/^[A-Za-z0-9_-]+$/, "Use letters, digits, _ or -"),
	name: z.string().trim().min(1, "Name the column").max(80),
	type: DataColumnTypeSchema.default("text")
});
const DataTableSchema = z.object({
	columns: z.array(DataColumnSchema).min(1, "Add at least one column").max(DATA_FEED_LIMITS.columns),
	/** One array per row, a cell per column (missing = ""). */
	rows: z.array(z.array(z.string().max(DATA_FEED_LIMITS.cellChars))).max(DATA_FEED_LIMITS.rows, `At most ${DATA_FEED_LIMITS.rows} rows`)
}).refine((t) => new Set(t.columns.map((c) => c.key.toLowerCase())).size === t.columns.length, {
	message: "Two columns have the same key",
	path: ["columns"]
}).refine((t) => t.rows.every((r) => r.length <= t.columns.length), {
	message: "A row has more cells than there are columns",
	path: ["rows"]
});
const sourceUrl = z.string().trim().url("Enter a full https:// address").refine((u) => u.startsWith("https://"), "Use an https:// address").max(2e3);
/** Where a source feed reads from. `itemsPath`: JSON only — dot path to the list ("data.items"); blank = the top level. */
const DataFeedSourceSchema = z.object({
	url: sourceUrl,
	itemsPath: z.string().trim().max(200).default("")
});
const UpsertDataFeedRequestSchema = z.object({
	name: z.string().trim().min(1, "Enter a name").max(120),
	tags: TagsSchema.default([]),
	kind: DataFeedKindSchema,
	/** Required unless manual. */
	source: DataFeedSourceSchema.nullable().default(null),
	/** Source feeds: check every N minutes; null = only when "Sync now" is pressed. */
	refreshMinutes: z.int().min(DATA_FEED_LIMITS.minRefreshMinutes).max(DATA_FEED_LIMITS.maxRefreshMinutes).nullable().default(60),
	/** false = new data waits in "Pending approval" until an editor approves it (onsign "Updates are automatically marked as approved"). */
	autoApprove: z.boolean().default(true),
	/** Manual feeds: the table itself (saved = approved). Ignored for source feeds. */
	table: DataTableSchema.nullable().default(null)
}).refine((f) => f.kind === "manual" || f.source !== null, {
	message: "Enter the address to read from",
	path: ["source", "url"]
}).refine((f) => f.kind !== "manual" || f.table !== null, {
	message: "Add at least one column",
	path: ["table"]
});
const DataFeedStatusSchema = z.enum([
	"ok",
	"pending",
	"failing",
	"never"
]);
const DataFeedSummarySchema = z.object({
	id: IdSchema,
	name: z.string(),
	tags: z.array(z.string()),
	kind: DataFeedKindSchema,
	source: DataFeedSourceSchema.nullable(),
	refreshMinutes: z.int().nullable(),
	autoApprove: z.boolean(),
	/** ok · pending (an update waits for approval) · failing (the last check failed) · never (not read yet). */
	status: DataFeedStatusSchema,
	rows: NonNegativeIntSchema,
	columns: NonNegativeIntSchema,
	/** Bumps each time a new table is approved (apps reload on change). */
	version: NonNegativeIntSchema,
	/** onsign "Web Feeds Status": Checked · Changed · Items. */
	lastCheckedAt: IsoDateTimeSchema.nullable(),
	lastChangedAt: IsoDateTimeSchema.nullable(),
	nextCheckAt: IsoDateTimeSchema.nullable(),
	lastError: z.string().nullable(),
	/** Consecutive failed checks. */
	failures: NonNegativeIntSchema,
	updatedAt: IsoDateTimeSchema
});
const DataFeedPendingSchema = DataTableSchema.and(z.object({
	fetchedAt: IsoDateTimeSchema,
	/** Compared with the approved table. */
	diff: z.object({
		added: NonNegativeIntSchema,
		removed: NonNegativeIntSchema,
		changed: NonNegativeIntSchema,
		columnsChanged: z.boolean()
	})
}));
const DataFeedSchema = DataFeedSummarySchema.extend({
	/** The approved table (what apps get); null until the first successful read. */
	table: DataTableSchema.nullable(),
	pending: DataFeedPendingSchema.nullable()
});
/** The create form's "Preview": parses an address (or pasted CSV) without saving. */
const DataFeedPreviewRequestSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.enum([
		"csv",
		"json",
		"rss"
	]),
	source: DataFeedSourceSchema
}), z.object({
	kind: z.literal("csv_text"),
	text: z.string().min(1, "Paste or choose a CSV file").max(DATA_FEED_LIMITS.sourceBytes),
	/** All rows (up to the limit) instead of the first 20: a new manual feed's starting table. */
	all: z.boolean().default(false)
})]);
const DataFeedPreviewSchema = DataTableSchema.and(z.object({
	totalRows: NonNegativeIntSchema,
	truncated: z.boolean()
}));
/** "Price (SAR)" → "price_sar"; unique within `taken`. */
function columnKey(name, taken = /* @__PURE__ */ new Set()) {
	const base = name.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 36) || "column";
	let key = base;
	for (let i = 2; taken.has(key); i++) key = `${base}_${i}`;
	return key;
}
//#endregion
//#region src/monitoring.ts
const SCREENSHOT_LIMITS = {
	/** Upload cap (the player aims at ≤ 300 KB). */
	maxBytes: 6e5,
	/** Long edge the player captures at. */
	maxEdge: 960,
	/** One per player per this many seconds at most. */
	minGapSeconds: 60,
	retentionDays: 7,
	maxPerScreen: 700,
	/** Files the player keeps while it can't upload. */
	playerQueue: 24
};
const SCREENSHOT_INTERVALS = [
	5,
	10,
	15,
	30,
	60,
	120,
	360
];
/** Multipart text field sent BEFORE the `file` part. */
const ScreenshotUploadFieldsSchema = z.object({ capturedAt: IsoDateTimeSchema });
const SCREENSHOT_UPLOAD_FILE_FIELD = "file";
const ScreenshotSchema = z.object({
	id: IdSchema,
	screenId: IdSchema,
	capturedAt: IsoDateTimeSchema,
	url: z.string(),
	width: PositiveIntSchema,
	height: PositiveIntSchema,
	sizeBytes: NonNegativeIntSchema
});
/** One tile of the Monitoring page. */
const MonitoringTileSchema = z.object({
	screenId: IdSchema,
	name: z.string(),
	lastSeenAt: IsoDateTimeSchema.nullable(),
	groupIds: z.array(IdSchema),
	/** Effective schedule (null = off). */
	intervalMinutes: z.int().nullable(),
	latest: ScreenshotSchema.nullable()
});
const ScreenshotPageSchema = z.object({
	items: z.array(ScreenshotSchema),
	nextBefore: IsoDateTimeSchema.nullable()
});
const MONITORING_PAGE_SIZE = 50;
/** A refresh reloads every tile already on screen in one request, so the cap is larger than a page. */
const MONITORING_PAGE_MAX = 500;
/** The Monitoring wall, a page at a time (GET monitoring-page); `GET monitoring` still returns every tile. */
const MonitoringQuerySchema = z.object({
	/** Player name contains this (case-insensitive). */
	q: z.string().trim().max(200).default(""),
	group: IdSchema.optional(),
	/** Only these players (e.g. the one opened from a link); `ids=a,b`. */
	ids: z.string().transform((s) => [...new Set(s.split(",").map((x) => x.trim()).filter(Boolean))]).pipe(z.array(IdSchema).max(200)).optional(),
	offset: z.coerce.number().int().min(0).default(0),
	limit: z.coerce.number().int().min(1).max(500).default(50)
});
const MonitoringPageSchema = z.object({
	/** By name (numeric-aware). */
	items: z.array(MonitoringTileSchema),
	/** Tiles matching the query. */
	total: z.int(),
	/** Offset of the next page; null on the last one. */
	nextOffset: z.int().nullable(),
	/** Every player the member may see, whatever the filters (first page only): how many, and how many take screenshots. */
	summary: z.object({
		all: z.int(),
		scheduled: z.int()
	}).nullable()
});
//#endregion
//#region src/account.ts
/** Phase 17.7: CMS languages. */
const UiLanguageSchema = z.enum(["en", "ar"]);
/** Phase 17.7: digits in Arabic — Arabic-Indic ٠١٢٣ (default) or Western 0123. English always uses Western. */
const UiNumeralsSchema = z.enum(["arab", "latn"]);
/** Phase 17.6: avatar uploads (square-cropped to 256 px WebP on the server). */
const AVATAR_LIMITS = {
	maxBytes: 5242880,
	size: 256
};
const AVATAR_UPLOAD_FILE_FIELD = "file";
const UpdateProfileRequestSchema = z.object({
	name: z.string().trim().min(1, "Enter your name").max(120),
	/** Phase 17.6: every date and time in the CMS is shown in this zone; null = the browser's. Omitted = unchanged. */
	timeZone: IanaTimeZoneSchema.nullable().optional(),
	/** Phase 17.7. Omitted = unchanged. */
	language: UiLanguageSchema.optional(),
	numerals: UiNumeralsSchema.optional()
});
const ProfileSchema = z.object({
	id: IdSchema,
	name: z.string(),
	email: z.string(),
	timeZone: IanaTimeZoneSchema.nullable().default(null),
	language: UiLanguageSchema.default("en"),
	numerals: UiNumeralsSchema.default("arab"),
	avatarUrl: z.string().nullable().default(null)
});
const ChangePasswordRequestSchema = z.object({
	currentPassword: z.string().min(1, "Enter your current password").max(128),
	newPassword: PasswordSchema
}).refine((b) => b.newPassword !== b.currentPassword, {
	message: "Choose a password different from the current one",
	path: ["newPassword"]
});
/** A signed-in browser (session). */
const AccountSessionSchema = z.object({
	id: IdSchema,
	userAgent: z.string().nullable(),
	ip: z.string().nullable(),
	createdAt: IsoDateTimeSchema,
	lastUsedAt: IsoDateTimeSchema,
	/** The session making this request. */
	current: z.boolean()
});
//#endregion
//#region src/fonts.ts
/**
* Custom fonts: an organization uploads its brand fonts (TTF/OTF) and picks one per app (text, ticker, clock …).
* Players download the font file with the app (verified like any file) and fall back to the system font when it's
* missing. WOFF/WOFF2 are web-only formats Android can't load, so they are refused with a clear message.
*/
const FONT_LIMITS = {
	/** Per file: brand fonts are 20 KB – 2 MB; CJK families can reach ~15 MB. */
	maxBytes: 20971520,
	/** Fonts per organization. */
	maxPerOrganization: 50
};
const FONT_UPLOAD_FILE_FIELD = "file";
const FontFormatSchema = z.enum(["ttf", "otf"]);
const FontRecordSchema = z.object({
	id: IdSchema,
	/** Display name (defaults to the font's own family + style, e.g. "Brand Sans Bold"). */
	name: z.string().min(1).max(120),
	/** The family name inside the file. */
	family: z.string().min(1).max(120),
	format: FontFormatSchema,
	fileUrl: z.string(),
	sizeBytes: PositiveIntSchema,
	/** Apps that use it. */
	usedBy: z.int().min(0),
	createdAt: IsoDateTimeSchema
});
const UpdateFontRequestSchema = z.object({ name: z.string().trim().min(1).max(120) });
//#endregion
//#region src/admin.ts
/**
* Platform admin (the Displaro operators' area at /admin). Separate from organization roles: a platform role is
* set on a person, needs an authenticator-app code per session, and every action is logged with a reason.
*/
const PlatformRoleSchema = z.enum([
	"SUPER_ADMIN",
	"SUPPORT",
	"BILLING",
	"READ_ONLY"
]);
/** How long one authenticator-app check opens the admin area for this session. */
const ADMIN_VERIFY_TTL_HOURS = 12;
/** Deleted organizations can be restored for this long. */
const ORG_DELETE_GRACE_DAYS = 30;
/** "View as customer" visits end by themselves after this long. */
const SUPPORT_VISIT_MINUTES = 60;
const AdminMeSchema = z.object({
	role: PlatformRoleSchema,
	/** The authenticator app is set up. */
	totpEnabled: z.boolean(),
	/** This session passed the check (within ADMIN_VERIFY_TTL_HOURS). */
	verified: z.boolean(),
	verifiedUntil: IsoDateTimeSchema.nullable()
});
const TotpSetupSchema = z.object({
	secret: z.string(),
	otpauthUrl: z.string()
});
const TotpCodeSchema = z.object({ code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code") });
/** Every admin action says why (it is logged, and shown to the customer where it concerns them). */
const AdminReasonSchema = z.string().trim().min(3, "Say why (it is logged)").max(500);
const AdminOrgStateSchema = z.enum([
	"active",
	"suspended",
	"deleted"
]);
const AdminOrgSummarySchema = z.object({
	id: IdSchema,
	name: z.string(),
	planTier: PlanTierSchema,
	subscriptionStatus: SubscriptionStatusSchema,
	trialEndsAt: IsoDateTimeSchema.nullable(),
	purchasedSeats: z.number().int(),
	players: z.number().int(),
	playersOnline: z.number().int(),
	storageBytes: z.number(),
	maxStorageBytes: z.number(),
	owner: z.object({
		id: IdSchema,
		name: z.string(),
		email: z.string()
	}).nullable(),
	members: z.number().int(),
	state: AdminOrgStateSchema,
	suspendedReason: z.string().nullable(),
	deleteAfter: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema
});
const AdminOrgDetailSchema = AdminOrgSummarySchema.extend({
	memberList: z.array(z.object({
		id: IdSchema,
		name: z.string(),
		email: z.string(),
		role: RoleSchema,
		lastLoginAt: IsoDateTimeSchema.nullable(),
		locked: z.boolean()
	})),
	billingStatus: z.string().nullable(),
	country: z.string().nullable()
});
const AdminOrgListQuerySchema = z.object({
	q: z.string().trim().max(100).optional(),
	filter: z.enum([
		"all",
		"trial",
		"trial_ending",
		"paying",
		"suspended",
		"deleted"
	]).default("all"),
	cursor: z.string().max(100).optional(),
	limit: z.coerce.number().int().min(1).max(100).default(50)
});
const AdminOrgPageSchema = z.object({
	items: z.array(AdminOrgSummarySchema),
	nextCursor: z.string().nullable(),
	total: z.number().int()
});
/** Change plan, seats or trial end — at least one, plus why. */
const AdminUpdateOrgSchema = z.object({
	planTier: PlanTierSchema.optional(),
	purchasedSeats: z.number().int().min(1).max(1e5).optional(),
	trialEndsAt: IsoDateTimeSchema.optional(),
	reason: AdminReasonSchema
}).refine((b) => b.planTier !== void 0 || b.purchasedSeats !== void 0 || b.trialEndsAt !== void 0, "Nothing to change");
const AdminReasonBodySchema = z.object({ reason: AdminReasonSchema });
const AdminUserSummarySchema = z.object({
	id: IdSchema,
	name: z.string(),
	email: z.string(),
	platformRole: PlatformRoleSchema.nullable(),
	lastLoginAt: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema,
	locked: z.boolean(),
	lockedReason: z.string().nullable(),
	activeSessions: z.number().int(),
	twoFactorEnabled: z.boolean().default(false),
	organizations: z.array(z.object({
		id: IdSchema,
		name: z.string(),
		role: RoleSchema
	}))
});
const AdminUserListQuerySchema = z.object({
	q: z.string().trim().max(254).optional(),
	limit: z.coerce.number().int().min(1).max(100).default(50)
});
/** A one-time password link to hand over (copy, chat…) — works without email. */
const AdminResetLinkSchema = z.object({
	url: z.string(),
	expiresAt: IsoDateTimeSchema
});
const AdminActionSchema = z.object({
	id: IdSchema,
	actorEmail: z.string(),
	action: z.string(),
	subject: z.string(),
	reason: z.string(),
	organizationId: IdSchema.nullable(),
	targetUserId: IdSchema.nullable(),
	details: z.record(z.string(), z.unknown()).nullable(),
	createdAt: IsoDateTimeSchema
});
const AdminActionPageSchema = z.object({
	items: z.array(AdminActionSchema),
	nextCursor: z.string().nullable()
});
const AdminActionQuerySchema = z.object({
	organizationId: IdSchema.optional(),
	cursor: z.string().max(100).optional(),
	limit: z.coerce.number().int().min(1).max(100).default(50)
});
const AdminOverviewSchema = z.object({
	organizations: z.number().int(),
	trials: z.number().int(),
	trialsEndingThisWeek: z.number().int(),
	paying: z.number().int(),
	suspended: z.number().int(),
	players: z.number().int(),
	playersOnline: z.number().int(),
	users: z.number().int(),
	signupsLast7Days: z.number().int(),
	/** The lists under the numbers: what to look at today. */
	trialsEnding: z.array(z.object({
		id: IdSchema,
		name: z.string(),
		trialEndsAt: IsoDateTimeSchema,
		players: z.number().int(),
		ownerEmail: z.string().nullable()
	})).default([]),
	recentSignups: z.array(z.object({
		id: IdSchema,
		name: z.string(),
		createdAt: IsoDateTimeSchema,
		planTier: PlanTierSchema,
		ownerEmail: z.string().nullable()
	})).default([]),
	recentActions: z.array(AdminActionSchema).default([])
});
//#endregion
//#region src/content-fit.ts
const RESERVE = 268435456;
/** Every file a player downloads for an asset (mirrors the players' manifestFiles). */
function filesOf(a) {
	switch (a.kind) {
		case "video": return a.poster ? [a, a.poster] : [a];
		case "image":
		case "audio": return [a];
		case "app": return [a, ...a.feeds];
		case "widget": return [...a.font ? [a.font] : [], ...a.feed ? [a.feed] : []];
		case "pdf": return a.pages;
	}
}
/** Bytes of the distinct files a manifest needs on the player. */
function manifestContentBytes(m) {
	const seen = /* @__PURE__ */ new Map();
	for (const a of m.assets) for (const f of filesOf(a)) seen.set(f.sha256, f.sizeBytes);
	let total = 0;
	for (const n of seen.values()) total += n;
	return total;
}
/**
* About how much content this player can hold: its free space plus what its stored content already uses, less the
* players' own reserve (Android: 256 MiB; the web engine: at most a tenth, for small TV-browser quotas).
*/
function playerContentCapacity(hb) {
	const s = hb.storage;
	if (!s) return null;
	const room = s.freeBytes + s.mediaCacheBytes;
	const android = (hb.app.platform ?? "android") === "android";
	return Math.max(0, room - (android ? RESERVE : Math.min(RESERVE, Math.floor(room / 10))));
}
/** The content's size and the player's capacity when it does not fit; null when it fits or nothing is known. */
function contentTooBig(m, hb) {
	if (!m || !hb) return null;
	const capacity = playerContentCapacity(hb);
	const need = manifestContentBytes(m);
	return capacity !== null && need > capacity ? {
		need,
		capacity
	} : null;
}
/** "152 MB" (decimal, like the players' storage screens). */
const megabytes = (n) => `${Math.round(n / 1e6)} MB`;
//#endregion
export { ACCEPTED_AUDIO_TYPES, ACCEPTED_DOCUMENT_TYPES, ACCEPTED_IMAGE_TYPES, ACCEPTED_UPLOAD_TYPES, ACCEPTED_VIDEO_TYPES, ACTIVITY_COUNT_CAP, ACTIVITY_FACET_LIMIT, ACTIVITY_GROUPS, ACTIVITY_RETENTION_MONTHS, ADMIN_VERIFY_TTL_HOURS, ADVANCED_FEATURES, ALERT_MAX_WINDOW_MINUTES, ALERT_METRICS, ALERT_METRIC_META, ALERT_OPERATORS, ALERT_OPERATOR_TEXT, ANALYTICS_MAX_DAYS, ANNUAL_DISCOUNT, API_GLOBAL_PREFIX, ATTRIBUTE_TARGETS, ATTRIBUTE_TYPES, ATTRIBUTE_TYPE_LABEL, AVATAR_LIMITS, AVATAR_UPLOAD_FILE_FIELD, AcceptInvitationRequestSchema, AccountSessionSchema, AcknowledgeIssuesRequestSchema, ActivityCategorySchema, ActivityEventSchema, ActivityFacetsQuerySchema, ActivityFacetsSchema, ActivityGroupSchema, ActivityPageSchema, ActivityQuerySchema, ActivitySeveritySchema, AdminActionPageSchema, AdminActionQuerySchema, AdminActionSchema, AdminMeSchema, AdminOrgDetailSchema, AdminOrgListQuerySchema, AdminOrgPageSchema, AdminOrgStateSchema, AdminOrgSummarySchema, AdminOverviewSchema, AdminReasonBodySchema, AdminReasonSchema, AdminResetLinkSchema, AdminUpdateOrgSchema, AdminUserListQuerySchema, AdminUserSummarySchema, AlertCheckResultSchema, AlertConditionKindSchema, AlertConditionSchema, AlertDeliverySchema, AlertFilterSchema, AlertIncidentSchema, AlertMetricSchema, AlertNotifySchema, AlertOperatorSchema, AlertSchema, AlertTestResultSchema, AnalyticsOverviewSchema, AnalyticsPlayerSchema, AnalyticsQuerySchema, ApiErrorBodySchema, ApiErrorCode, ApiRoutes, AppAssetSchema, AppFeedFileSchema, AppFeedSlotSchema, AppInstanceConfigSchema, AppPackageManifestSchema, AppSettingSchema, AppSettingValuesSchema, AttributeDefinitionSchema, AttributeTargetSchema, AttributeTypeSchema, AttributeValueSchema, AttributeValuesSchema, AudioAssetSchema, AuthSessionSchema, BILLING_GRACE_DAYS, BILLING_MAX_SEATS, BILLING_MIN_CHARGE_CENTS, BILLING_RETRY_DAYS, BUILT_IN_COMPOSITION_TEMPLATES, BillingContactSchema, BillingIntervalSchema, BillingOverviewSchema, BillingQuoteSchema, BillingStatusSchema, BrightnessSchema, BuildingInfoSchema, BulkPlacementRequestSchema, CHART_KINDS, CMS_NAMESPACE, COMMAND_MIN_ROLE, COMPAT_SHORT_EDGE, COMPOSITION_DOC_FILE, COMPOSITION_ENTRY, COMPOSITION_LIMITS, COMPOSITION_WEATHER_ORIGIN, CONTRACT_SCHEMA_VERSION, COVERS_TEXT, CUSTOM_APP_ENTRY, CUSTOM_APP_IGNORED, CUSTOM_APP_LIMITS, CUSTOM_APP_MANIFEST_FILE, CUSTOM_APP_UPLOAD_FILE_FIELD, CalendarEventSchema, CalendarFeedSchema, CalendarUrlSchema, CalendarWidgetSchema, CampaignAudioSchema, CanvasSchema, ChangePasswordRequestSchema, ChartWidgetSchema, CheckoutRequestSchema, CheckoutResultSchema, CommandResultEventSchema, CommandResultSchema, CompileFailureSchema, CompileIssueSchema, CompileResultSchema, CompositionDocSchema, CompositionElementSchema, CompositionFeedSchema, CompositionInfoSchema, CompositionRuntimeSettingsSchema, CompositionTemplateSchema, ContentActivityQuerySchema, ContentActivitySchema, ContentDeleteImpactSchema, ContentDetachRequestSchema, ContentDetachResultSchema, ContentFolderSchema, ContentMuteRequestSchema, ContentPersonSchema, ContentPlaybackQuerySchema, ContentPlaybackSchema, ContentProblemReasonSchema, ContentProblemSchema, ContentRefSchema, ContentReplaceRequestSchema, ContentReplaceResultSchema, ContentReplaceRowsSchema, ContentRestrictionRequestSchema, ContentUsageSchema, CountdownWidgetSchema, CountryCodeSchema, CreateAttributeRequestSchema, CreateFolderRequestSchema, CreateInvitationRequestSchema, CreateMediaAssetRequestSchema, CreateScreenRequestSchema, CustomAppFileSchema, CustomAppSchema, CustomAppSummarySchema, CustomAppUploadFieldsSchema, CustomAppVersionSchema, DASHBOARD_PROVIDERS, DATA_COLUMN_TYPES, DATA_FEED_KINDS, DATA_FEED_KIND_LABEL, DATA_FEED_LIMITS, DEFAULT_DATA_SAVING, DEFAULT_DEVICE_LOGS, DEFAULT_HEALTH_SETTINGS, DEFAULT_ICE_SERVERS, DEFAULT_IDLE_SCREEN, DEFAULT_LAYOUT_DURATION_MS, DEFAULT_LOCATION_MINUTES, DEFAULT_PDF_PAGE_MS, DEFAULT_PDF_PAGE_TRANSITION, DEFAULT_PLAYER_SETTINGS, DEFAULT_RESTART_SCHEDULE, DEFAULT_SCREENSHOT_SCHEDULE, DEVICE_LOG_LEVELS, DEVICE_LOG_UPLOAD_MINUTES, DEVICE_METRICS_INTERVAL_MS, DEVICE_NAMESPACE, DEVICE_PERMISSION_KEYS, DOCUMENT_ROW_LIMIT, DailyWindowSchema, DashboardCheckSchema, DashboardWidgetSchema, DataColumnSchema, DataColumnTypeSchema, DataFeedKindSchema, DataFeedPendingSchema, DataFeedPreviewRequestSchema, DataFeedPreviewSchema, DataFeedSchema, DataFeedSourceSchema, DataFeedStatusSchema, DataFeedSummarySchema, DataSavingSchema, DataTableSchema, DeleteUnpublishSchema, DeviceCapabilitiesSchema, DeviceCredentialsSchema, DeviceInfoSchema, DeviceLocationSchema, DeviceLogItemSchema, DeviceLogLevelSchema, DeviceLogSettingsSchema, DeviceLogsBatchSchema, DeviceMetricSampleSchema, DeviceMetricsBatchSchema, DevicePairingClaimSchema, DevicePairingExchangeRequestSchema, DevicePairingExchangeResponseSchema, DevicePairingInitiateRequestSchema, DevicePairingInitiateResponseSchema, DevicePairingPreviewSchema, DevicePermissionKeySchema, DevicePermissionsSchema, DeviceRepairRequestSchema, EmailSchema, FOLDER_MAX_DEPTH, FONT_FAMILIES, FONT_LIMITS, FONT_UPLOAD_FILE_FIELD, FORMAT_LABEL, FallbackReasonSchema, FeedBindingSchema, FitSchema, FontFileSchema, FontFormatSchema, FontRecordSchema, GoogleSlidesWidgetSchema, GroupContentSchema, GroupSyncRequestSchema, GroupsHealthSchema, HEALTH_CODES, HEARTBEAT_INTERVAL_MS, HealthCodeSchema, HealthIssueSchema, HealthIssuesPageSchema, HealthIssuesQuerySchema, HealthNotificationsSchema, HealthSettingsSchema, HealthSeveritySchema, HealthSubjectKindSchema, HealthSummarySchema, HeartbeatAckSchema, HeartbeatBatchSchema, HeartbeatPayloadSchema, HexColorSchema, HotspotSchema, HttpUrlSchema, IMAGE_RENDITION_SHORT_EDGES, IanaTimeZoneSchema, IceCandidateSchema, IceServerSchema, IceServersSchema, IdSchema, IdleScreenSchema, IdsQuerySchema, ImageAssetSchema, InteractionActionSchema, InteractionAppliesToSchema, InteractionSchema, InteractionTargetSchema, InteractionTriggerSchema, InvitationCreatedSchema, InvitationPreviewSchema, InvitationSchema, InvoiceLineSchema, InvoiceSchema, IpAddressSchema, IsoDateTimeSchema, IsoWeekdaySchema, LEGACY_WIDGET_TYPES, LIBRARY_PAGE_MAX, LIBRARY_PAGE_SIZE, LIVE_INPUT_CHANNEL, LIVE_SWIPE_MAX_MS, LIVE_VIEW_MAX_MS, LIVE_VIEW_MIN_ROLE, LOGS_RETENTION_DAYS, LOOKUP_IDS_MAX, LatitudeSchema, LayoutSceneSchema, LayoutSchema, LayoutSectionSchema, LayoutTemplateSchema, LayoutZoneInputSchema, LayoutZoneItemInputSchema, LibraryPageSchema, LibraryQuerySchema, LibraryRefSchema, LibrarySummarySchema, LibraryTypeSchema, LiveInputSchema, LocalDateSchema, LocalTimeSchema, LocationMinutesSchema, LoginRequestSchema, LongitudeSchema, LoopGroupSchema, LoopItemSchema, LoopSyncSchema, MANIFEST_SCHEMA_VERSION, MAX_APP_ZONES_PER_LAYOUT, MAX_ASSETS_PER_MANIFEST, MAX_ATTRIBUTES_PER_TARGET, MAX_ATTRIBUTE_LIST, MAX_ATTRIBUTE_TEXT, MAX_CANVAS_PX, MAX_CONCURRENT_VIDEOS, MAX_FUTURE_SKEW_MS, MAX_HEARTBEATS_PER_BATCH, MAX_ITEMS_PER_PASS, MAX_ITEMS_PER_ZONE, MAX_LAYOUTS_PER_SCHEDULE, MAX_LAYOUT_DURATION_MS, MAX_LOGS_PER_BATCH, MAX_LOG_MESSAGE_CHARS, MAX_LOG_STACK_CHARS, MAX_METRIC_SAMPLES_PER_BATCH, MAX_PDF_PAGES, MAX_POP_EVENTS_PER_BATCH, MAX_RULE_DEPTH, MAX_SCHEDULES_PER_MANIFEST, MAX_SCREENSHOT_BASE64_CHARS, MAX_TRANSITION_MS, MAX_ZONES_PER_LAYOUT, MEDIA_LIMITS, MEDIA_STATUSES, MEDIA_STATUS_POLL_MS, MEDIA_UPLOAD_FILE_FIELD, MEETING_ROOM_LAYOUTS, MEETING_ROOM_UNKNOWN_COLOR, METRICS_RANGES, METRICS_RETENTION_DAYS, MIN_ITEM_DURATION_MS, MIN_LAYOUT_DURATION_MS, MONITORING_PAGE_MAX, MONITORING_PAGE_SIZE, MacAddressSchema, ManifestActionSchema, ManifestInteractionSchema, ManifestPointerSchema, ManifestPublishedEventSchema, ManifestTriggerSchema, MediaAssetRecordSchema, MediaAssetSchema, MediaKindSchema, MediaListQuerySchema, MediaStatusSchema, MediaUploadFieldsSchema, MediaVersionSchema, MeetingRoomWidgetSchema, MemberAccessSchema, MemberSchema, MemoryStatusSchema, MenuBoardWidgetSchema, MenuItemSchema, MenuSectionSchema, MetricBucketSchema, MetricsRangeSchema, MonitoringPageSchema, MonitoringQuerySchema, MonitoringTileSchema, MoveContentRequestSchema, NO_RESTRICTION, NetworkStatusSchema, NetworkTransportSchema, NonNegativeIntSchema, NotificationPrefsSchema, ORG_DELETE_GRACE_DAYS, OrganizationMembershipSchema, OrganizationSecuritySchema, OrganizationSettingsSchema, OrientationSchema, OutsideHoursSchema, PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX, PAIRING_CODE_ALPHABET, PAIRING_CODE_LENGTH, PAIRING_POLL_INTERVAL_MS, PAIRING_TTL_SEC, PASSWORD_RESET_TTL_MIN, PEOPLE_STYLES, PERIOD_PRESETS, PHOTO_STYLES, PLACEHOLDER_MAC_ADDRESS, PLAN_CATALOG, PLAN_RANK, PLAYBACK_CAUSES, PLAYBACK_CODES, PLAYERS_PAGE_MAX, PLAYERS_PAGE_SIZE, PLAYER_PLATFORMS, PRESET_LABEL, PaidPlanSchema, PairingCodeSchema, PairingNonceSchema, PasswordResetConfirmSchema, PasswordResetPreviewSchema, PasswordResetRequestSchema, PasswordSchema, PdfAssetSchema, PdfPageSchema, PdfPageTransitionSchema, PeopleWidgetSchema, PhotosWidgetSchema, PlacementExclusionSchema, PlacementSchema, PlacementSourceSchema, PlanFeatureSchema, PlanRequestSchema, PlanTierSchema, PlatformRoleSchema, PlayWindowSchema, PlaybackHealthSchema, PlaybackProblemSchema, PlaybackStateSchema, PlaybackStatusSchema, PlaybackTypeSchema, PlayerContentSchema, PlayerInteractionsSchema, PlayerLoopSchema, PlayerPlatformSchema, PlayerRssFeedSchema, PlayerSettingsOverridesSchema, PlayerSettingsSchema, PlayerStatusSchema, PlayerSyncSchema, PlayerUpdateSchema, PlayersPageSchema, PlayersQuerySchema, PlayersReachRequestSchema, PlayersReachSchema, PlayersStatusFilterSchema, PlayersSummarySchema, PlaylistContentRefSchema, PlaylistItemInputSchema, PlaylistItemSchema, PlaylistPlaybackModeSchema, PlaylistPlaybackOrderSchema, PlaylistSchema, PlaylistTypeSchema, PositiveIntSchema, PrayerTimeWidgetSchema, PresenceBatchEventSchema, PresenceQuerySchema, PresenceSnapshotSchema, ProfileSchema, ProofOfPlayBatchSchema, ProofOfPlayEventSchema, PublishRequestSchema, PublishResultSchema, PublishedCountsSchema, QrCodeWidgetSchema, REGION_MAX_POINTS, REGION_MAX_RADIUS_M, REGION_MIN_RADIUS_M, REMOTE_COMMAND_ACTIONS, REMOTE_KEYS, RENDITION_MIN_STEP, REPORT_FILE_RETENTION_DAYS, REPORT_TYPES, REPORT_TYPE_IDS, ROLE_RANK, RecoveryCodesSchema, RegionContentRequestSchema, RegionContentSchema, RegionDetailSchema, RegionMembershipSchema, RegionPlayerSchema, RegionPointSchema, RegionSchema, RegionShapeSchema, RemoteCommandActionSchema, RemoteCommandRequestSchema, RemoteCommandSchema, RemoteKeySchema, RemoteLeaveSchema, RenameTagRequestSchema, ReorderRequestSchema, ReportFormatSchema, ReportPeriodSchema, ReportPreviewSchema, ReportRunSchema, ReportRunStatusSchema, ReportScheduleSchema, ReportSchema, ReportScopeSchema, ReportTypeSchema, RestartScheduleSchema, RestrictionSchema, RoleSchema, RssFeedUrlSchema, RssPreviewSchema, RssWidgetSchema, RuleConditionSchema, RuleGroupSchema, SCREENSHOT_INTERVALS, SCREENSHOT_LIMITS, SCREENSHOT_UPLOAD_FILE_FIELD, SCREEN_OFFLINE_AFTER_MS, SEVERITY_RANK, SOCIAL_PLATFORMS, SOCIAL_WALL_STYLES, SOCKET_PATH, SPREADSHEET_ROW_LIMIT, STICKERS, STREAM_KINDS, SUPPORT_VISIT_MINUTES, ScheduleSchema, SchemaVersionSchema, ScreenActivityItemSchema, ScreenActivityPageSchema, ScreenActivityQuerySchema, ScreenDeviceSchema, ScreenGroupSchema, ScreenListQuerySchema, ScreenLogItemSchema, ScreenLogsPageSchema, ScreenLogsQuerySchema, ScreenManifestSchema, ScreenMetricsQuerySchema, ScreenMetricsSchema, ScreenPresenceAckSchema, ScreenPresenceEventSchema, ScreenResolutionSchema, ScreenSnapshotSchema, ScreenStatusSchema, ScreenSummarySchema, ScreenshotPageSchema, ScreenshotScheduleSchema, ScreenshotSchema, ScreenshotUploadFieldsSchema, SetAttributeValuesRequestSchema, SetContentAttributesRequestSchema, SetRegionPlayerRequestSchema, Sha256HexSchema, SignupRequestSchema, SocialWallWidgetSchema, SocketAckSchema, SocketErrorCode, SocketEvents, StickerSchema, StorageStatusSchema, StreamCheckSchema, StreamWidgetSchema, SubscriptionStatusSchema, SwitchOrganizationRequestSchema, SyncErrorCodeSchema, SyncStatusSchema, TagDeleteImpactSchema, TagDetailSchema, TagGroupSchema, TagMembersRequestSchema, TagPlayerSchema, TagPublicationRowSchema, TagRestrictionRowSchema, TagSchema, TagSummarySchema, TagsSchema, TelemetryIngestAckSchema, TextWidgetSchema, TickerWidgetSchema, TimeWidgetSchema, TimeWindowSchema, TimelineItemSchema, TotpCodeSchema, TotpSetupSchema, TransitionSchema, TwoFactorChallengeSchema, TwoFactorDisableSchema, TwoFactorLoginSchema, TwoFactorStatusSchema, UiLanguageSchema, UiNumeralsSchema, UpdateAttributeRequestSchema, UpdateContentTagsRequestSchema, UpdateCustomAppRequestSchema, UpdateFolderRequestSchema, UpdateFontRequestSchema, UpdateMediaAssetRequestSchema, UpdateMemberRoleRequestSchema, UpdateOrganizationRequestSchema, UpdatePlacementRequestSchema, UpdatePlayerContentRequestSchema, UpdateProfileRequestSchema, UpdateScreenRequestSchema, UpdateTagsRequestSchema, UpsertAlertRequestSchema, UpsertCompositionTemplateRequestSchema, UpsertDataFeedRequestSchema, UpsertInteractionRequestSchema, UpsertLayoutRequestSchema, UpsertLayoutTemplateRequestSchema, UpsertPlaylistRequestSchema, UpsertRegionRequestSchema, UpsertReportRequestSchema, UpsertScreenGroupRequestSchema, VIDEO_RENDITION_SHORT_EDGES, VideoAssetSchema, VideoCodecSchema, VolumeSchema, WIDGET_TYPES, WeatherWidgetSchema, WebPageWidgetSchema, WebRtcAnswerSchema, WebRtcHangupSchema, WebRtcOfferSchema, WidgetAssetSchema, WidgetConfigSchema, WidgetLocationSchema, WidgetTypeSchema, WorkingScheduleSchema, WorldClockWidgetSchema, YouTubeWidgetSchema, ZoneRoleSchema, ZoneSchema, addDays, addMonth, addPeriod, agenda, analyzeDecoderBudget, apiPath, birthday, calendarFetchUrl, canManageRole, chartAxis, chartData, chartSvg, checkAppZipEntries, checkLayoutDecoderBudget, choosePrimaryZone, cleanAttributeValues, columnKey, compareAlert, compareVersions, compositionAssetIds, compositionFontIds, contentTooBig, coveredPeriod, dashboardEmbedUrl, dashboardProvider, daysUntil, describeAlertCondition, directoryGrid, distanceMeters, effectiveSettings, elementLabel, formatAttributeValue, formatChartValue, formatPairingCode, googleSlidesEmbedUrl, guessSocialColumns, inRegion, inheritedSettings, insidePolygon, isInsideRegion, isRtlText, isRuleGroup, isoWeekday, localDate, localParts, localToUtc, lockedElementChanges, manifestContentBytes, manifestFileName, meetingRoomLabels, meetingRoomPortrait, megabytes, monthlyCents, nextRunAt, normalizePairingCode, organizedFields, parseAppPackageManifest, parseChartNumber, parseGoogleSlidesUrl, parseUnpublishQuery, parseYouTubeUrl, peopleGrid, peopleLabels, peopleList, peoplePerPage, periodCents, photoList, photoUrl, pickRendition, planChange, planCompatRendition, planHasFeature, planRenditions, playbackCause, playerContentCapacity, playerOsLabel, playerPeriodCents, polygonCircle, postInitials, postPlainText, postTime, presetRange, qrMatrixSvg, remainingFraction, resolveAppSettings, roleAtLeast, roomStatus, similarTags, socialGrid, socialLabels, socialPerPage, socialPlatform, socialPostList, soundOf, streamKind, streamKindOfContentType, streamRetryDelayMs, subscriptionStatus, tagKey, timeAgo, unpublishQuery, wallTiles, widgetLocation };

//# sourceMappingURL=index.js.map